import type { UnvaluedReasonContext } from 'components/unvalued-reason';
import type {
  PackageEvaluationResponse,
  PackageResponse,
} from './packages-api/responses';
import type { GunBarrelResponse } from 'components/charts/gun-barrel-chart/types';
import type { UnitFormationSlotsResponse } from 'pages/type-curves/types';
import type { PromoteForecastsResponse } from './dca-workspaces';
import type { TractsCalculationDetails } from './title-calculations/types';
import type { EvalSlotResponse } from './evaluation-api/responses';
import type { JSONObject, JSONValue } from 'lib/types';
import type { CategorySlotCount, Pricing, SlotCategory } from './evaluation';
import type {
  AggregatedFreeCashFlowPayload,
  DeclineCurveAnalysisParams,
} from './api-payloads';
import type { SlotSummary } from 'components/slot-summary-table';
import type { ActivityFeedItem as ActivityFeedItemResponse } from 'components/activity-feed-panel/types';
import type { FlagCategory, FlagSeverity } from 'types/flags';
import type { AfeCatalogResult } from 'pages/afe-catalog/types';
import type { EvalUnifiedResult } from 'pages/packages/compare/wizard/types';
import type { PriceDeckResult as PriceDeckQueryResult } from 'pages/price-decks/types';
import type { DevelopmentPaceScenarioResult } from 'pages/development-pace-scenarios/types';
import type { DiscountRateScenarioResult } from 'pages/discount-rate-scenarios/types';
import type { DifferentialScenarioResult } from 'pages/differential-scenarios/list/types';
import type { TaxScenarioResult } from 'pages/tax-scenarios/types';
import type { ExpenseScenarioResult } from 'pages/expense-scenarios/types';
import type { PackageLibraryResult } from 'pages/packages/list/types';
import type { DivisionOrderResult } from 'pages/division-orders/list/types';
import type { RoyaltyCheckResult } from 'pages/royalty-checks/list/types';
import type { RoyaltyCheckLineItem } from 'pages/royalty-checks/show/types';
import type { MineralType } from './minerals';
import type { TypeCurveProbitResponse } from './type-curve-probit';
import type {
  TypeCurveWorkspaceResponse,
  TypeCurveWorkspacesListResponse,
} from './type-curve-workspace';
import type {
  PreDrillWorkspace,
  PreDrillWellboreDetails,
  PreDrillTargetZoneSuggestions,
  PreDrillVerificationItems,
  PreDrillVerificationItem,
  PreDrillVerificationEventResponse,
  PreDrillAgentRunsResponse,
  PreDrillStructureGridProfilesResponse,
  PreDrillGunBarrelResponse,
  PreDrillSgcQueryResponse,
  PreDrillTargetZoneIntervalsResponse,
  LandingInterpretationIntervalsResponse,
  WellsFacetsResponse,
  WellsFacetGroup,
  PreDrillWorkspaceListResponse,
  PreDrillCreateResponse,
  PreDrillEnqueuedResponse,
} from './pre-drill-workspace';
import type {
  WellborePointsResponse,
  IntersectedGridsResponse,
} from './wellbore-geometry-workspace';
import type { AreaWorkspace } from './area-workspace';

/**
 * HATEOAS link emitted by the document-structuring jbuilder partials
 * ({ href, auth }). Shared by both structuring features (AFE + DO), so it lives
 * here alongside the other cross-feature API shapes rather than in either
 * feature's local types.
 */
export interface StructuringResource {
  href: string;
  auth: boolean;
}

export type {
  PreDrillWorkspace,
  PreDrillWorkspaceShow,
  PreDrillWorkspaceState,
  PreDrillColorRule,
  PreDrillTargetZoneStatus,
  PreDrillGeometryStatus,
  PreDrillVerdict,
  PreDrillWellboreDetails,
  PreDrillTargetZoneSuggestions,
  PreDrillSuggestionCandidate,
  PreDrillRuleBreakdown,
  PreDrillPermitOnFile,
  PreDrillUserEnteredZone,
  PreDrillUserSelectedZone,
  PreDrillZoneDepthRange,
  PreDrillTargetZoneIntervalRow,
  PreDrillTargetZoneIntervalsResponse,
  LandingInterpretationIntervalsResponse,
  LandingInterpretationIntervalRow,
  PreDrillVerificationItem,
  PreDrillVerificationItems,
  PreDrillVerificationPath,
  PreDrillVerificationEventResponse,
  PreDrillAgentRun,
  PreDrillAgentRunsResponse,
  PreDrillStructureGridProfilesResponse,
  PreDrillGunBarrelResponse,
  PreDrillFormation,
  PreDrillTypeCurveFormation,
  FormationDepth,
  PreDrillWellboreTrajectory,
  PreDrillProfileResult,
  PreDrillGunBarrelWell,
  PreDrillGunBarrelPoiLayer,
  PreDrillSgcRow,
  PreDrillSgcQueryResponse,
  WellsFacetBucket,
  WellsFacetGroup,
  WellsFacetsResponse,
  PreDrillWorkspaceListRow,
  PreDrillWorkspaceListResponse,
  PreDrillCreateResponse,
  PreDrillEnqueuedResponse,
} from './pre-drill-workspace';
export type {
  WellborePoint,
  WellborePointType,
  WellborePointSource,
  WellborePointDepthSource,
  WellborePointsResponse,
  IntersectedGrid,
  IntersectedGridReason,
  IntersectedGridsResponse,
  WellboreGeometryEditPoint,
  WellborePointEdit,
  WellboreDatumReference,
  WellboreGeometryResource,
} from './wellbore-geometry-workspace';

export interface Resource {
  href: string;
  auth: string[];
}

export type ResourceNoAuth = Pick<Resource, 'href'>;

export interface MapSourcesResponse {
  basins: string;
  basinTiers: string;
  developmentAreas: string;
  landGrid: string;
  typeCurves: string;
  wells: string;
  wellsV2: string;
  structureGridContours: string;
  structureGridSurfaces: string;
  entities: string;
  structureGrids: string;
  areaGeometries: string;
  referenceLocations: string;
  pipelines: string;
  drillingRigs: string;
  dcaWorkspaceWells: string;
  portfolioAssets: string;
}

export interface ApiRootResponse {
  session?: {
    href: string;
    auth: string[];
    id: number;
    email?: string;
    trackingToken?: string;
    profile?: {
      name: string;
    };
  };
  version: {
    href: string;
    auth: string[];
    major: string | null;
    minor: string | null;
  };
  resources: ApiResourcesResponse | LogoutApiResourcesResponse;
}

interface LogoutApiResourcesResponse {
  sessions: {
    basic: Resource;
    oauth: Resource;
  };
}

export interface TypeCurveOptionsResponse {
  total_count: number;
  results: Record<string, TypeCurveOption[]>;
  errors?: {
    code: string;
    message: string;
    details?: Record<string, string>;
  }[];
}

export interface TypeCurveOption {
  href: string;
  id: string;
  uniqueTcId: string;
  name: string;
  isPreselected: boolean;
  status: string;
  formation: string;
  wellSpacingFt: string;
}

export interface UpdateFormationSlotResponse {
  id: number;
  valued: boolean;
  notes: string | null;
}

export interface InventoryVerificationEventResponse {
  id: number;
  kind: string;
  reason: string | null;
  details: string | null;
}

export type EarlyTimeStreamsResponse = {
  bulkDcaRegressionResults: {
    uwi: string;
    status: 'success' | 'partial_success' | 'failure';
    earlyTimeStreams: MineralType[];
    errors?: {
      code: string;
      message: string;
      details?: Record<string, string>;
    }[];
  }[];
  summary: {
    totalWellsProcessed: number;
    success: number;
    partialSuccess: number;
    failure: number;
  };
};

export interface TypeCurveAggregationMetrics {
  oilEur: number;
  gasEur: number;
  waterEur: number;
  gasOilRatioTotal: number;
  waterCutTotal: number;
  waterOilRatioTotal: number;
  oilGasRatioTotal: number;
  waterGasRatioTotal: number;
}

export interface BulkTypeCurveVolumesResponse {
  total_count: number;
  results: {
    uniqueTcId: string;
    typeCurveId: string;
    uwi: string;
    dcaScenarioId: number;
    scalingApplied: boolean;
    parameters: {
      month: number;
      oil?: number;
      gas?: number;
      water?: number;
    }[];
    volumes: {
      month: number;
      oil?: number;
      gas?: number;
      water?: number;
    }[];
    cumulatives: {
      month: number;
      oil?: number;
      gas?: number;
      water?: number;
    }[];
    mbts: {
      month: number;
      oil?: number;
      gas?: number;
      water?: number;
    }[];
    ratios: {
      month: number;
      gor?: number;
      wor?: number;
      wgr?: number;
      cgr?: number;
    }[];
    aggregationMetrics: TypeCurveAggregationMetrics;
    errors?: {
      code: string;
      message: string;
      details?: Record<string, string>;
    }[];
  }[];
  errors?: {
    code: string;
    message: string;
    details?: Record<string, string>;
  }[];
}

export interface SaveDcaForecastResponse {
  id: number;
  businessId: string | null;
  uwi: string;
  uwi12: string;
  dcaScenarioId: number;
  productionVolumeVersionId: string;
  uniqueTcId: string;
  notes: string;
  dcaForecastParametersAttributes: {
    id: number;
    inEarlytime: boolean;
    stream: MineralType;
    dcaParams: DeclineCurveAnalysisParams;
    createdAt: string;
    updatedAt: string;
  }[];
  productionSegmentsAttributes: {
    id: number;
    stream: MineralType;
    segmentStartDate: string;
    createdAt: string;
    updatedAt: string;
  }[];
  productionOutliersAttributes: {
    id: number;
    stream: MineralType;
    outlierStartDate: string;
    createdAt: string;
    updatedAt: string;
  }[];
}

export interface ApiResourcesResponse {
  crmAccounts: {
    href: string;
    auth: string[];
  };
  queries: {
    activities: Resource;
    titleWorkspaces: Resource;
    referenceLocations: Resource;
    referenceLocationsSimple: Resource;
    entitiesSimple: Resource;
    locations: Resource;
    entities: Resource;
    documents: Resource;
    wells: Resource;
    interpretations: Resource;
    transactions: Resource;
    counties: Resource;
    developmentAreas: Resource;
    packages: Resource;
    structureGrids: Resource;
    geologicIntervalTypeCurves: Resource;
    stateTaxes: Resource;
    areaGeometries: Resource;
    geologicIntervals: Resource;
    structureGridCollections: Resource;
    crmTractsAndSubtracts: Resource;
    crmAccounts: Resource;
    salesforceAccounts: Resource;
  };
  validations: {
    developmentAreas: Resource;
    titleWorkspaces: Resource;
    documents: Resource;
  };
  states: Resource;
  boundingBoxes: Resource;
  quarteredGeographies: Resource;
  documents: Resource;
  wells: Resource;
  directUploads: Resource;
  documentUploads: Resource;
  courthouseDocuments: Resource;
  nonCourthouseDocuments: Resource;
  developmentAreas: Resource;
  entities: Resource;
  interpretations: Resource;
  referenceLocations: Resource;
  titleWorkspaces: Resource;
  maps: { sources: Resource };
  scenarios: Resource;
  scenariosDefault: Resource;
  packages: Resource;
  basinTiers: Resource;
  structureGrids: Resource;
  typeCurveFormations: Resource;
  formationManagement: Resource;
  stateTaxes: Resource;
  areaGeometries: Resource;
  divisionOrders: Resource;
  structureGridsCollection: Resource;
  drillingRigs: Resource;
  crmTractsAndSubtracts: Resource;
  establishments: Resource;
  wellLandingAnalyses?: {
    request: { href: string; auth: string[] };
    create: { href: string; auth: string[] };
    defaultParameters: { href: string; auth: string[] };
  };
}

export type ApiQueryResource = keyof ApiResourcesResponse['queries'];
export type ApiValidationResource = keyof ApiResourcesResponse['validations'];
export type ApiResource = keyof Omit<
  ApiResourcesResponse,
  'queries' | 'maps' | 'validations' | 'session'
>;

interface CountyResponse {
  id: number;
  stateId: number;
  name: string;
  state: {
    abbreviation: string;
  };
}

export type Point = [number, number];

export type SourceDocumentResponse = {
  id: number;
  documentId: number;
  createdAt: string;
  updatedAt: string;
  originalDocumentId?: number;
  countyId?: number;
} & Record<
  | 'source'
  | 'sourceId'
  | 'documentType'
  | 'effectiveDate'
  | 'recordedDate'
  | 'instrumentDate'
  | 'fileDate'
  | 'imagePath'
  | 'bookType'
  | 'volume'
  | 'page'
  | 'instrumentNumber',
  string | undefined
>;

export interface DocumentResponse {
  id: number;
  locations?: LocationResponse[];
  href: string;
  auth: string[];
  bookType: string;
  duplicate: boolean;
  county: CountyResponse | undefined;
  createdAt: string;
  updatedAt: string;
  documentTitle: string;
  documentType?: string;
  structuredPartiesSupported: boolean;
  referenceLocations?: ReferenceLocationResponseMinimal[];
  type?: string | null;
  instrumentDate: string;
  instrumentNumber?: string;
  interpretationsCount: number;
  /** On vesting-only searches: the CRM tracts this deed vests. */
  vestingTractIds?: string[];
  newInterpretationHref: string;
  notifications: Notifications;
  grantors: TextBasedEntityResponse[];
  grantees: TextBasedEntityResponse[];
  referencedDocuments?: DocumentResponse[];
  effectiveDate?: string | null;
  recordedDate?: string;
  fileDate?: string;
  page?: string;
  volume?: string;
  // Read-only county-index vendor fields (Vendor Information panel). Serialized
  // only by the document show endpoints, not the search-card list. vendorSource
  // is the ranked SourceDocument#source key (null for interactive uploads).
  legalDescription?: string | null;
  vendorSource?: string | null;
  resources: {
    grantors: Resource;
    grantees: Resource;
    interpretations: Resource;
    image?: Resource & { contentType?: string; filename?: string };
    locations?: Resource;
    referenceLocations?: Resource;
    referencedDocuments?: Resource;
    duplicateInterpretations: Resource;
    transactionParties: Resource;
    validateTitleChain: Resource;
    interpretationValidation: Resource;
    completions: Resource;
    completionsConfiguration: Resource;
    structuredParties?: Resource;
    confirmedEntities?: Resource;
    agentMessages?: (Resource & { model: string | null }) | null;
  };
  siblingDocuments: DocumentReference[];
  primarySiblingDocument: DocumentReference;
  citations: CitationResponse[];
  sourceDocuments?: SourceDocumentResponse[];
  regulatoryBody?: {
    id: number;
    name: string;
    abbreviation: string | null;
    state: { id: number; name: string; abbreviation: string };
  } | null;
  regulatedTargets?: RegulatedTargetResponse[];
  afeTargets?: AfeTargetResponse[];
  salesforceDivisionOrderLineItems?: SalesforceDivisionOrderLineItemLink[];
  royaltyCheckTargets?: RoyaltyCheckTargetResponse[];
  lineItems?: RoyaltyCheckLineItemResponse[];
}

export interface RoyaltyCheckTargetResponse {
  id: number;
  targetType: string;
  targetId: number;
  displayName: string;
  uwi?: string | null;
}

export interface RoyaltyCheckLineItemResponse {
  completionItemId: number | null;
  path: string | null;
  operatorName?: string | null;
  operatorEntityId?: number | null;
  operatorEntityHref?: string | null;
  ownerName?: string | null;
  ownerEntityId?: number | null;
  ownerEntityHref?: string | null;
  ownerNumber?: string | null;
  checkNumber?: string | null;
  checkDate?: string | null;
  checkAmount?: string | null;
  propertyName?: string | null;
  propertyNumber?: string | null;
  wellApiUwi?: string | null;
  product?: string | null;
  productionDateOriginal?: string | null;
  productionDateStandardized?: string | null;
  interestTypeRaw?: string | null;
  interestTypeNormalized?: string | null;
  taxOrDeductCode?: string | null;
  ownerInterest?: string | null;
  distributionInterest?: string | null;
  propertyBtu?: string | null;
  propertyVolume?: string | null;
  propertyPrice?: string | null;
  propertyValue?: string | null;
  propertyTaxesAndDeductions?: string | null;
  propertyAmountAfterTaxesAndDeductions?: string | null;
  ownerVolume?: string | null;
  ownerValue?: string | null;
  ownerTaxesAndDeductions?: string | null;
  ownerAmountAfterTaxesAndDeductions?: string | null;
  notes?: string | null;
  salesforceRoyaltyLineItemId?: string | null;
}

export interface SalesforceDivisionOrderLineItemLink {
  doIndex: number;
  lineIndex: number;
  sfLineItemId: string;
  sfLineItemName: string | null;
  salesforceDivisionOrderId: string | null;
  href: string | null;
  auth?: string[];
}

export interface AfeTargetWellResponse {
  id: number;
  uwi: string;
  wellNumber: string | null;
  leaseName: string | null;
  displayName: string;
  wellType: string | null;
  href: string;
}

export interface AfeCostLineItem {
  costCategory: string | null;
  costSubcategory: string | null;
  standardizedCostClass: string | null;
  standardizedCostCategory: string | null;
  code: string | null;
  description: string | null;
  drillingCost: string | null;
  completionCost: string | null;
  facilitiesCost: string | null;
  productionCost: string | null;
  dryHoleCost: string | null;
  originalCost: string | null;
  totalCost: string | null;
  perFacilityCost: string | null;
  perWellCost: string | null;
}

export interface AfeCostSummary {
  grandTotal: string | null;
  categoryTotals: {
    categoryName: string | null;
    categoryTotal: string | null;
    standardizedCostCategory: string | null;
    subcategoryTotals: {
      subcategoryName: string | null;
      subcategoryTotal: string | null;
      standardizedCostClass: string | null;
      standardizedCostCategory: string | null;
    }[];
  }[];
}

export interface AfeTargetResponse {
  id: number;
  documentId: number;
  wellId: number | null;
  citationId: number | null;
  data: {
    wellName?: string | null;
    afeType?: string | null;
    afeNumber?: string | null;
    operatorAfeNumber?: string | null;
    costSummary?: AfeCostSummary | null;
    costLineItems?: AfeCostLineItem[];
    [key: string]: unknown;
  };
  well: AfeTargetWellResponse | null;
}

export type DocumentReference = Pick<
  DocumentResponse,
  'id' | 'href' | 'auth' | 'bookType' | 'duplicate' | 'county' | 'documentTitle'
>;

export interface TransactionPartyResponse {
  role: 'grantee' | 'grantor';
  interpretationIndices: number[];
  entity: EntityBaseResponse;
}

interface IndividualEntity {
  birthDate: string | null;
  deathDate: string | null;
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
}

interface OrganizationEntity {
  formationDate: string | null;
  terminationDate: string | null;
  organizationType: string;
  primaryName: string;
}

export type EntityType =
  'OrganizationEntity' | 'IndividualEntity' | 'ImportedEntity';

export interface EntityBaseResponse {
  auth: string[];
  href: string;
  id: number;
  canonicalId?: number;
  hasInventory: boolean;
  name: string;
  type: EntityType;
  deleted: boolean;
  portfolioInvestment: boolean;
  // Tagged "Operator": a code points here or, for a primary, at one of its
  // aliases. Sent only where the view asks for it.
  operator?: boolean;
  deduplicatedAt?: string | null;
  mergedInto?: {
    id: number;
    name: string;
    href: string;
  } | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  phoneNumbers: {
    id: number;
    number: string;
    primary: boolean;
  }[];
  emailAddresses: {
    id: number;
    email: string;
    primary: boolean;
  }[];
  addresses: {
    id: number;
    streetAddress: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    primary: boolean;
  }[];
  resources: {
    queries: {
      interpretations: ResourceNoAuth;
      inventoryAssets: ResourceNoAuth;
      aliasedEntities: ResourceNoAuth;
      capacities: ResourceNoAuth;
    };
    aliasedEntities: ResourceNoAuth;
    capacities: ResourceNoAuth;
    fromAliases: ResourceNoAuth;
    ownership: ResourceNoAuth;
    deduplication: ResourceNoAuth;
    details: ResourceNoAuth;
    packages: ResourceNoAuth;
    inventoryAssetsDownload: ResourceNoAuth;
    crmAccounts: ResourceNoAuth;
    crmTractsAndSubtracts: ResourceNoAuth;
  };
}

export interface EntityLinkedDocumentParty {
  text: string;
}

export interface EntityLinkedDocumentResponse {
  id: number;
  href: string;
  auth: string[];
  documentType: string | null;
  documentTitle: string | null;
  state: string | null;
  county: string | null;
  // The entity's unresolved-flag address cited on this document, or null.
  // Present → the reconcile drilldown highlights the address + shows a Review
  // tag and a "Clear flag" action for it.
  flaggedAddress: {
    id: number;
    label: string;
    criticality: 'high' | 'medium';
  } | null;
  partyFromDocument: EntityLinkedDocumentParty[];
  fileDate: string | null;
  recordedDate: string | null;
  effectiveDate: string | null;
  instrumentDate: string | null;
}

export interface EntityDocumentsResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: EntityLinkedDocumentResponse[];
}

// How the viewed entity appears on one document of POST /queries/documents with an
// entity_id: one block per party appearance (party slot) on its latest extraction.
export interface EntityAsFoundAddress {
  id: number;
  kind: 'standardized' | 'as_found';
  label: string;
}

export interface EntityAsFound {
  role: string | null;
  // Null only when the cited entity record is gone.
  partyDetails: string | null;
  entityId: number | null;
  addresses: EntityAsFoundAddress[];
}

export interface TextBasedEntityResponse {
  auth: string[];
  href: string;
  id: number;
  name: string;
}

export interface EntityResponse extends EntityBaseResponse {
  notes: string | null;
  flagCategory: FlagCategory | null;
  flagRationale: string | null;
  flagSeverity: FlagSeverity | null;
  // The entity's own unresolved ADDRESS review flag (Reconcile::AddressFlag) —
  // lets the reconcile tearsheet mark the viewed-entity anchor row the same as a
  // flagged suggestion, which is otherwise built without this signal.
  addressFlagged: boolean;
  addressFlagCriticality: 'high' | 'medium' | null;
  // Each flagged address ({ id, criticality }), so the reconcile anchor row can
  // mark every flagged address individually when the entity has more than one.
  flaggedAddresses: { id: number; criticality: 'high' | 'medium' }[];
  // The entity's own participation (same membership paths as the flag) — the
  // reconcile tearsheet's anchor row uses these for its links. titleWorkspaces
  // feeds the popup (its length is the displayed count); interpretationCount is a
  // bare count.
  titleWorkspaces: { id: number; name: string }[];
  interpretationCount: number;
  concreteEntity: { id: number } & IndividualEntity & OrganizationEntity;
}

export interface EntityWithAccountsResponse extends EntityBaseResponse {
  address: string;
  importType: string;
  accounts: {
    salesforceId: string;
    salesforceHref: string;
    name: string;
    type: string | null;
    billingAddress: string | null;
    phone: string | null;
  }[];
}

export interface EntityDetailsResponse {
  entityCreatedAt: string;
  entityId: number;
  transactionApplicableAreas: string;
  transactionMaxDate: string;
  transactionMinDate: string;
}

export interface AliasedEntityResponse {
  canonical: boolean;
  // Consolidation-risk flag for this member (null when unflagged), so the
  // alias-family diagram can badge each flagged node (PROD/2907373590) and show
  // its reason on hover.
  flagCategory?: string | null;
  flagRationale?: string | null;
  // Chip severity: 'warning' (workspace) / 'info' (interpretation only) badge the
  // node differently; null (unflagged or dormant) is not badged.
  flagSeverity?: FlagSeverity | null;
  entity: EntityBaseResponse;
  resources: {
    alias: Resource | null;
  };
}

// A directed alias edge (from_id -> to_id) as actually stored in the Alias table.
export interface AliasEdge {
  from: number;
  to: number;
}

// GET /entities/:id/aliased_entities — the entity's alias cluster: the members
// (transitive closure) plus the actual directed edges among them.
export interface AliasedClusterResponse {
  members: AliasedEntityResponse[];
  edges: AliasEdge[];
}

// Lean row shape for the paginated Aliases tab query — the tab only needs
// id/name/href, so the endpoint renders a minimal entity (not the full partial).
export interface AliasedEntityQueryResult {
  canonical: boolean;
  entity: { id: number; name: string; href: string };
  resources: {
    alias: Resource | null;
  };
}

export interface PackagesFromEntityResponse {
  id: number;
  href: string;
  name: string;
  effectiveDate: string;
  createdAt: string;
  includeChildren: boolean;
}

export interface InventoryResponse {
  assetId: string;
  assetName: string;
  fullLegalDescription: string | null;
  startDepth: number | null;
  endDepth: number | null;
  legacyDepth: string | null;
  notes: string | null;
  assetType: string | null;
  tractGrossAcres: number;
  tractNetAcres: number;
  tractNetAcreageUnits: string;
  inventoryAssetId: string;
  inventoryAssetName: string;
  inventoryOwnerId: string | null;
  inventoryOwnerType: string | null;
  inventoryOwnerName: string | null;
  inventoryOwnerPortcoAbbreviation: string | null;
  inventoryOwnerParentId: string | null;
  inventoryOwnerParentType: string | null;
  inventoryOwnerParentName: string | null;
  inventoryOwnerParentPortcoAbbreviation: string | null;
  acquisitionDate: string;
  acquisitionUnitPrice: string;
  acquisitionTotalPrice: string;
  saleDate: string | null;
  saleUnitPrice: string | null;
  saleTotalPrice: string | null;
  acquiredFromType: string;
  acquiredFromName: string;
  soldToType: string | null;
  soldToName: string | null;
  state: string;
  county: string;
  quarterCall: string;
  tractInstrumentType: string | null;
  startEffectiveDate: string;
  endEffectiveDate: string | null;
  daysHeld: number;
}

export interface CapacityEntityResponse {
  id: number;
  name: string;
  href: string;
}

export interface CapacityResponse {
  id: number;
  predicate: string | null;
  object: CapacityEntityResponse | null;
  subject: CapacityEntityResponse | null;
}

export interface TransactionQueryResponse {
  id: number;
  date: string;
  type: string;
  documentTitle: string;
  interpretationHref: string;
}

export interface StructureGridQueryResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  gridSurface: string;
  gridProcessed: boolean;
  owner: string;
  source: string;
  createdAt: string;
}

export interface StructureGridManagementQueryResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  gridSurface: string;
  gridProcessed: boolean;
  owner: string;
  source: string;
  createdAt: string;
  validity: string;
  status: string;
}

export interface GeologicIntervalTypeCurveQueryResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  typeCurveFormation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  endedAt: string;
  positionalIndex?: number;
}

export interface StateTaxesQueryResponse {
  id: number;
  stateAbbreviation: string;
  oilTaxPercentage: number;
  gasTaxPercentage: number;
  nglTaxPercentage: number;
  adValoremTaxPercentage: number;
  createdAt: string;
  updatedAt: string;
}

export interface GeologicIntervalQueryResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  positionalIndex: number;
  age: number;
  createdAt: string;
  updatedAt: string;
}

export interface GeologicIntervalListQueryResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  positionalIndex: number | null;
  lithology: string | null;
  age: number | null;
  displayColor: string | null;
  canonical: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  href: string;
  auth: string[];
  operatorNamingConventions: {
    id: number;
    operator: {
      name: string;
      entityId: number | null;
      allOperators: boolean;
    };
    targetZoneIdentifier: string;
  }[];
  resources: {
    comments: Resource;
    activityFeed: {
      href: string;
      body: { modelType: string; modelId: number };
      auth: string[];
    };
  };
}

export interface FormationManagementFacets {
  basins: string[];
  intervals: string[];
  creators: string[];
  lithologies: string[];
}

/**
 * One row of POST /queries/operators. Slim shape — name plus the resolved
 * `entityId` when the operator is tied to a SIM Entity (null today, will
 * populate once entity resolution lands). Used by the inline Operator
 * Naming Convention picker.
 */
export interface OperatorSearchResult {
  name: string;
  entityId: number | null;
}

export interface GeologicIntervalValidationError {
  attribute: string;
  code: string;
  message: string;
}

export interface GeologicIntervalValidationWarning {
  code: string;
  operator: {
    name: string | null;
    entityId: number | null;
    allOperators: boolean;
  };
  targetZoneIdentifier: string;
  conflictingIntervalIds: number[];
  conflictingIntervalNames: string[];
}

export interface GeologicIntervalValidationResponse {
  errors: GeologicIntervalValidationError[];
  warnings: GeologicIntervalValidationWarning[];
}

export interface GeologicIntervalCreateResponse {
  id: number;
  basinName: string;
  geologicInterval: string;
  positionalIndex: number | null;
  lithology: string | null;
  age: number | null;
  displayColor: string | null;
  canonical: boolean;
  createdAt: string;
  updatedAt: string;
  href: string;
  auth: string[];
  operatorNamingConventions: {
    id: number;
    operator: {
      name: string;
      entityId: number | null;
      allOperators: boolean;
    };
    targetZoneIdentifier: string;
  }[];
  resources: {
    comments: Resource;
    activityFeed: {
      href: string;
      body: { modelType: string; modelId: number };
      auth: string[];
    };
  };
}

export interface TypeCurveHeader {
  id: number;
  uniqueTcId: string | null;
  tcPublicId: string;
  typeCurveScenarioId: number | null;
  basin: string | null;
  formation: string | null;
  status: string | null;
  developmentType: string | null;
  vintageDate: string | null;
  lateralLengthFt: number | null;
  wellSpacingFt: number | null;
  horizontalWellSpacingFt: number | null;
  proppantIntensityLbsFt: number | null;
  fluidIntensityBblFt: number | null;
  fluidIntensityGalFt: number | null;

  oilEurMbbl30yr: string | null;
  gasEurMmcf30yr: string | null;
  waterEurMbbl30yr: string | null;
  oilEurMbbl30yrPerFoot: string | null;
  gasEurMmcf30yrPerFoot: string | null;
  waterEurMbbl30yrPerFoot: string | null;
  oilEurMbbl30yrPerMile: string | null;
  gasEurMmcf30yrPerMile: string | null;
  waterEurMbbl30yrPerMile: string | null;

  boe6Eur: number | null;
  boe6EurPerFoot: number | null;
  boe6EurPerMile: number | null;
  boe20Eur: number | null;
  boe20EurPerFoot: number | null;
  boe20EurPerMile: number | null;

  tcComments: string | null;
  lastQcDate: string | null;
  isValid: boolean | null;
  hasWarnings: boolean | null;
  validity: string | null;
  requestDetail: string | null;
  dataLoadSource: string | null;
  originatedFromTypeCurveHeaderDetailsId: number | null;
  legacyTypeCurveHeaderId: number | null;

  /** The Type Curve's author — the detail's own column (upstream for migrated
   * records, the internal creator for native ones). Distinct from the
   * workspace's createdBy. */
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;

  typeCurveAreaId: number | null;
  typeCurveAreaName: string | null;
  /** Why a workspace on this version cannot promote beyond its tasks (an archived Type Curve
   * Area), in the server's words. Empty when nothing else blocks. */
  domainPromotionBlockers: string[];
  operatorNames: string[];
  wellCount: number | null;
  /** True on the one Type Curve serving this (scenario, area, formation) zone by default. Set only by
   *  an explicit Set as Default at promote — promoting alone does not move it. */
  isDefault?: boolean | null;
  analogWells: string[];

  geometry: string | null;
  /** From PostGIS via ST_AsGeoJSON — may be Point, Polygon, MultiPolygon, etc. */
  geometryAsGeojson: GeoJSON.Geometry | null;

  wellControlMedianProppantLbsPerFt: number | null;
  wellControlMedianFluidGalPerFt: number | null;
  wellControlMedianFluidBblPerFt: number | null;

  // Persisted Well Control Area (mirrors the Global Map Filter selection).
  wellControlAreaKind: 'type_curve_area' | 'map_extent' | 'aoi_polygon' | null;
  // [minLng, minLat, maxLng, maxLat] — populated only for the map_extent kind.
  wellControlAreaBbox: number[] | null;
  wellControlAreaGeometryAsGeojson: GeoJSON.Polygon | null;

  structureGridCollectionId: number | null;

  dcaForecastParameters: DcaForecastParameter[];

  typeWellDefaults?: {
    aggregation: string;
    dcaScenarioId: number | null;
  } | null;

  effectiveVerification?: {
    verificationStatus?: string | null;
    lastVerifiedBy?: string | null;
    lastVerifiedDate?: string | null;
    // The Type Curve verdict behind the header badge: who gave it and when. Separate from the
    // lastVerified pair above, which describes the promotion.
    markedBy?: string | null;
    markedAt?: string | null;
  } | null;
  latestPromotedWorkspace?: {
    id: number;
    name: string;
    promotedAt: string | null;
  } | null;

  workspacesCount: number;

  typeCurveScenario: {
    id: number;
    name: string;
  } | null;

  dcaScenario: {
    id: number;
    name: string;
  } | null;

  /** Hypermedia links + auth for related endpoints (from `type_curves#show` JSON). */
  resources?: {
    workspaces?: { href: string; auth: string[] };
    /** Inventory / DCA workspace collection links, mirroring `workspaces`.
     *  Added by the backend branch; the "Workspaces" dropdown lists off these. */
    inventoryWorkspaces?: { href: string; auth: string[] };
    dcaWorkspaces?: { href: string; auth: string[] };
    latestPromotedWorkspace?: { href: string; auth: string[] } | null;
    latest_promoted_workspace?: { href: string; auth: string[] } | null;
    events?: { href: string; auth: string[] };
    comments?: { href: string; auth: string[] };
    typeCurveScenario?: { href: string; auth: string[] } | null;
    dcaScenario?: { href: string; auth: string[] } | null;
    structureGridCollection?: { href: string; auth: string[] } | null;
  };
}

export interface DcaRampUp {
  n: number;
  qi: number;
  model: string;
  t_ramp: number;
}

export interface DcaForecastParams {
  di: number;
  qi: number;
  dlim: number;
  ramp_up: DcaRampUp | null;
  b_factor: number;
  dca_model: string;
  di_format: string;
  qi_format: string;
  time_format: string;
  duration_format: string;
  duration_period: number;
}

export interface DcaForecastParameter {
  id: number;
  stream: string;
  dcaScenarioId: number;
  aggregation: string;
  inEarlyTime: boolean;
  dcaParams: DcaForecastParams;
  segmentMonthsOnline: number[];
  outlierMonthsOnline: number[];
  createdAt: string;
  updatedAt: string;
}

export interface TypeCurveHeaderQueryResponse {
  id: number;
  uniqueTcId: string;
  verificationStatus?: string | null;
  lastVerifiedBy?: string | null;
  lastVerifiedDate?: string | null;
  validity?: string | null;
  typeCurveAreaName: string;
  status: string;
  basin: string;
  formation: string;
  wellCount: number;
  state: string;
  horizontalWellSpacingFt: number;
  oilEurMbbl30yr: number;
  gasEurMmcf30yr: number;
  waterEurMbbl30yr: number;
  sectionEurMboe20: number;
  requestDetail: string;
  dataLoadSource: string;
  dateApproved: string;
  createdBy: string;
  lastQcDate: string;
  createdAt: string;
  updatedAt: string;
  srpLandingZones: string[];
  newWellsSinceCreated: number;
  newWellsSinceLastQc: number;
  geologicIntervals: {
    geologicInterval: string;
    positionalIndex: number;
  }[];
  // Fields from the updated API.
  typeCurveScenario?: string | null;
  promotedBy?: string | null;
}

/** Listing-page row shape returned by `POST queries/type_curve_headers` with `view: "type_curves"`. */
export interface TypeCurveListingRow {
  id: number;
  /**
   * Type Curve area extent as [minLng, minLat, maxLng, maxLat] for click-to-zoom
   * on the listing map. Null when the version has no area.
   */
  bbox?: [number, number, number, number] | null;
  /**
   * development_area ids of this row's units. The listing map's units layer is
   * keyed by `development_area_id`, so this is what lets a clicked row show only
   * its own shapes. Empty when the row has no unit inventory.
   */
  unitDaIds?: number[];
  /**
   * Type Curve Area id, sent to the listing map tile so it draws this row's area
   * geometry (the Type Curves / Workspaces map layer is keyed by area `id`).
   */
  typeCurveAreaId?: number | null;
  tcPublicId: string;
  typeCurveScenarioId: number;
  typeCurveScenario: string;
  uniqueTcId: string;
  verification: string;
  validation: string;
  typeCurveArea: string;
  basin: string;
  formation: string;
  status: string;
  archived?: boolean;
  promotedBy: string | null;
  state: string;
  wellCount: number;
  wellSpacingFt: number;
  oilEurMbbl30yr: number;
  gasEurMmcf30yr: number;
  href: string;
  lastVerifiedBy?: string | null;
  lastVerifiedDate?: string | null;
  newWellsSinceCreatedCount?: number | null;
  newWellsSinceLastQcCount?: number | null;
  dataLoadSource?: string | null;
  waterEurMbbl30yr?: number | null;
  sectionEurMboe20?: number | null;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  /**
   * Hyperlinks to this row's related docs. `href` is null when the target is
   * absent (e.g. a version with no workspace), so the cell renders plain text.
   */
  resources?: {
    /** `typeCurveHeader` is the original key; `typeCurve` is what the row
     * partials emit once the detail hrefs move to /type_curves/:public_id. Read
     * both — the two are still in flight across branches. */
    typeCurve?: { href: string | null };
    typeCurveHeader?: { href: string | null };
    typeCurveScenario?: { href: string | null };
    workspace?: { href: string | null };
  };
}

export interface TypeCurveWorkspaceListQueryResponse {
  id: number;
  workspace: string;
  uniqueTcId: string | null;
  promotedBy: string | null;
  tasks: { completed: number; total: number };
  verification: string | null;
  // Kept for column fallback — not returned by the new API
  typeCurveScenario?: string | null;
  status?: string | null;
  promotedAt?: string | null;
  unitsVerified?: { verified: number; total: number } | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  href: string;
  /**
   * Type Curve area extent as [minLng, minLat, maxLng, maxLat] for click-to-zoom
   * on the listing map. Null when the version has no area (e.g. a blank draft).
   */
  bbox?: [number, number, number, number] | null;
  /**
   * development_area ids of this row's units — the join key to the units map
   * layer, which is keyed by `development_area_id`. Empty when the workspace's
   * version has no unit inventory.
   */
  unitDaIds?: number[];
  /**
   * Type Curve Area id, sent to the listing map tile so it draws this row's area
   * geometry (the Type Curves / Workspaces map layer is keyed by area `id`).
   */
  typeCurveAreaId?: number | null;
  /**
   * Server-built links for this row (queries/type_curve_headers/_workspace_row).
   * The workspace's own link is the top-level `href`. Each nested `href` is null
   * when its target is absent, so cells render plain text rather than a dead link.
   */
  resources?: {
    /** `typeCurveHeader` is the original key; `typeCurve` is what the row
     * partials emit once the detail hrefs move to /type_curves/:public_id. Read
     * both — the two are still in flight across branches. */
    typeCurve?: { href: string | null };
    typeCurveHeader?: { href: string | null };
    typeCurveScenario: { href: string | null };
    events?: { href: string | null };
    comments?: { href: string | null };
  };
}

export interface StructureGridCollectionsQueryResponse {
  id: number;
  href: string;
  auth: string[];
  basin: string;
  status: string;
  validity: string;
  collectionName: string;
  owner: string;
  isDefault: boolean;
  defaultStartTime: string | null;
  defaultEndTime: string | null;
  activeCollection: boolean;
  landingAutoUpdateEnabled: boolean;
  intervals: number;
  createdAt: string;
  sourceCollectionCreatedAt: string;
}

export interface StructureGridCollectionsQueryListResponse {
  totalCount: number;
  results: StructureGridCollectionsQueryResponse[];
}

export interface LandingAnalysisDefaultParametersResponse {
  href: string;
  auth: string[];
  scope: string;
  landingParameters: {
    usePerforations: boolean;
    horizontalInclinationCutoffDeg: number;
    thicknessCutoffFt: number;
    stepLengthFt: number;
    outOfRangeThreshold: number;
    initialAzimuthPercent: number;
    toeDownThresholdDeg: number;
    toeUpThresholdDeg: number;
    uTurnThresholdDeg: number;
    interpolationMethod: string;
  };
}

export interface WellLandingPreviewResponse {
  href: string;
  auth: string[];
  candidateWellCount: number;
  estimatedRuntimeMinutes: number;
  hasActiveJob: boolean;
  landingParameters: {
    horizontalInclinationCutoffDeg: number;
    stepLengthFt: number;
    thicknessCutoffFt: number;
    usePerforations: boolean;
  };
  resources: {
    wellLandingAnalysisRequests: {
      href: string;
      auth: string[];
    };
    activeJob: {
      href: string;
      auth: string[];
    } | null;
  };
}

export interface State {
  id: number;
  name: string;
  abbreviation: string;
}

export interface DevAreaStatesResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: State[];
}

export interface SurfaceLeaseOwnershipRow {
  party: string;
  partyId: number;
  partyHref: string;
  lease: string;
  leaseHref: string;
  encumberedSurfaceInterest: string;
  surfaceLeaseInterest: string;
}

type CalculationOutputObject = Record<string, string>;

export interface CalculationOutput {
  bonus: CalculationOutputObject;
  delay: CalculationOutputObject;
  royalty: CalculationOutputObject;
  surface: CalculationOutputObject;
  executive: CalculationOutputObject;
  royaltyRates: CalculationOutputObject;
  workingInterests: CalculationOutputObject;
  mineralEncumbrances: CalculationOutputObject;
  netRevenueInterests: CalculationOutputObject;
  mineralLeaseOverrides: MineralLeaseOverrideRow[];
  aggregateSurfaceLeaseOwnership: SurfaceLeaseOwnershipRow[];
}

export interface MineralLeaseOverrideRow {
  amount: string;
  from: string;
  fromId: number;
  fromHref: string;
  lease: string;
  leaseHref: string;
  to: string;
  toId: number;
  toHref: string;
}

export interface SurfaceCalculationOutputRow {
  entityName: string;
  entityId: number;
  entityHref: string;
  surface: string;
  preventedOverconveyance: boolean;
}

export interface DepthRangeCalculationOutputRow {
  entityName: string;
  entityId: number;
  entityHref: string;
  executive?: string;
  bonus?: string;
  royalty?: string;
  delay?: string;
  netRevenueInterest: string;
  preventedOverconveyance: boolean;
}

export type CalculationOutputRow =
  SurfaceCalculationOutputRow | DepthRangeCalculationOutputRow;

export interface MineralLeaseholdRow {
  entityName: string;
  entityId: number;
  entityHref: string;
  lease: string;
  leaseHref: string;
  royaltyRate: string;
  encumberedInterest?: string;
  workingInterest?: string;
}

export interface Notifications {
  channel: string;
  events: Record<'name', string>[];
}

export interface MathFormulaTraceEntry {
  document_id: number;
  conveyance_id: number;
  location_id: number;
  reference_location_id: number;
}

export interface TraceLocation {
  id: number;
  name: string;
  reference_location_id: number;
  reference_location_name: string;
}

export interface LocationMath {
  transaction_id: number;
  location_id: number;
  reference_location_id: number;
  location: string;
  conveyor_pre_ownership: string;
  conveyee_post_ownership: string;
  conveyed_amount: string;
  retained_amount: string;
  conveyed_formula: string;
  retained_formula: string;
}

export interface ConveyanceMath {
  validated_interest_type: string;
  multiplier_raw?: string;
  conveyor_math_formula_trace_entries: MathFormulaTraceEntry[];
  locations_affected: string[];
  location_math: LocationMath[];
  is_establishment?: boolean;
  seeded_amount?: string;
}

export interface BurdenedLease {
  id: number;
  name: string;
  grantee: string;
  percentage: string;
  royalty_rate: string;
  document_title: string;
  active: boolean;
  interest_type: string;
  location_text: string;
  interest_amount: string;
  executive_interest_owner: string;
  nri: string | null;
  royalty_owner_name: string;
  royalty_ownership: string;
  volume: string | null;
  page: string | null;
  expiration_date: string | null;
  held_by_production: boolean | null;
  document_type: string | null;
}

export interface SeveranceMetadata {
  executive_stick_uuid: string;
  geometry_name: string;
  document_id: number;
  document_title: string;
}

export interface OwnershipEntry {
  location: string;
  depth_range: string;
  interests: Record<string, number>;
}

export interface TitleValidatorTransaction {
  document_id: number;
  document_type: string | null;
  document_title: string;
  grantor: string;
  grantee: string;
  grantor_id: number;
  grantee_id: number;
  transaction_date: string;
  transaction_date_source: string | null;
  effective_date: string | null;
  instrument_date: string | null;
  file_date: string | null;
  recorded_date: string | null;
  conveyance_id: number | null;
  transaction_id: number | null;
  interpretation_id: number | null;
  interest_type: string;
  of_type: string;
  transaction_type: string | null;
  multiplier: string;
  location_search_texts: string[];
  location_ids: number[];
  conveyance_geometries: unknown[];
  direction: string;
  chain_type: string;
  volume: string | null;
  page: string | null;
  instrument_number: string | null;
  transaction_number: number;
  validated_interest_type: string;
  matched_interest_types: string[];
  symbolic_expression: string;
  written_formula: string;
  conveyance_math?: ConveyanceMath;
  conveyor_pre_ownership: OwnershipEntry[];
  conveyor_post_ownership: OwnershipEntry[];
  conveyee_pre_ownership: OwnershipEntry[];
  conveyee_post_ownership: OwnershipEntry[];
  is_valid: boolean;
  validation_reason: string;
  is_establishment: boolean;
  establishment_source?: 'manual' | 'seeded' | null;
  is_establishment_chain: boolean;
  conveyance_event_origin_severance_royalty: SeveranceMetadata[];
  conveyance_event_origin_severance_working: SeveranceMetadata[];
  conveyance_event_origin_severance_overriding: SeveranceMetadata[];
  conveyance_event_origin_severance_bonus: SeveranceMetadata[];
  conveyance_event_origin_geospatial_severance: SeveranceMetadata[];
  conveyance_event_origin_depth_severance: SeveranceMetadata[];
  tracking_severance_royalty: SeveranceMetadata[];
  tracking_severance_working: SeveranceMetadata[];
  tracking_severance_overriding: SeveranceMetadata[];
  tracking_severance_bonus: SeveranceMetadata[];
  burdened_leases?: BurdenedLease[];
}

export interface SupportingDocument {
  id: number;
  href: string;
  volume: string;
  page: string;
  instrument_number: string | null;
  document_title: string;
}

export interface LocationGeography {
  id: number;
  geometry_json: unknown;
}

export interface EstablishmentLocationGeography extends LocationGeography {
  search_text?: string;
  reference_location_name: string;
  reference_location_id: number;
  geometry_hash: string;
}

export interface TitleValidatorResponse {
  target_entity: string;
  cutoff_date: string;
  backward_entities: string[];
  forward_entities: string[];
  transactions: TitleValidatorTransaction[];
  geographies: EstablishmentLocationGeography[];
  supporting_documents_by_document_id: Record<number, SupportingDocument[]>;
  coverage_threshold: number;
  locations: TraceLocation[];
  summary: {
    backward_count: number;
    forward_count: number;
    total_entities: number;
    transaction_count: number;
    date_range: {
      min: string | null;
      max: string | null;
    };
  };
}

export type TitleChainValidationResult = TitleValidatorResponse;

export interface DepthRangeResponse {
  href: string;
  name: string;
  output: CalculationOutput;
  outputRows: CalculationOutputRow[];
  mineralLeaseholdRows: MineralLeaseholdRow[];
  notifications: Notifications;
  resources: { debuggingGraph?: Resource };
}

export interface DownloadTrackerResponse extends Resource {
  notifications: Notifications;
  status: string;
}

export interface DocumentProvisionJobResponse extends Resource {
  notifications: Notifications;
}

export interface DownloadResponse extends Resource {
  id: number;
  status: string;
  fileUrl: string;
  notifications: Notifications;
}

export interface TractResponse extends Resource {
  id: number;
  status: string;
  output: CalculationOutput;
  outputRows: CalculationOutputRow[];
  calculatedAcreage: number | null;
  legalAcreage: number | null;
  derivedName: string;
  overriddenName: string | null;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  depthRanges: TractDepthRangeResponse[];
  notifications: Notifications;
  resources: { debuggingGraph?: Resource };
}

export interface WorkingInterestOwnerResponse {
  entity: ResourceWithName;
}

export type WellIntersectionWellResponse =
  | {
      deleted: false;
      href: string;
      auth: string[];
      name: string;
      uwi: string;
      resources: {
        features: {
          href: string;
          auth: string[];
        };
      };
    }
  | {
      deleted: true;
      name: string;
      uwi: string;
    };

export interface WellIntersectionResponse extends Resource {
  id: number;
  well: WellIntersectionWellResponse;
  workingInterestOwners: WorkingInterestOwnerResponse[];
}

export interface ResourceWithName extends Resource {
  name: string;
}

export interface TractDepthRangeResponse extends ResourceWithName {
  id: number;
}

export interface CalculationTractResponse extends ResourceWithName {
  id: number;
  calculatedAcreage: number | null;
  legalAcreage: number | null;
  geometry: GeoJSON.Geometry;
}

export interface CalculationWellIntersectionResponse extends Resource {
  id: number;
  well: WellIntersectionWellResponse;
}

export interface TitleCalculationResponse extends Resource {
  id: number;
  status: string;
  asOfDate: string;
  createdAt: string;
  tracts: CalculationTractResponse[];
  wellIntersections: CalculationWellIntersectionResponse[];
  resources: { downloads: Resource; entities: Resource; cancel: Resource };
  notifications: Notifications;
}

export interface CalculationEntity {
  href: string;
  entity: EntityBaseResponse;
  resources: {
    mineralDebuggingGraph?: Resource;
    surfaceDebuggingGraph?: Resource;
  };
}

export interface OwnershipResponse {
  assets: {
    type: 'mineral' | 'surface';
    titleWorkspace?: {
      href: string;
      id: number;
      name: string;
      referenceLocation: ReferenceLocationResponseMinimal;
    };
    date: string;
    updatedAt: string;
    coveredArea: {
      aggregateId: string;
      name: string;
      startDepthInFeet: string | null;
      endDepthInFeet: string | null;
      calculatedAcreage: string;
      legalAcreage: string;
      geojson: GeoJSON.Geometry;
    };
    lease: {
      href: string;
      name: string;
      royaltyRate: string;
      interpretation: Resource & { id: number };
    } | null;
    burdenedEntity: EntityBaseResponse | null;
    surface: string;
    executive: string;
    bonus: string;
    delay: string;
    royalty: string;
    fixedRoyalty: string;
    working: string;
    override: string;
    netRevenueInterest: string;
    preventedOverconveyance: boolean;
  }[];
}

export interface InterestWorkspaceResponse extends Resource {
  id: number;
  name: string;
  /** Optional free-text annotation; null when unset. Frozen once promoted. */
  description: string | null;
  entityId: number;
  entityName: string;
  promoted: boolean;
  promotedAt: string | null;
  promotedBy: number | null;
  promotedByName: string | null;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  slotCount: number;
  confirmedCount: number;
  autoConfirmThresholdPct: number;
  /** Entity-scoped Pusher channel; the FE subscribes to learn when another
   *  workspace for this entity is promoted. */
  pusherChannel: string;
}

// ──────────────────────────────────────────────────────────────────────────
// NRI Matrix (GET /nri_calculations/:id/calculation_matrix) — consumed by
// `UATNRIMatrixPopup` via the `nriMatrixAtom` triplet in
// `atoms/nri-matrix-atoms.ts`. The selection-atom + shared-loadable shape
// limits one matrix open at a time today; the atomFamily conversion is a
// deferred behaviour-change story.
// ──────────────────────────────────────────────────────────────────────────

export interface NriMatrixRow {
  inUse: boolean;
  allocationMethod: 'acreage' | 'lateralLength';
  source: 'plat' | 'gis';
  uat: string;
  value: number | null;
  factor: number | null;
  uatNri: number | null;
  uatWi: number | null;
}

export interface NriMatrixFormulaInputs {
  tractNri?: number;
  tractWi?: number;
  participationTractGisAc: number;
  allocationManagerPresent: boolean;
  uatAc?: number;
  uatGisAc?: number;
  /**
   * The popup's third-term denominator — under legal basis this is the plat
   * acreage, NOT the UAT's GIS acreage that `uatGisAc` carries for the
   * matrix's GIS allocation axis. Absent on payloads predating the split;
   * consumers fall back to `uatGisAc`.
   */
  participationDenominatorAc?: number;
  unitGisAc?: number;
  unitAc?: number;
  uatLateralLength?: number;
  uatGisLateralLength?: number;
  unitLateralLength?: number;
  unitLateralGisLength?: number;
  // Tract NRI formula fields
  grossAcres?: number;
  netAcreageUnits?: string;
  nma?: number | null;
  nra?: number | null;
  leaseRoyaltyRate?: number;
  instrumentType?: string;
  orriBurdens?: number;
}

export interface NriMatrixResponseRow {
  allocationMethod: 'acreage' | 'lateralLength';
  source: 'plat' | 'gis';
  inUse: boolean;
  value: number | null;
  factor: number | null;
  nri: number | null;
  workingInterest: number | null;
}

export interface NriMatrixResponse {
  nriCalculationId: number;
  uwi: string | null;
  slotId: string | null;
  wellName: string;
  unitTract: {
    id: string;
    href: string;
    name: string;
  };
  unitAllocationTract: {
    id: number;
    tractName: string;
  } | null;
  rows: NriMatrixResponseRow[];
  formulaInputs: NriMatrixFormulaInputs;
}

// ──────────────────────────────────────────────────────────────────────────
// Tract NRI Matrix (GET /nri_calculations/tract_matrix?tract_id=…) — consumed
// by `TractNriMatrixPopup` via `tractNriMatrixAtomFamily` in
// `atoms/tract-nri-matrix-atoms.ts`.
// ──────────────────────────────────────────────────────────────────────────

export interface TractNriMatrixRow {
  inUse: boolean;
  allocationMethod: 'acreage' | 'lateralLength';
  source: 'plat' | 'gis';
  aggregatedNri: number | null;
  aggregatedWi: number | null;
}

export interface TractNriMatrixBreakdownItem {
  nriCalculationId: number;
  subtractId: string;
  subtractName: string;
  unitTract: {
    id: string;
    href: string;
    name: string;
  };
  unitAllocationTract: {
    id: number;
    tractName: string;
  } | null;
  formulaInputs: NriMatrixFormulaInputs;
  rows: NriMatrixResponseRow[];
}

export interface TractNriMatrixResponse {
  tractId: string;
  uwi: string | null;
  slotId: string;
  wellName: string;
  rows: TractNriMatrixRow[];
  breakdown: TractNriMatrixBreakdownItem[];
}

// ──────────────────────────────────────────────────────────────────────────
// Formula details (GET /nri_calculations/:id/formula_details) — consumed by
// `LazyUnitInterestCard` via `formulaDetailsAtomFamily` in
// `atoms/formula-details-atoms.ts`.
// ──────────────────────────────────────────────────────────────────────────

export interface FormulaDetailsNriFormula {
  allocationManagerPresent: boolean;
  tractNri?: number;
  tractWi?: number;
  participationTractGisAc?: number;
  uatGisAc?: number;
  uatMeasureValue?: number;
  unitMeasureValue?: number;
  measureUnit: string;
  unitGisAc?: number;
  /** Which acreage figures produced these numbers. 'gis' where nobody opted in. */
  acreageBasis?: 'gis' | 'legal';
  /** Overlap actually applied, whether measured or manually overridden. */
  overlapFraction?: number | null;
  /** What the shapes measure, shown as the baseline beside a manual entry. */
  gisOverlapFraction?: number | null;
  /** The UAT's plat acreage — the legal-basis denominator. */
  uatPlatAc?: number | null;
  /** The document's gross acres — the legal-basis numerator before scaling. */
  tractGrossAc?: number | null;
  acreageBasisWarnings?: string[];
  /** Served by the backend, which owns the rule; the page does not keep a copy. */
  overlapTolerance?: number;
  driftPrecision?: number;
}

export interface FormulaDetailsTractFormula {
  grossAcres?: number;
  netAcreageUnits?: string;
  nma?: number | null;
  nra?: number | null;
  leaseRoyaltyRate?: number;
  instrumentType?: string;
  orriBurdens?: number;
}

export interface FormulaDetailsParameters {
  participationTractFactor: number;
  unitCoverageThreshold: number;
  uatCoverageThreshold: number;
  minimumDevelopmentAreaCoverage: number;
}

export interface FormulaDetailsResponse {
  nriFormula: FormulaDetailsNriFormula | null;
  tractNriFormula: FormulaDetailsTractFormula | null;
  tractWiFormula: FormulaDetailsTractFormula | null;
  parameters: FormulaDetailsParameters;
}

export interface TitleWorkspaceResponse extends Resource {
  id: number;
  referenceLocation: ReferenceLocationResponseMinimal;
  name: string;
  legalAcreage?: string;
  createdBy: string;
  createdAt: string;
  lastCalculated: string | null;
  numberOfOwners: number | null;
  numberOfTracts: number | null;
  numberOfDepths: number | null;
  startingInterestDate: string | null;
  channel: string | null;
  startingInterest: {
    entity: EntityBaseResponse;
    id: number;
    mineral: string;
    surface: string;
  }[];
  resources: {
    queries: { entities: Resource; interpretations: Resource };
    documents: Resource;
    entities: Resource;
    wells: Resource;
    runsheet: Resource;
    startingInterest: Resource;
    calculations: Resource;
    runsheetDownloads: Resource;
    lastCalculation: Resource | null;
    lastTag: Resource | null;
    currentCalculation: (Resource & { cancelHref: string }) | null;
    agentMessages: (Resource & { model: string | null }) | null;
  };
}

interface ReferenceLocationResponseMinimal {
  auth?: string[];
  href: string;
  id: number;
  name: string;
  shortName: string;
  county?: CountyResponse;
  acreage?: number;
}

export interface ReferenceLocationResponse extends ReferenceLocationResponseMinimal {
  geometry?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  createdAt?: string;
  createdBy?: string;
  resources: {
    documentsDownloads?: Resource;
    titleWorkspace: Resource | null;
  };
}

export interface CountyQueryResponse {
  id: number;
  name: string;
  fips: string;
  stateAbbreviation: string;
}

export interface CitationEntityResponse {
  id: number;
  name: string;
  type: string;
  address: string | null;
  capacity?: string | null;
  interestPercentage?: string | null;
  emails?: string[];
  phones?: string[];
}

export interface RegulatedTargetResponse {
  id: number;
  targetType: string;
  targetId: number;
  displayName: string;
}

export interface CitationRegulatoryBodyResponse {
  id: number;
  name: string;
  abbreviation: string | null;
  state: { id: number; name: string; abbreviation: string };
}

export interface CitationRegulatedTargetResponse {
  id: number;
  type: string;
  displayName: string;
  uwi?: string;
  wellType?: string | null;
  href: string;
}

export interface CitationResponse {
  id: number;
  provisionField: string;
  provisionValue: string;
  entities?: CitationEntityResponse[];
  regulatoryBodies?: CitationRegulatoryBodyResponse[];
  regulatedTargets?: CitationRegulatedTargetResponse[];
}

export interface LocationResponse {
  auth: string[];
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  href: string;
  id: number;
  quarterCalls: string[];
  locationId: number;
  citations: CitationResponse[];
  referenceLocationName: string;
  referenceLocation: ReferenceLocationResponse;
}

export interface ReviewAttributes {
  id: number;
  transactionsReviewed: boolean;
  applicableAreasReviewed: boolean;
  documentReferencesReviewed: boolean;
  capacitiesReviewed: boolean;
  curativeRequirementsReviewed: boolean;
  additionalInformationReviewed: boolean;
  grantorsReviewed: boolean;
  granteesReviewed: boolean;
}

interface TransactionGrantor {
  id: number;
  entityId: number;
  entity: EntityResponse;
}

interface TransactionConveyance {
  id: number;
  granteeId: number;
  entity: EntityResponse;
}

type TransactionConveyanceForOverridingRoyalty = {
  multiplier: string | null;
} & TransactionConveyance;

type TransactionConveyanceForConveyanceTransaction = {
  surfaceMultiplier?: string;
  mineralMultiplier?: string;
  bonusMultiplier?: string;
  delayMultiplier?: string;
  royaltyMultiplier?: string;
  executiveMultiplier?: string;
  workingMultiplier?: string;
  overridingMultiplier?: string;
  surfaceLeaseMultiplier?: string;
  fixed?: boolean;
} & TransactionConveyance;

export interface LeaseExtension {
  id?: number;
  transactionId?: number;
  referenceLocation: ReferenceLocationResponse;
  extensionReason: string;
  startDepthInFeet: number | null;
  endDepthInFeet: number | null;
  quarterCalls: string[];
}

type TransactionWellReference = {
  id: number;
  well: WellsResponse;
  referenceType: 'grant' | 'reserve';
};

export type TransactionResponse =
  | {
      type: 'Transaction::MineralLeaseTransaction';
      status?: StatusValues | null;
      id: number;
      sequence: number;
      mineralLease: {
        expirationDate: string | null;
        extension: number;
        heldByProduction: boolean;
        royaltyRate: string;
        hbpReason: string | null;
        term: number;
        id: number;
        topLeases: {
          id: number;
          name: string;
          interpretationHref: string;
        }[];
        subjectLeases: {
          id: number;
          name: string;
          interpretationHref: string;
        }[];
      };
      grantors: TransactionGrantor[];
      conveyances: TransactionConveyance[];
      leaseExtensions: LeaseExtension[];
    }
  | {
      type: 'Transaction::SurfaceLeaseTransaction';
      status?: StatusValues | null;
      id: number;
      sequence: number;
      surfaceLease: {
        expirationDate: string;
        extension: number;
        term: number;
        id: number;
      };
      grantors: TransactionGrantor[];
      conveyances: TransactionConveyance[];
    }
  | {
      type: 'Transaction::OverridingRoyalty';
      status?: StatusValues | null;
      id: number;
      ofType: string;
      sequence: number;
      grantors: TransactionGrantor[];
      conveyances: TransactionConveyanceForOverridingRoyalty[];
      transactionDocumentReferences: {
        id: number;
        document: DocumentResponse;
      }[];
      transactionWellReferences: TransactionWellReference[];
    }
  | {
      type: 'Transaction::ConveyanceTransaction';
      unburdened: boolean | null;
      status?: StatusValues | null;
      id: number;
      sequence: number;
      conveyanceType: string;
      ofType: string;
      grantors: TransactionGrantor[];
      conveyances: TransactionConveyanceForConveyanceTransaction[];
      transactionDocumentReferences: {
        id: number;
        document: DocumentResponse;
      }[];
      transactionWellReferences: TransactionWellReference[];
    };

interface LocationReferenceInterpretation {
  id: number;
  startDepthInFeet: number | null;
  endDepthInFeet: number | null;
  depthRangeName: string;
  location: LocationResponse;
}

export interface DocumentInterpretationResponse {
  href: string;
  id: number;
  documentId: number;
  auth: string[];
  notes: string;
  reviewed: boolean | null;
  imported: boolean;
  hasOpenRequirements: boolean;
  pageReferences: string | null;
  supersedingDate: string | null;
  legalDescription: string;
  lastSavedAt: string | null;
  lastSavedBy: { email: string } | null;
  assumption: boolean;
  interpretationDate: string | null;
  interpretationDateSource: string | null;
  transactionsCount: number;
  topLease: boolean;
  subjectLease: boolean;
  // Optional because the full interpretation resource emits these via render_path!
  // (present only when that path is requested); the ES card projection emits them
  // unconditionally.
  subjectLeases?: {
    id: number;
    name: string;
    interpretationHref: string;
  }[];
  topLeases?: {
    id: number;
    name: string;
    interpretationHref: string;
  }[];
  granteeReferences?: {
    id: number;
    granteeId: number;
    name: string;
    notes: string | null;
  }[];
  grantorReferences?: {
    id: number;
    grantorId: number;
    name: string;
    notes: string | null;
  }[];
  locationReferences: LocationReferenceInterpretation[];
  interpretationCapacities: {
    id: number;
    capacityId: number;
    predicate: string | null;
    object: EntityResponse | null;
    subject: EntityResponse;
  }[];
  curativeRequirements: {
    id: number;
    status: string;
    priority: string;
    description: string;
  }[];
  documentReferences: {
    id: number;
    referenceType: string;
    document: DocumentResponse;
  }[];
  document: {
    id: number;
    documentType: string;
    bookType: string;
    fileDate: string;
    instrumentDate: string;
    effectiveDate: string;
    recordedDate: string;
    documentTitle: string;
    county: CountyResponse | null;
    instrumentNumber?: string | null;
    newInterpretationHref: string;
    duplicateInterpretations: Resource;
    resources: {
      transactionParties: Resource;
      validateTitleChain: Resource;
    };
  };
  transactions: TransactionResponse[];
  reviewAttributes: ReviewAttributes | null;
  supporting: {
    id: number;
    interpretationHref: string;
    documentTitle: string;
    documentType: string;
    volume?: string;
    page?: string;
  }[];
  // Optional because the full resource emits it via render_path!; the ES card
  // projection emits it unconditionally.
  supportedBy?: {
    id: number;
    document: {
      id: number;
      href: string;
      documentTitle: string;
      documentType: string;
      volume?: string;
      page?: string;
    };
  }[];
  resources: {
    duplicate: Resource;
  };
  updatedAt: string;
}

// A grantor/grantee's entity as the card projection emits it: the five card
// fields, or a { id: null, deleted: true } sentinel when the party's entity was
// soft-deleted with no surviving merge target.
export type InterpretationCardPartyEntity =
  | {
      id: number;
      name: string;
      href: string;
      deleted: boolean;
      canonicalId: number | null;
    }
  | { id: null; deleted: true };

export interface InterpretationCardParty {
  id: number;
  entity: InterpretationCardPartyEntity;
}

// The card projection's transaction shape — far smaller than the full
// TransactionResponse: no sequence, lease/well references, or per-conveyance
// multipliers, only what the interpretation card renders.
export interface InterpretationCardTransaction {
  id: number;
  type: string;
  formatTransactionType: string;
  status: StatusValues | null;
  conveyanceType: string | null;
  grantors: InterpretationCardParty[];
  conveyances: InterpretationCardParty[];
}

// The card projection's nested document — the interpretation-card fields only,
// without the full resource's new-interpretation/duplicate/resources links, and
// its county carries no id.
export interface InterpretationCardDocument {
  id: number;
  type: string;
  documentType: string | null;
  bookType: string | null;
  documentTitle: string | null;
  effectiveDate: string | null;
  instrumentDate: string | null;
  fileDate: string | null;
  recordedDate: string | null;
  volume: string | null;
  page: string | null;
  instrumentNumber: string | null;
  hasCompletion: boolean;
  hasCompletionComments: boolean;
  county: {
    stateId: number;
    name: string;
    state: { abbreviation: string | null };
  } | null;
}

// The ES interpretation card projection (Query::Interpretations). Matches the full
// interpretation resource except its location references carry only the two fields
// the card renders, and that location can be null — the projection omits it when
// the reference's location was removed. The full resource keeps LocationResponse.
export type InterpretationCardResponse = Omit<
  DocumentInterpretationResponse,
  // Keys the ES card projection (Interpretation::CardProjection) does not emit —
  // only the full interpretation resource carries them. Kept off the card type so
  // a stray read surfaces as a compile error instead of undefined at runtime.
  | 'locationReferences'
  | 'auth'
  | 'reviewed'
  | 'pageReferences'
  | 'lastSavedAt'
  | 'lastSavedBy'
  | 'transactionsCount'
  | 'interpretationCapacities'
  | 'curativeRequirements'
  | 'documentReferences'
  | 'resources'
  // Narrowed below to the card projection's smaller nested shapes.
  | 'transactions'
  | 'document'
> & {
  // The card emits the transaction count under this key, not `transactionsCount`.
  transactionCount: number;
  transactions: InterpretationCardTransaction[];
  document: InterpretationCardDocument;
  locationReferences: {
    id: number;
    startDepthInFeet: number | null;
    endDepthInFeet: number | null;
    depthRangeName: string;
    location: { referenceLocationName: string; quarterCalls: string[] } | null;
  }[];
};

export interface ActivitiesQueryResponse {
  email: string;
  newInterpretationsCount: number;
  editedInterpretationsCount: number;
}

export type WellVersionsResponse = {
  versionName: string;
  beginTime: string;
  endTime: string | null;
  versionDisplayTime: string;
  versionId: string;
};

export interface WellsQueryResponse {
  href: string;
  auth: string[];
  id: number;
  uwi: string;
  name: string;
  leaseName: string;
  wellNumber: string;
  currentOperator: string;
  currentOperatorHref?: string | null;
  holeDirection: string | null;
  status: string;
  permitDate: string;
  spudDate: string;
  firstProductionDate: string | null;
  srpLandingZone: string | null;
  /**
   * Sourced from canonical_landing_interval on the backend (wells.srp_landing_zone is
   * retired) — same value as srpLandingZone above, exposed under its resolved name too.
   */
  canonicalLandingInterval: string | null;
  /** Dropped from the /wells/:id and /queries/wells payloads — the backend column (wells.
   *  srp_landing_zone) this was sourced from is retired; see srpLandingZone above for the
   *  current resolved value. */
  srpLandingZoneAttribute?: string | null;
  vendorLandingZone: string | null;
  /**
   * Landing-analysis Landing Zone for the requested structure grid collection,
   * and the srpLandingZone code resolved to its geologic interval name.
   * Both emitted only when the caller passes `includeLandingZone`, and
   * `landingZone` is null unless a `structureGridCollectionId` was also sent,
   * because landing is stored per collection.
   */
  landingZone?: string | null;
  srpLandingZoneLabel?: string | null;
  surfaceHole: GeoJSON.Point | null;
  stateAbbreviation?: string | null;
  trueVerticalDepthFeet?: number | null;
  measuredDepthFeet?: number | null;
  completionDate?: string | null;
  rigReleaseDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  productionForecasts?: WellsQueryProductionForecastsResponse[];
  resources: {
    queries: {
      productionRecords: Resource;
      productionRecordsVersions: Resource;
      /** Daily production; only `/wells/:uwi` serves these two. */
      dailyProduction?: Resource;
      dailyProductionVersions?: Resource;
      productionForecasts: Resource;
      productionForecastsVersions: Resource;
      wellbores: Resource;
      sourceWellStimulations: Resource;
      permitHeaders: Resource;
      wellPerforations: Resource;
      wellCompletions: Resource;
    };
    features: Resource;
    productionRecord: Resource;
    productionForecast: Resource;
    comments: Resource;
    supportingFiles: Resource;
    events: Resource;
    formation: Resource;
    units: Resource;
    versions: Resource;
    maps: {
      units: Resource;
    };
  };
  notifications: Notifications;
  latestWellStimulationDetails?: {
    jobDate: string | null;
    proppantLbsPerFt: number | null;
    fluidGalPerFt: number | null;
    fluidBblPerFt: number | null;
    waterGalPerFt: number | null;
    waterBblPerFt: number | null;
    acidGalPerFt: number | null;
    acidBblPerFt: number | null;
    stageSpacingFt: number | null;
    proppantMeshSize: string | null;
    fluidSystem: string | null;
  };
}

export interface WellsQueryProductionForecastsResponse {
  id: number;
  wellId: number | null;
  sourceId: string | null;
  engineerEmail: string | null;
  uploadedAt: string | null;
  generatedOn: string | null;
  notes: string | null;
  source: string | null;
  uwi: string | null;
  engineerName: string | null;
  declineTool: string | null;
  status: string | null;
  requestCategory: string | null;
  requestDetail: string | null;
  pita: string | null;
  eur30yrActualOilP20Mbo: number | null;
  eur30yrActualGasP20Mmcf: number | null;
  eur30yrActualOilP35Mbo: number | null;
  eur30yrActualGasP35Mmcf: number | null;
  eur30yrActualOilP50Mbo: number | null;
  eur30yrActualGasP50Mmcf: number | null;
  eur30yrActualOilP65Mbo: number | null;
  eur30yrActualGasP65Mmcf: number | null;
  eur30yrActualOilP80Mbo: number | null;
  eur30yrActualGasP80Mmcf: number | null;
  eur30yrActualWaterP50Mbbl: number | null;
  eurSrpnorm: number | null;
  qc6monthFlag: boolean | null;
  qcScoreOil: number | null;
  qcScoreGas: number | null;
  qcScoreWater: number | null;
  oilParamsP20: string | null;
  gasParamsP20: string | null;
  oilParamsP35: string | null;
  gasParamsP35: string | null;
  oilParamsP50: string | null;
  gasParamsP50: string | null;
  waterParamsP50: string | null;
  oilParamsP65: string | null;
  gasParamsP65: string | null;
  oilParamsP80: string | null;
  gasParamsP80: string | null;
  declineType: string | null;
  prodDataSource: string | null;
  declineStartDate: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * 30-year EUR for one well, from its promoted DCA forecast for the pdp_default
 * scenario — `well_dca_forecast_metrics` joined to `well_dca_forecasts`, which is
 * also what the wells index carries under the nested
 * `latest_promoted_dca_forecasts`. Totals are bbl / Mcf; the per-foot values are
 * normalized server-side against the lateral that forecast used, so consumers must
 * not re-derive them. 0 stands for "no figure reported".
 *
 * Optional because no jbuilder emits it yet (the workspace's EUR card
 * renders em-dashes until the well payload carries it).
 */
export interface WellEurMetrics {
  oilBbl: number | null;
  gasMcf: number | null;
  waterBbl: number | null;
  oilBblPerFt: number | null;
  gasMcfPerFt: number | null;
  waterBblPerFt: number | null;
}

/**
 * Where a well's production comes from: `ihs` for the monthly IHS records, or
 * `scada` for the operator's daily production. Users see these as "IHS" and
 * "Operator".
 */
export type ProductionDataSource = 'ihs' | 'scada';

/** The production data sources a well has, and the one it defaults to. */
export interface WellProductionDataSources {
  /** The sources that have production for the well. */
  availableSources: ProductionDataSource[];
  /** The source a DCA scenario's hierarchy picks for the well, or IHS without a scenario. */
  defaultSource: ProductionDataSource;
}

/** One well from `/queries/reservoir/production_data_sources`. */
export interface WellProductionDataSourcesResponse extends WellProductionDataSources {
  uwi: string;
}

export interface WellsResponse extends WellsQueryResponse {
  eur?: WellEurMetrics | null;
  /**
   * The production data sources that have production for this well. Optional
   * because only `/wells/:uwi` serves it.
   */
  productionDataSources?: ProductionDataSource[];
  /**
   * The well's county name, for the Well Details card — which carries the same ten
   * fields as the Well Workspace's. Optional because `/wells/:uwi` does not serve it
   * yet (nor `stateAbbreviation`), so both render an em-dash until it does; the staged
   * well already carries them, which is why the workspace card fills them.
   */
  county?: string | null;
  versionId: string;
  // Emitted by wells/_well_notifications for every well payload.
  notifications: Notifications;
  rigReleaseDate: string | null;
  completionDate: string | null;
  lastProductionDate: string | null;
  measuredDepth: number | null;
  trueVerticalDepth: number | null;
  producingFormation: string | null;
  formationAtTotalDepth: string | null;
  events: EventResponse[];
  wellNumber: string;
  name: string;
  uwi: string;
  currentOperator: string;
  status: string;
  permitDate: string;
  spudDate: string;
  firstProductionDate: string;
  wellAllocationFactor: number;
  estimatedPerforatedLateralLengthFactor: number | null;
  // Tier-4 stick-to-lateral factor: a fraction in (0, 1] to 3 decimals, shown and edited as a
  // percent (see stickLateralFactorToPercent). NOT NULL, default 0.925, so never null.
  stickEstimatedLateralFactor: number;
  updatedAt?: string;
  /**
   * Server-stamped MVT tile-URL templates for the Subsurface Explorer inset map
   * scoped layers. Templates carry literal
   * `{geo_attribute}/{z}/{x}/{y}` placeholders. `offsetWells` is omitted when the
   * subject has no completed spacing job. Optional because older responses (and
   * the `POST /queries/wells` query response) do not include it.
   */
  scoped?: {
    tileUrls: {
      nearbyWells: string;
      offsetWells?: string;
    };
  };
}

export interface PermitsResult {
  id: number;
  uwi: string | null;
  uwi14: string | null;
  wellName: string | null;
  currentOperator: string | null;
  permitNum: string | null;
  permitPsa: string | null;
  permitLease: string | null;
  permitLeaseType: string | null;
  activePermit: string | null;
  permitActivityStatus: string | null;
  permitHoleDirection: string | null;
  permitLicensee: string | null;
  originalPermitLicenseDate: string | null;
  originalPermitReportDate: string | null;
  permitExpirationDate: string | null;
  permitReissueDate: string | null;
  permitAbandonedDate: string | null;
  permitBasin: string | null;
  permitField: string | null;
  permitSubBasin: string | null;
  permitPlay: string | null;
  permitSubPlay: string | null;
  permitPlayType: string | null;
  permittedDepthFt: string | null;
  permittedDepthTvdFt: string | null;
  permitTargetFormation: string | null;
  permitTargetFormationCode: string | null;
  permitTargetFluid: string | null;
  permitWaterDepthFt: string | null;
  anticipatedH2sPpm: number | null;
  anticipatedH2sInd: string | null;
  permitClass: string | null;
  permitInjClass: string | null;
  maxPermittedLiquidInjectionPressurePsi: string | null;
  maxPermittedLiquidInjectionVol: string | null;
  maxPermittedLiquidInjectionVolUnit: string | null;
  permitLicenseDateQuality: string | null;
  permitLicenseDateRanking: string | null;
  permitLicenseFiler: string | null;
  permitLicenseFilerPhone: string | null;
  permitLicenseNum: string | null;
  permitLicensePurpose: string | null;
  permitLicenseStatus: string | null;
  updatedDate: string | null;
  source: string | null;
  rowId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  resources: {
    wells: Resource;
  };
}

export interface WellFeaturesResponse {
  href: string;
  auth: string[];
  surfaceHole: GeoJSON.Point | null;
  bottomHole: GeoJSON.Point | null;
  stick: GeoJSON.Feature | null;
  survey: GeoJSON.Feature | null;
}

export interface WellFormationResponse {
  href: string;
  auth: string[];
  groundElevation: number;
  layers: {
    name: string;
    elevation: number;
    economicValue: number;
  }[];
}

export interface ProductionDecline {
  declineModel: string | null;
  rampUpModel: string | null;
  rampUpRate: number | null;
  rampUpMonths: number | null;
  rampUpSlope: number | null;
  declineStartDate: string | null;
  qi: number | null;
  bFactor: number | null;
  dLim: number | null;
  di: number | null;
  m: number | null;
  ri: number | null;
  tElf: number | null;
}

export interface VolumeRecord {
  date: string;
  oil: number;
  gas: number;
  water: number;
}

export interface TypeCurveVolumeRecord {
  month: number;
  oil: number | null;
  gas: number | null;
  water: number | null;
}
export interface RatioRecord {
  date: string;
  gor: number;
  wor: number;
  wgr: number;
  cgr?: number; // CGR may not be available in production data, only in type curves
  watercut?: number;
}

export interface TypeCurveRatioRecord {
  month: number;
  gor: number | null;
  wor: number | null;
  wgr: number | null;
  cgr: number | null;
  watercut?: number | null;
}
export interface WellSupportingFileResponse {
  id: number;
  createdBy: string;
  fileType: string;
  href: string;
  fileName: string;
  attachmentHref: string;
  createdAt: string;
}

export type WellProductionForecastResponse = Record<
  string,
  {
    volumes: VolumeRecord[];
    pdpForecastVersionId: string | null;
    wellDcaForecastId: string | null;
    forecastId: number;
    generatedOn: string;
    notes: string;
    sourceId: string;
    engineer: string;
    declineStartDate?: string;
    gasDeclineParams: ProductionDecline;
    oilDeclineParams: ProductionDecline;
    waterDeclineParams: ProductionDecline;
  }[]
>;

export interface BulkForecastItemWithVolumes {
  volumes?: VolumeRecord[];
  ratios?: RatioRecord[];
  mbts?: VolumeRecord[];
  pdpForecastVersionId?: string;
  wellDcaForecastId?: number | string;
}

export interface BulkForecastsVolumesResponse {
  results?: Record<string, BulkForecastItemWithVolumes[]>;
}

export type WellProductionRecordResponse = Record<
  string,
  {
    volumes: VolumeRecord[];
    /** Daily production only, when the request asks for them: running totals, ratios and material balance time (months) for each row. */
    cumulatives?: VolumeRecord[];
    ratios?: RatioRecord[];
    mbts?: VolumeRecord[];
    productionVolumeVersionId: string;
    monthsFromFirstToLastProduction?: number;
    monthsReported?: number;
    lastProductionDate?: string;
    lastProductionMonth?: string;
  }[]
>;

export interface ActivitiesResponse {
  totalCount: number;
  results: {
    email: string;
    newInterpretationsCount: number;
    editedInterpretationsCount: number;
  }[];
}

export interface WellProperties {
  statusCounts: { status: string; count: number }[];
  totalProduction: {
    totalLiquid: number | null;
    totalWater: number | null;
    totalGas: number | null;
  };
  wellItemsCount: number;
  topOperators: { name: string; count: number }[];
}

export interface AllocationManagerVersionsResponse {
  versions: string[];
}

export interface AllocationManagerLineItem {
  unitAllocationTractAcreageValue: number;
  unitAllocationTractAcreageFactor: number;
  unitAllocationTractLateralValue: number;
  unitAllocationTractLateralFactor: number;
  unitAllocationTractLateralGisValue: number;
  unitAllocationTract: {
    id: number;
    tractName: string;
    geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
    gisAcres: number;
  };
}

export interface AllocationManagerResponse {
  id: number;
  unitPlatGrossAcres: number;
  wellPlatTotalLateralLength: number;
  allocationMethod: 'acreage' | 'lateral_length';
  totalAcreageSum: number;
  totalGisAcreageSum: number;
  totalGisAcreageSourced: number;
  totalLateralSum: number;
  totalGisLateralSum: number;
  totalGisLateralSourced: number;
  totalAcreageFactor: number;
  totalLateralFactor: number;
  well: {
    id: number;
    uwi: string;
  };
  lineItems: AllocationManagerLineItem[];
  supportingFile?: {
    id: number;
    createdAt: string;
    updatedAt: string;
    attachmentHref: string;
  };
  verifications: {
    allocationMethods: {
      status: boolean | null;
      id: number;
      commentsCount: number;
      href: {
        events: string;
        comments: string;
      };
    };
  };
}

export interface VersionsResponse {
  versions: string[];
}

export interface AllocationManagerVersionsResponse {
  versions: string[];
}

type Formations = {
  id: number;
  href: string;
  name: string;
  depth: number;
  typeCurve: {
    name: string;
    href: string;
    spacing: number;
    status: string;
    oilEurMbbl30yr: number;
    gasEurMmcf30yr: number;
    verificationStatus?: string | null;
    lastVerifiedBy?: string | null;
    lastVerifiedDate?: string | null;
  } | null;
  slotCount: number;
};

export interface DevelopmentAreaResponse extends WellProperties {
  href: string;
  auth: string[];
  name: string;
  gisName: string;
  shapeCreatedAt: string;
  shapeCreatedBy: string;
  shapeUpdatedAt: string | null;
  shapeUpdatedBy: string | null;
  id: number;
  createdAt: string;
  state: string | null;
  isImported: boolean;
  geometry: GeoJSON.Geometry;
  basinName: string | null;
  tierName: string | null;
  unitType: string | null;
  normalLateralLength: number | null;
  developmentAreaGeographies: {
    id: number | null;
    quarterCalls: string[];
    quarteredGeography: {
      name: string;
      geometry: GeoJSON.Geometry;
    } | null;
    referenceLocation: ReferenceLocationResponse;
  }[];
  formations: Formations[];
  width: number | null;
  length: number | null;
  acreage: number;
  lateralLength: number | null;
  filedWellOnly: boolean;
  ltdCapable?: boolean;
  unitWellAssignments: {
    wellUWI: string;
    allocationManagerId: number;
    allocationMethod: string | null;
    assignmentType: string;
    wellStatus: string;
  }[];
  resources: {
    currentVersion: Resource;
    comments: Resource;
    defaultAssumptions: Resource;
    events: Resource;
    slots: ResourceNoAuth;
    versions: ResourceNoAuth;
    wellFiles: ResourceNoAuth;
    maps: {
      wells: Resource;
    };
  };
}

export type DevelopmentAreaVersionsResponse = {
  beginTime: string;
  endTime: string;
  href: string;
  versionDisplayTime: string;
  versionName: string;
  majorVersionNumber: number;
}[];

export interface ProductionForecastResponse {
  dates?: string[];
  forecastedWellsCount?: number;
  gas?: number[];
  liquid?: number[];
  water?: number[];
}

type CommentEvent = {
  kind: 'create_comment';
  content: string;
};

type CreateSupportingFileEvent = {
  kind: 'create_supporting_file';
  content: string;
};

type DestroySupportingFile = {
  kind: 'destroy_supporting_file';
  content: string;
};

type CompletionCorrectEvent = {
  kind: 'completion_correct';
  content: null;
};

type CompletionIncorrectEvent = {
  kind: 'completion_incorrect';
  content: {
    reason: string;
    details?: string;
  };
};

type CompletionRevertedEvent = {
  kind: 'completion_reverted';
  content: null;
};

/**
 * One line of a Type Curve task feed entry's card. `items` are already labelled and formatted by the
 * backend, so they render as-is.
 */
export type TypeCurveTaskSection = {
  title: string;
  items: string[];
};

/**
 * Display-ready content for a Type Curve verification event, resolved server side the same way
 * validation-check labels are. Present only on task verdict and note events.
 */
export type TypeCurveTaskDisplayMetadata = {
  headline: string;
  verdict: string | null;
  reason: string | null;
  note: string | null;
  sections: TypeCurveTaskSection[];
};

/**
 * Typed to what the feed renders. The event payload also carries the raw task, verdict, reason and
 * the cited filters / cross plots, all of which are already summarised into `displayMetadata`;
 * declare them here when something reads them directly.
 */
type TypeCurveTaskEvent = {
  kind:
    | 'verification_correct'
    | 'verification_incorrect'
    | 'verification_reverted'
    | 'verification_note_saved';
  content: string;
  displayMetadata?: TypeCurveTaskDisplayMetadata;
};

export interface EventResponse {
  id: number;
  data:
    | CommentEvent
    | CreateSupportingFileEvent
    | DestroySupportingFile
    | CompletionCorrectEvent
    | CompletionRevertedEvent
    | CompletionIncorrectEvent
    | TypeCurveTaskEvent;
  createdBy: string;
  createdAt: string;
}

export interface DevelopmentAreaQueryResponse {
  href: string;
  auth: string[];
  name: string;
  id: number;
  state: string | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  isImported: boolean;
  ltdCapable?: boolean;
  anchorGeography: { name: string; quarterCalls: string[] };
  unitType: string;
  unitName?: string;
  resources: {
    comments: Resource;
    events: Resource;
  };
}

/**
 * One seller of a package. `id` / `href` are set when the seller resolved to
 * an Insights entity — a wizard package's own seller entity, or a legacy
 * Salesforce seller account that links to exactly one entity. They are null
 * when nothing resolved: a legacy account linking to none, or to several (no
 * single entity to point at).
 *
 * `name` is always the seller's OWN display name, so for a legacy account it
 * is the CRM ACCOUNT's name and can differ from the linked entity's. Never
 * infer the entity's name from it.
 */
export type PackageSeller =
  | { id: number; name: string; href: string }
  | { id: null; name: string; href: null };

export interface PackagesQueryResponse {
  id: string;
  href: string;
  auth: string[];
  name: string;
  sellers: PackageSeller[];
  totalAcreage: number;
  createdAt?: string;
  acquirerEntities: {
    id: number;
    name: string;
    href: string;
    entityClassification: 'Primary' | 'Alias' | null;
    entityType: string | null;
    crmTractCount: number;
    titleAssetCount: number;
    crmAccountCount: number;
  }[];
  interestTypes: string[];
  closeDate: string;
  effectiveDate: string | null;
  includeChildren: boolean | null;
  completedEvals?: number;
}

export interface StateResponse {
  id: number;
  name: string;
  abbreviation: string;
}

export type OutputPartyLease = Record<string, Record<string, string>>;

export type OutputAggregate = Record<string, Record<string, string>>;

export interface TitleChainCalculatorYamlResponse {
  surface: OutputAggregate;
  aggregateRoyaltyInterest: OutputAggregate;
  aggregateBonusInterest: OutputAggregate;
  aggregateDelayInterest: OutputAggregate;
  aggregateExecutiveInterest: OutputAggregate;
  surfaceLeases: OutputPartyLease;
  aggregateMineralInterestWithRoyalty: OutputAggregate;
  aggregateMineralInterestWithBonus: OutputAggregate;
  aggregateMineralInterestWithDelay: OutputAggregate;
  surfaceEncumbrances: OutputPartyLease;
  mineralEncumbrances: OutputPartyLease;
  workingInterests: OutputPartyLease;
  mineralLeaseRoyaltyRates: CalculationOutput;
  netRevenueInterests: OutputAggregate;
  mineralLeaseOverrides: {
    from: string;
    to: string;
    lease: string;
    amount: number;
  }[];
  wells: {
    name: string;
    executiveInterests: OutputAggregate;
    workingInterests: OutputAggregate;
  }[];
  imageHref: string;
}

export interface BasinTierResponse {
  basin: string;
  tier: string;
}

export interface ScenariosResponse {
  id: number;
  name: string;
  interestType: string;
  href: string;
  isDefault: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisionResponse {
  id: number | string;
  href: string;
  ocrPath: string | null;
  outputPath: string | null;
  status: 'queued' | 'pending' | 'complete' | 'error';
  state: JSONObject;
  documentState: JSONObject;
  createdAt: string;
  updatedAt: string;
  notifications: Notifications;
  content?: JSONValue;
  metadata: {
    completion: {
      service: string;
      version: string;
      prompt: string;
      createdBy: string;
    };
    ocr: {
      service: string;
      version: string;
    };
  };
  resources: {
    comments: Resource;
    events: Resource;
    extraction: Resource;
    items: Resource;
  };
  markAsCancelledHref: string;
}

export interface OCRExtractionResponse {
  content: {
    paragraphs:
      | {
          spans?: {
            offset: number;
            length: number;
          }[];
          boundingRegions?: {
            pageNumber: number;
            polygon: number[];
          }[];
          role?: string;
          content: string;
        }[]
      | null;
    tables:
      | {
          rowCount: number;
          columnCount: number;
          cells: {
            kind?: string;
            rowIndex: number;
            columnIndex: number;
            content: string;
            boundingRegions?: {
              pageNumber: number;
              polygon: number[];
            }[];
            spans?: {
              offset: number;
              length: number;
            }[];
            elements?: string[];
          }[];
        }[]
      | null;
    text: string | null;
  };
  resources: {
    contentDownload: Resource;
  };
}

export interface CompletionItemResponse {
  path: string;
  commentsCount: number;
  resources: {
    comments: Resource;
    events: Resource;
  };
}

export interface VerificationItemEventResponse {
  id: number;
  itemId: number;
  itemType: string;
  userId: number;
  kind:
    | 'verification_correct'
    | 'verification_incorrect'
    | 'verification_reverted'
    | 'create_comment';
  payload: {
    reason?: string | null;
    details?: Record<string, unknown> | null;
    content?: string[];
  };
  createdBy: string;
  createdAt: string;
}

export interface VerificationItemCommentResponse {
  id: number;
  itemId: number;
  itemType: string;
  content: string;
  createdBy: string;
  createdAt: string;
}

export interface CompletionConfigurationOption {
  provider: string;
  version: string;
  default: boolean;
  id: number;
}

export interface CompletionPromptConfigurationOption {
  id: number;
  version: string;
  default: boolean;
  category: string;
}

export interface DocumentTypeOption {
  value: string;
  label: string;
  promptCategory: string;
  promptVersionLabel?: string;
  acceptedFormats?: string[];
}

export interface CompletionConfigurationResponse {
  completionConfigurations: CompletionConfigurationOption[];
  extractionConfigurations: CompletionConfigurationOption[];
  promptConfigurations: CompletionPromptConfigurationOption[];
  documentTypes: DocumentTypeOption[];
}

export interface BulkImportResponse {
  account: {
    id: number;
    username: string;
    encrypted_password: string;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
  };
  days_before_type_cast: number;
  days: number;
  validation_context: null;
  errors: Record<string, unknown>;
}

export interface AcresByInstrumentType {
  instrumentType: string;
  netAcreageUnits: string;
  netAcres: number;
  grossAcres: number;
}

export interface ExecutiveSummary {
  acresByInstrumentType?: AcresByInstrumentType[];
  assetCount?: number;
  pdpSlotCount?: number;
  ducSlotCount?: number;
  permitSlotCount?: number;
  /** Each Create New Well status its own card (Query::Executive::SlotCounts). */
  proposalSlotCount?: number;
  plannedSlotCount?: number;
  prePermitSlotCount?: number;
  apdSlotCount?: number;
  ltdSlotCount?: number;
  operatorCount?: number;
  formationCount?: number;
  targetNpvExcluded?: number;
  targetNpvIncluded?: number;
  maxNpvExcluded?: number;
  maxNpvIncluded?: number;
}
export interface ExecutiveSummaryUpdatedEventPayload {
  section: 'tracts' | 'slots';
  data: ExecutiveSummary;
  evaluationCalculationId: number;
  calculationId: string;
}

export interface AggregatedFreeCashFlowResultResponse {
  executiveSummary?: ExecutiveSummary;
  results: {
    calculationId: string;
    request:
      | (AggregatedFreeCashFlowPayload['calculations'][number] & {
          appliedEffectiveDate: string;
        })
      | null;
    result: {
      slotCategory: SlotCategory[];
      month?: string[];
      year?: string[];
      quarter?: string[];
      grossCashflow: number[];
      netCashflow: number[];
      cumulativeNetCashflow: number[];
      cumulativeGrossCashflow: number[];
      grossOil?: number[];
      grossGas?: number[];
      grossNgl?: number[];
      grossWater?: number[];
      netOil?: number[];
      netGas?: number[];
      netNgl?: number[];
      netWater?: number[];
      grossGasSold?: number[];
      netGasSold?: number[];
      grossCosts?: number[];
      netCosts?: number[];
      totalCumulativeGrossOil?: number[];
      totalCumulativeNetOil?: number[];
      totalCumulativeGrossGasSold?: number[];
      totalCumulativeNetGasSold?: number[];
      totalCumulativeGrossNgl?: number[];
      totalCumulativeNetNgl?: number[];
      totalCumulativeGrossWater?: number[];
      totalCumulativeNetWater?: number[];
      packageOverview: {
        maxPricing: Pricing[];
        targetPricing: Pricing[];
        mtmPricing?: Pricing[];
      };
      rescatSlotCount: CategorySlotCount[];
    } | null;
    status: 'in_progress' | 'completed' | 'error' | 'pending' | 'unknown';
  }[];
}

export interface CashflowsByMonthResponse {
  cashflows: {
    data: {
      date: (string | null)[];
      slotRef: (string | null)[];
      slotCategory: (string | null)[];
      formationName: (string | null)[];
      basin: (string | null)[];
      tier: (string | null)[];
      developmentAreaStateAbbreviation: (string | null)[];
      wellPermitDate: (string | null)[];
      spudDate: (string | null)[];
      wellCompletionDate: (string | null)[];
      firstProductionDate: (string | null)[];
      shutInDate: (string | null)[];
      netRevenueInterest: (number | null)[];
      nriSource: (string | null)[];
      netWorkingInterest: (number | null)[];
      wiSource: (string | null)[];
      ltdAssumptionsType: (string | null)[];
      ducKind: (string | null)[];
      permitKind: (string | null)[];
      applyLtdHurdleRate: (boolean | null)[];
      ltdHurdleRate: (number | null)[];
      applyDucHurdleRate: (boolean | null)[];
      ducHurdleRate: (number | null)[];
      applyPermitHurdleRate: (boolean | null)[];
      permitHurdleRate: (number | null)[];
      grossOil: (number | null)[];
      grossGas: (number | null)[];
      grossGasSold: (number | null)[];
      grossNgl: (number | null)[];
      grossWater: (number | null)[];
      netOil: (number | null)[];
      netGasSold: (number | null)[];
      netNgl: (number | null)[];
      gasShrinkPercentage: (number | null)[];
      nglYield: (number | null)[];
      waterForecastSource: (string | null)[];
      waterCutPercentage: (number | null)[];
      oilWellheadPrice: (number | null)[];
      gasWellheadPrice: (number | null)[];
      oilDifferentialPrice: (number | null)[];
      gasDifferentialPrice: (number | null)[];
      nglDifferentialPercentage: (number | null)[];
      oilRealizedPrice: (number | null)[];
      gasRealizedPrice: (number | null)[];
      nglRealizedPrice: (number | null)[];
      grossOilRevenue: (number | null)[];
      grossGasRevenue: (number | null)[];
      grossNglRevenue: (number | null)[];
      grossRevenue: (number | null)[];
      source: (string | null)[];
      netOilRevenue: (number | null)[];
      netGasRevenue: (number | null)[];
      netNglRevenue: (number | null)[];
      netRevenue: (number | null)[];
      oilTaxPercentage: (number | null)[];
      gasTaxPercentage: (number | null)[];
      nglTaxPercentage: (number | null)[];
      adValoremTaxPercentage: (number | null)[];
      netOilTaxAmount: (number | null)[];
      netGasTaxAmount: (number | null)[];
      netNglTaxAmount: (number | null)[];
      netAdValoremTaxAmount: (number | null)[];
      oilOpexCost: (number | null)[];
      gasOpexCost: (number | null)[];
      waterOpexCost: (number | null)[];
      netOilOpexCost: (number | null)[];
      netGasOpexCost: (number | null)[];
      netWaterOpexCost: (number | null)[];
      dcCostPerWell: (number | null)[];
      percentageCostFirstProductionDate: (number | null)[];
      percentageCostSpudDate: (number | null)[];
      loeCostPerWellYear1: (number | null)[];
      loeCostPerWellYear2: (number | null)[];
      loeCostPerWellYear3: (number | null)[];
      loeCostPerWellYear4: (number | null)[];
      loeCostPerWellYear5AndUp: (number | null)[];
      netFirstProductionDateDcCostPerWell: (number | null)[];
      netSpudDateDcCostPerWell: (number | null)[];
      netLoeFixedWellCost: (number | null)[];
      afeDrillingCosts?: (number | null)[];
      afeCompletionCosts?: (number | null)[];
      afeFacilitiesCosts?: (number | null)[];
      afeContingenciesCosts?: (number | null)[];
      afeOtherCosts?: (number | null)[];
      grossCosts: (number | null)[];
      netCosts: (number | null)[];
      leaseholdCashflow: (number | null)[];
      grossCashflow: (number | null)[];
      netCashflow: (number | null)[];
    };
  };
}

// Server-side paginated cashflows types
export interface CashflowRow {
  date: string | null;
  slotRef: string | null;
  slotCategory: string | null;
  formationName: string | null;
  basin: string | null;
  tier: string | null;
  developmentAreaStateAbbreviation: string | null;
  wellPermitDate: string | null;
  spudDate: string | null;
  wellCompletionDate: string | null;
  firstProductionDate: string | null;
  shutInDate: string | null;
  netRevenueInterest: number | null;
  netWorkingInterest: number | null;
  ltdAssumptionsType: string | null;
  ducKind: string | null;
  permitKind: string | null;
  applyLtdHurdleRate: boolean | null;
  ltdHurdleRate: number | null;
  applyDucHurdleRate: boolean | null;
  ducHurdleRate: number | null;
  applyPermitHurdleRate: boolean | null;
  permitHurdleRate: number | null;
  grossOil: number | null;
  grossGas: number | null;
  grossGasSold: number | null;
  grossNgl: number | null;
  grossWater: number | null;
  netOil: number | null;
  netGasSold: number | null;
  netNgl: number | null;
  gasShrinkPercentage: number | null;
  nglYield: number | null;
  waterForecastSource: string | null;
  waterCutPercentage: number | null;
  oilWellheadPrice: number | null;
  gasWellheadPrice: number | null;
  oilDifferentialPrice: number | null;
  gasDifferentialPrice: number | null;
  nglDifferentialPercentage: number | null;
  oilRealizedPrice: number | null;
  gasRealizedPrice: number | null;
  nglRealizedPrice: number | null;
  grossOilRevenue: number | null;
  grossGasRevenue: number | null;
  grossNglRevenue: number | null;
  grossRevenue: number | null;
  source: string | null;
  netOilRevenue: number | null;
  netGasRevenue: number | null;
  netNglRevenue: number | null;
  netRevenue: number | null;
  oilTaxPercentage: number | null;
  gasTaxPercentage: number | null;
  nglTaxPercentage: number | null;
  adValoremTaxPercentage: number | null;
  netOilTaxAmount: number | null;
  netGasTaxAmount: number | null;
  netNglTaxAmount: number | null;
  netAdValoremTaxAmount: number | null;
  oilOpexCost: number | null;
  gasOpexCost: number | null;
  waterOpexCost: number | null;
  netOilOpexCost: number | null;
  netGasOpexCost: number | null;
  netWaterOpexCost: number | null;
  dcCostPerWell: number | null;
  percentageCostFirstProductionDate: number | null;
  percentageCostSpudDate: number | null;
  loeCostPerWellYear1: number | null;
  loeCostPerWellYear2: number | null;
  loeCostPerWellYear3: number | null;
  loeCostPerWellYear4: number | null;
  loeCostPerWellYear5AndUp: number | null;
  netFirstProductionDateDcCostPerWell: number | null;
  netSpudDateDcCostPerWell: number | null;
  netLoeFixedWellCost: number | null;
  afeDrillingCosts?: number | null;
  afeCompletionCosts?: number | null;
  afeFacilitiesCosts?: number | null;
  afeContingenciesCosts?: number | null;
  afeOtherCosts?: number | null;
  applyAfe?: boolean | null;
  grossCosts: number | null;
  netCosts: number | null;
  leaseholdCashflow: number | null;
  grossCashflow: number | null;
  netCashflow: number | null;
}

export interface CashflowsPaginationMeta {
  nextCursor: string | null;
  prevCursor: string | null;
  hasMore: boolean;
  hasPrevious: boolean;
  pageSize: number;
}

export interface CashflowsPaginatedResponse {
  rows: CashflowRow[];
  meta: CashflowsPaginationMeta;
}

export interface CashflowsFilterConfig {
  column: string;
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'contains';
  value: string | number | boolean | string[] | number[];
}

export interface CashflowsSortConfig {
  column: string;
  dir: 'asc' | 'desc';
}

export interface CashflowsCachedPage {
  rows: CashflowRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasMore: boolean;
  hasPrevious: boolean;
  pageNumber: number;
}

export interface AggregatedFreeCashFlowCalculationResponse {
  calculations: {
    calculationId: string;
    status: 'pending' | 'error';
    errorMessage?: string;
    notifications?: {
      channel: string;
      events: {
        name: string;
      }[];
    };
    href: string;
  }[];
}

export interface StructureGridValidationResult {
  type: string;
  status: string;
  isLatest: boolean;
  isValid: boolean;
  level: string;
  failureReason: string | null;
  displayMetadata: {
    label: string;
    status: string;
  };
}

export interface StructureGridValidationResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: StructureGridValidationResult[];
}

export interface TypeCurveValidationResult {
  type: string;
  status: string;
  isLatest: boolean;
  isValid: boolean;
  level: string;
  failureReason: string | null;
  displayMetadata: {
    label: string;
    status: string;
    validationType: string;
    failureMessages: string[];
    helpHint?: {
      label?: string;
      level: string;
      description: string;
      failureIndication?: string;
    };
  };
}

export interface TypeCurveValidationResponse {
  totalCount: number;
  results: TypeCurveValidationResult[];
}

export interface AggregationMetrics {
  oilEur: number;
  gasEur: number;
  waterEur: number;
  recoveredOil: number;
  recoveredGas: number;
  recoveredWater: number;
  remainingOil: number;
  remainingGas: number;
  remainingWater: number;
  gasOilRatioTotal: number;
  waterCutTotal: number;
  waterGasRatioTotal: number;
  oilGasRatioTotal: number;
  waterOilRatioTotal: number;
}

export interface WellPDPDeclineMetricData {
  cumulatives: VolumeRecord[];
  ratios: RatioRecord[];
  volumes: VolumeRecord[];
  mbts: VolumeRecord[];
  productionVolumeVersionId?: string;
  /** The source the production was read from; only when the request named a source or a DCA scenario. */
  productionSource?: ProductionDataSource;
  wellDcaForecastId?: string | null;
  pdpForecastVersionId?: string | null;
  aggregationMetrics: AggregationMetrics;
  errors: {
    code: string;
    message: string;
    details: { streamType: string; field: string };
  }[];
}

export interface BulkForecastMetricData {
  totalCount: number;
  results: WellProductionForecastResponse;
  errors: {
    code: string;
    message: string;
    details: { streamType: string; field: string };
  }[];
}

// New Insights PDP Forecast versions
export interface WellDcaForecastShowResponse {
  id: number;
  uwi: string;
  dcaScenarioId: number;
  productionVolumeVersionId: string;
  uniqueTcId: string | null;
  dcaForecastParameters: {
    stream: 'oil' | 'gas' | 'water';
    inEarlyTime: boolean;
    dcaParams: {
      dcaModel?: string;
      qi?: number;
      di?: number;
      bFactor?: number;
      dlim?: number;
      forecastStartDate?: string;
      durationPeriod?: number;
      qiFormat?: string;
      diFormat?: string;
      forecastDurationOption?: string;
      rampUp?: { model?: string; qi?: number; n?: number; tRamp?: number };
      ri?: number;
      tElf?: number;
      m?: number;
      mainStream?: string;
    };
  }[];
  productionSegments: { stream: string; segmentStartDate: string }[];
  productionOutliers: { stream: string; outlierDate: string }[];
}

export interface LegacyDeclineParams {
  declineModel?: string;
  rampUpRate?: number;
  rampUpMonths?: number;
  qi?: number;
  di?: number;
  bFactor?: number;
  dLim?: number;
}

export interface LegacyBulkForecastItem {
  pdpForecastVersionId?: string;
  oilDeclineParams?: LegacyDeclineParams;
  gasDeclineParams?: LegacyDeclineParams;
  waterDeclineParams?: LegacyDeclineParams;
}

// Old Legacy PDP Forecast versions
export interface LegacyBulkForecastsResponse {
  results?: Record<string, LegacyBulkForecastItem[]>;
}

export interface VolumeMetric {
  pdpDeclines: Record<string, WellPDPDeclineMetricData[]>;
}

export interface ConvertedDcaParameters {
  dcaModel: string;
  qi: number;
  bFactor: number;
  di: number;
  dlim: number;
  forecastStartDate: string;
}

export interface DeclineCurveAnalysis {
  pdpDeclines: Record<
    string,
    {
      volumes: VolumeRecord[];
      convertedDcaParameters?: {
        gasDcaParams: ConvertedDcaParameters;
        oilDcaParams: ConvertedDcaParameters;
        waterDcaParams: ConvertedDcaParameters;
      };
      errors: {
        code: string;
        message: string;
        details: { streamType: string; field: string };
      }[];
    }[]
  >;
}

export interface DeclineCurveAnalysisTableResponse {
  dcaTable: {
    uwi: string;
    productionVolumeVersionId: string;
    dcaRows: {
      productionDate: string;
      monthsOnline: number;
      oilProductionVolume: number | null;
      gasProductionVolume: number | null;
      waterProductionVolume: number | null;
      oilModelForecast: number | null;
      gasModelForecast: number | null;
      waterModelForecast: number | null;
      grossOil: number | null;
      grossGas: number | null;
      grossWater: number | null;
    }[];
    errors: {
      code: string;
      message: string;
      details: { streamType: string; field: string };
    }[];
  }[];
}

export interface WellRecordVersionsResponse {
  results: WellVersionsResponse[];
  totalCount: number;
}

export interface WellDcaScenariosResponse {
  results: DcaScenariosResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// A segment of a reconcile/undo audit-log detail bullet: either literal text or
// an entity reference rendered as an inline "<name> (Entity ID: <id>)" link. Mirrors the
// mock's AuditSegment (DedupeTearsheet).
export type AuditSegment = string | { entityName: string; entityId: number };

// One guardrail's evaluation, snapshotted at decision time — the auto-confirm
// activity-log detail renders these as a Check / Requirement / Observed / Result
// table (parity with the guardrail bench).
export interface AuditGateRow {
  guardrailId: string;
  requirement: string;
  observed: string;
  // 'not_evaluated' is emitted for candidate-dependent guardrails when a party
  // drew no candidates, alongside pass/fail/disabled.
  result: 'pass' | 'fail' | 'disabled' | 'not_evaluated';
}

// Reconcile / undo feed-row decoration (Query::ActivityFeeds::EntityFeed): the
// audit action label plus the detail bullets, rendered client-side.
// One additional-survivor's alias rationale (R8), sourced from the durable event
// snapshot (alias_rationales), not the live alias row.
export interface ReconcileAliasRationale {
  survivorId: number;
  survivorName: string | null;
  rationale: string;
}

// Per-entity history for a reconcile event (R8). Discriminated on `action`:
// `rationale` is present only on aliased entries (and may be null when none was
// recorded). survivorId/Name is the entity this one merged into or aliased to.
export type ReconcileEntityHistory =
  | {
      action: 'deduplicated';
      entityId: number;
      entityName: string | null;
      survivorId: number;
      survivorName: string | null;
    }
  | {
      action: 'aliased';
      entityId: number;
      entityName: string | null;
      survivorId: number;
      survivorName: string | null;
      rationale: string | null;
    };

export interface ReconcileAuditDetails {
  label: string;
  bullets: AuditSegment[][];
  // R8 — present only when the event carries the snapshot; absence is meaningful
  // (older events, and undo rows surface deduplication history only).
  aliasRationales?: ReconcileAliasRationale[];
  perEntityHistory?: ReconcileEntityHistory[];
}

export interface StructuredPartyAuditEventRow {
  activityId: string;
  activityType: 'event';
  id: number;
  expandable: false;
  createdAt: string;
  itemType: string;
  itemId: number;
  data: {
    user: string;
    action: string;
    description: string;
    // Optional structured detail for grouped reconcile / undo entries: one
    // bullet per line, entity segments rendered as links. Falls back to
    // `description` when absent.
    details?: AuditSegment[][];
    // Gate rundown for auto-confirm rows; absent for all other events.
    gateTrace?: AuditGateRow[];
    kind: string;
    payload: Record<string, unknown>;
  };
}

export interface StructuredPartyAuditToolSummaryRow {
  toolName: string;
  callCount: number;
  totalDurationMs: number;
  successCount: number;
  failureCount: number;
}

export interface StructuredPartyAuditTimelineRow {
  toolName: string;
  callNumber: number;
  calledAt: string | null;
  offsetMs: number | null;
  durationMs: number | null;
  success: boolean | null;
  errorMessage: string | null;
}

export interface StructuredPartyAuditSuggestionRow {
  id: number;
  status: string;
  suggestedEntityId: number | null;
  suggestedEntityName: string | null;
  confidenceScore: number | null;
  confidenceFactors: {
    factor: string;
    weight: number;
    scored: number | null;
    note?: string;
  }[];
  reviewReason: string | null;
  processFailed: boolean;
  error: string | null;
  // PEA-specific (null for EMA suggestions)
  addressId?: number | null;
  addressLabel?: string | null;
  acknowledgedAt?: string | null;
  parsedComponents?: {
    streetAddress: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
  } | null;
}

export interface StructuredPartyAuditAgentRunRow {
  activityId: string;
  activityType: 'agent_run';
  id: number;
  expandable: true;
  createdAt: string;
  itemType: string;
  itemId: number;
  data: {
    user: string;
    action: string;
    description: string;
    status: string | null;
    processFailed: boolean;
    failureReason: string | null;
    model: string | null;
    startedAt: string | null;
    endedAt: string | null;
    durationMs: number | null;
    durationFormatted: string | null;
    documentId: number | null;
    entityId: number | null;
    importedEntityId: number | null;
    // The run's prompt endpoint; null for a run that kept none.
    promptHref: string | null;
    toolSummary: StructuredPartyAuditToolSummaryRow[];
    timeline: StructuredPartyAuditTimelineRow[];
    conversation: unknown[];
    suggestions: StructuredPartyAuditSuggestionRow[];
  };
}

export type StructuredPartyAuditRow =
  StructuredPartyAuditEventRow | StructuredPartyAuditAgentRunRow;

export interface StructuredPartyActivityFeedResponse {
  results: StructuredPartyAuditRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

export type ApiResponse = Readonly<
  | GunBarrelResponse
  | ScenariosResponse
  | TitleWorkspaceResponse
  | ReferenceLocationResponse
  | DocumentInterpretationResponse
  | TitleCalculationResponse
  | TractResponse
  | DocumentResponse
  | EntityResponse
  | PackageEvaluationResponse
  | DevelopmentAreaResponse
  | AliasedEntityResponse
  | PackageResponse
  | EvalSlotResponse
  | CompletionItemResponse
  | VerificationItemEventResponse
  | VerificationItemCommentResponse
  | BulkImportResponse
  | CreateAccountResponse
  | AggregatedFreeCashFlowResultResponse
  | AggregatedFreeCashFlowCalculationResponse
  | { notifications: Notifications }
  | AllocationManagerResponse
  | StructureGridCollectionCreateResponse
  | StructureGridCollectionUpdateResponse
  | Well3DViewerResponse
  | StructureGridResponse
  | DevAreaStatesResponse
  | StructureGridValidationResponse
  | StructureGridListResponse
  | VolumeMetric
  | BulkForecastMetricData
  | DeclineCurveAnalysis
  | CRMAccountResponse[]
  | CRMAccountResult
  | CreateCrmAccountResponse
  | CRMAccountSearchResult
  | CRMTractsAndSubtractsResult
  | DeclineCurveAnalysisTableResponse
  | StructureGridCollectionValidityResponse
  | TitleValidatorResponse
  | EntityLinkResponse
  | SubtractIntersectionAnalysisParameterResponse
  | AllocationMethodUpdateResponse
  | EntitySummaryStatsResponse
  | EntityLibraryAnalyticsResponse
  | CountyCoverageSummaryResponse
  | EstablishmentsResponse
  | CapacityResponse
  | WellboresResponse
  | WellboreGeometryExportResponse
  | WellMapResponse
  | WellIntersectionAnalysisResponse
  | WellRecordVersionsResponse
  | WellDcaScenariosResponse
  | StructuredPartyActivityFeedResponse
  | EarlyTimeStreamsResponse
  | TypeCurveOptionsResponse
  | BulkTypeCurveVolumesResponse
  | SaveDcaForecastResponse
  | StructureGridProfilesResponse
  | WellLandingAnalysisExportResponse
  | WellLandingAnalysisRequestResponse
  | WellLandingAnalysisResponse
  | WellLandingAnalysisInstancesResponse
  | WellLandingAnalysisRequestCancelResponse
  | GeneralConfig
  | SegmentationConfig
  | OutlierConfig
  | ValidateAreaConfigResponse
  | DcaScenarioResponse
  | StructureGridCollectionsQueryListResponse
  | LandingAnalysisCountsResponse
  | WellLandingAnalysisJobsQueryResponse
  | WellLandingAnalysisWellsQueryResponse
  | WellSpacingResultsResponse
  | WellSpacingEvalPointResultsResponse
  | TypeCurveProbitResponse
  | TypeCurveWorkspaceResponse
  | TypeCurveWorkspacesListResponse
  | StructuredPartyLifecycleResponse
  | StructuredPartyRerunResponse
  | StructuredPartyRerunDocumentResponse
  | StructuredPartyAddResponse
  | StructuredAddressActionResponse
  | ReconcilePreviewResponse
  | ReconcileCreateResponse
  | ReconcileUndoResponse
  | EntityReconcileSignalResponse
  | EntityReconcileScoredResponse
  | EntityReconcilePreviewResponse
  | EntityReconcileCreateResponse
  | AliasedClusterResponse
  | GeologicIntervalValidationResponse
  | PreDrillWorkspace
  | PreDrillWellboreDetails
  | WellborePointsResponse
  | IntersectedGridsResponse
  | PreDrillTargetZoneSuggestions
  | PreDrillVerificationItems
  | PreDrillVerificationItem
  | PreDrillVerificationEventResponse
  | PreDrillAgentRunsResponse
  | PreDrillStructureGridProfilesResponse
  | PreDrillGunBarrelResponse
  | PreDrillSgcQueryResponse
  | PreDrillTargetZoneIntervalsResponse
  | LandingInterpretationIntervalsResponse
  | WellsFacetsResponse
  | PreDrillWorkspaceListResponse
  | AreaWorkspace
  | PreDrillCreateResponse
  | PreDrillEnqueuedResponse
  | GenerateLtdResponse
  | UnitFormationSlotsResponse
  | PromoteForecastsResponse
  | UpdateFormationSlotResponse
  | InventoryVerificationEventResponse
  | InventorySlotRequestResponse
  | InventoryGeometryRefreshResponse
  | InventoryFormationSlotsBulkResponse
  | SlotCompositeResponse
  | SlotCompositeMergeResponse
  | SlotCompositeUnmergeResponse
  | SlotCompositesIndexResponse
  | CreatedAreaGeometry
  | InventoryWorkspacePromotionPlanResponse
  | PromoteInventoryWorkspaceResponse
>;

export interface GenerateLtdResponse {
  id: number;
  href: string;
  auth: string[];
  status: string;
  createdAt: string;
  notifications: {
    channel: string;
    events: {
      name: string;
    }[];
  };
  resources: {
    activeJobStatus: {
      href: string;
      auth: string[];
    };
    queries: {
      inventoryUnitSlots: {
        href: string;
        auth: string[];
      };
    };
  };
}

/**
 * One row from `POST /queries/wells/autocomplete`. Slim shape designed for
 * UWI ComboBox autocomplete — no geometry / type curves / forecasts.
 * `unitName` is null when the well has no current unit assignment; the
 * BE Asset::Wellbore validator rejects creates with a null unit, so the
 * UWI ComboBox should mark unit-less rows non-selectable visually.
 */
export interface WellsAutocompleteRow {
  uwi: string;
  /** Composed `lease_name + #well_number`; nullable. */
  name: string | null;
  leaseName: string | null;
  wellNumber: string | null;
  wellStatus: string | null;
  unitName: string | null;
}

export type ApiQueryResponse = Readonly<
  | TitleWorkspaceResponse
  | ReferenceLocationResponse
  | DocumentInterpretationResponse
  | LocationResponse
  | EntityBaseResponse
  | DocumentResponse
  | WellsAutocompleteRow
  | WellsQueryResponse
  | CountyQueryResponse
  | TransactionQueryResponse
  | DevelopmentAreaQueryResponse
  | ActivitiesQueryResponse
  | PackagesQueryResponse
  | InventoryResponse
  | StructureGridQueryResponse
  | StructureGridManagementQueryResponse
  | GeologicIntervalTypeCurveQueryResponse
  | StateTaxesQueryResponse
  | AreasQueryResponse
  | GeologicIntervalQueryResponse
  | GeologicIntervalListQueryResponse
  | GeologicIntervalCreateResponse
  | TypeCurveHeaderQueryResponse
  | StructureGridCollectionsQueryResponse
  | WellVersionsResponse
  | WellProductionRecordResponse
  | WellProductionForecastResponse
  | PipelineQueryResponse
  | DrillingRigQueryResponse
  | CRMTractsResponse
  | SalesforceAccountResponse
  | DcaScenariosResponse
  | CountyCoverageResponse
  | ActivityFeedItemResponse
  | EntitySearchQueryResult
  | AfeCatalogResult
  | PriceDeckQueryResult
  | DevelopmentPaceScenarioResult
  | DiscountRateScenarioResult
  | DifferentialScenarioResult
  | TaxScenarioResult
  | ExpenseScenarioResult
  | PackageLibraryResult
  | EvalUnifiedResult
  | OperatorSearchResult
  | DivisionOrderResult
  | RoyaltyCheckResult
  | RoyaltyCheckLineItem
  | PermitsResult
>;

export interface EntitySearchQueryResult {
  id: number;
  name: string;
  entityType: 'Individual' | 'Organization' | 'Imported';
  entityClassification: 'Primary' | 'Alias';
  createdAt: string | null;
  updatedAt: string | null;
  aliasCount: number;
  documentCount: number;
  capacityCount: number;
  crmAccountCount: number;
  crmTractCount: number;
  titleAssetCount: number;
  href: string;
  deduplicatedAt?: string | null;
  mergedInto?: {
    id: number;
    name: string;
    href: string;
  } | null;
  // Consolidation-risk flag (PROD/2907373590): category marks flagged entities in
  // the list / dedupe search; rationale is shown on hover. Absent/null = unflagged.
  // flagSeverity drives the chip: 'warning' (yellow/gated), 'info' (blue), or null
  // (unflagged or dormant — no chip).
  flagCategory?: string | null;
  flagRationale?: string | null;
  flagSeverity?: FlagSeverity | null;
  portfolioInvestment?: boolean;
  // Tagged "Operator": a code points here or, for a primary, at one of its
  // aliases.
  operator?: boolean;
}

export interface EntitySummaryStatsResponse {
  totalEntities: number;
  individualEntities: number;
  organizationEntities: number;
  importedEntities: number;
  entitiesLast24h: number;
  entitiesLast7d: number;
  entitiesLastMonth: number;
  entitiesLast6m: number;
}

// Shared by the analytics panel's source filter and leaderboard columns so a
// typo'd id fails to compile instead of silently matching nothing.
export type EntityActivitySource =
  | 'document_ingest'
  | 'operator_feed'
  | 'manual_add'
  | 'add_as_new'
  | 'deduplicate'
  | 'bulk_deduplicate'
  | 'reconcile_entity_page'
  | 'reconcile_document'
  | 'mark_as_correct'
  | 'direct_alias';

export type EntityActivityDirection = 'add' | 'remove' | 'alias';

export interface EntityLibraryAnalyticsTrendPoint {
  date: string;
  entitiesAdded: number;
  entitiesRemoved: number;
  cumulativeEntities: number;
}

export interface EntityLibraryAnalyticsLeader {
  actorKey: string;
  actorLabel: string;
  bySource: {
    source: EntityActivitySource;
    direction: EntityActivityDirection;
    count: number;
  }[];
  total: number;
}

export interface EntityLibraryAnalyticsResponse {
  // The applied window — echoes the server-side clamp, which can differ from
  // what was requested.
  window: { startDate: string; endDate: string; days: number };
  trend: EntityLibraryAnalyticsTrendPoint[];
  leaderboard: EntityLibraryAnalyticsLeader[];
}

export interface ReferenceLocationBucket {
  href: string;
  id: number;
  auth: string[];
  count: number;
  name: string;
  shortName: string;
}

export type TitleCalculationDetailsResponse = {
  transactionId: number;
  expiration: boolean;
  interpretation: DocumentInterpretationResponse;
  changed: boolean;
  tracts: TractsCalculationDetails[];
};

export interface SearchResult<
  Data,
  Facets = undefined,
  Aggregations = undefined,
> {
  totalCount: number;
  page: number;
  pageSize: number;
  results: Data[];
  facets?: Facets;
  boundingBox?: { points: [Point, Point] } | null;
  aggregations?: Aggregations;
}

export const STATUS_TYPES = {
  partialHBP: 'Partial HBP',
  active: 'Active',
  inactive: 'Inactive',
  HBP: 'HBP',
} as const;

type StatusKeys = keyof typeof STATUS_TYPES;
export type StatusValues = (typeof STATUS_TYPES)[StatusKeys];

export interface WellRelatedUnitsResponse {
  unit: {
    id: string;
    name: string;
    href: string;
  };
  fwo: boolean;
  wellParticipationFactor: string;
  acreage: number;
  width: string;
  length: string;
}

export interface DevelopmentAreaOverlap {
  id: number;
  name: string;
  overlapPercentage: number;
}

// Top-level overlap graph entry (one per unit that overlaps others).
// Lives at `response.overlappingResults[]` — the spec keeps `results[].overlappingUnits`
// as an empty array and puts the actual graph here.
export interface DevelopmentAreaOverlappingResult {
  id: number;
  name: string;
  overlappingUnits: DevelopmentAreaOverlap[];
}

export interface FilteredUnit {
  id: number;
  name: string;
}

export interface DevelopmentArea extends Record<string, unknown> {
  id: number;
  href: string;
  name: string;
  unitName: string | null;
  unitType: string;
  unitDevelopmentType: string;
  state: string;
  unitSource: string;
  estimateSource: string | null;
  basin: string | null;
  tier: string | null;
  basinTierId: number | null;
  acreage: number;
  width: number;
  length: number;
  azimuth: number;
  overlapping: boolean;
  overlappingUnits: DevelopmentAreaOverlap[];
  removed: boolean;
  // Optional fields carried by the shared fixture / real backend response
  uuid?: string;
  auth?: string[];
  filedWellOnly?: boolean;
  ltdCapable?: boolean;
  isImported?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  notifications?: { channel: string; events: { name: string }[] };
  resources?: Record<string, { href: string; auth: string[] }>;
  anchorGeography?: { name: string; quarterCalls: unknown[] };
  verificationStatus?: 'correct' | 'incorrect' | null;
}

export interface DevelopmentAreasQueryRequest {
  page?: number;
  pageSize?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  text?: string;
  // Existing fields per spec — sent with defaults to match canonical contract
  units?: 'all' | 'plat_based' | 'dev_areas';
  unit_allocation_flag?: boolean;
  source_document_assignment?: 'assigned' | 'unassigned';
  // Type-curve scenario context: header details id (workspace mode) or header id (scenario mode)
  type_curve_header_details_id?: number;
  typeCurveHeaderId?: number;
  // Inventory Workspace only: scope units to the workspace's candidate units and
  // apply the selected formation's FOUCS overlay. Omitted on plain Type Curve pages.
  inventory_workspace_id?: number;
  formation_name?: string;
  polygon_ewkb?: string;
  polygon_geojson?: { type: string; coordinates: number[][][] };
  bounding_box?: {
    sw_lng: number;
    sw_lat: number;
    ne_lng: number;
    ne_lat: number;
  };
  response_config?: {
    include_overlapping_flag?: boolean;
    include_cross_section_line?: boolean;
    include_facets?: boolean;
  };
  // Units filter accordion params. Backend doesn't accept
  // most of these yet — they're documented as future strong_params extensions.
  names?: string[];
  unitNames?: string[];
  unitTypes?: string[];
  unitDevelopmentTypes?: string[];
  states?: string[];
  unitSources?: string[];
  estimateSources?: string[];
  basinTierIds?: number[];
  acreage?: { min?: number; max?: number };
  length?: { min?: number; max?: number };
  width?: { min?: number; max?: number };
  overlapping?: boolean;
  removed?: boolean;
  filedWellOnly?: boolean;
}

export interface UnitOverride {
  id: number;
  unitId: number;
  action: 'remove' | 'rotate';
  azimuthRotationAngleDeg: number | null;
  notes: string | null;
  createdAt: string;
}

export interface UnitOverridesListResponse {
  unitActions: UnitOverride[];
  removedCount: number;
}

/**
 * The slot regeneration a rotate (create or undo) kicked off for the unit across every formation
 * package it belongs to — `POST/DELETE /inventory_workspaces/:id/unit_actions`. Present only when at
 * least one zone will regenerate. The runs are created asynchronously after the response, so this is
 * what the client can act on immediately: the copy to show, the total to count zone completions
 * against, and the ONE Pusher channel every zone run reports its terminal status on.
 */
export interface SlotRegeneration {
  status: 'started';
  unitId: number;
  unitName: string | null;
  message: string;
  /** DISTINCT formations being re-laid — the grain all operator-facing copy counts in. */
  totalFormations: number;
  formations: string[];
  /**
   * Zone runs each formation fans out (one per package/spacing) — a formation is done only when
   * all of its runs report. An array, not a formation-keyed map: the global key camelization
   * would mangle formation-name keys.
   */
  formationZoneCounts: { formation: string; zones: number }[];
  /** Zone runs in total (the sum of formationZoneCounts) — the final refresh waits for all. */
  totalZones: number;
  notifications: {
    channel: string;
    events: { name: string }[];
  };
}

/**
 * `POST …/unit_actions` — the created action, plus, for a rotate, the bearing the unit is now headed
 * to (its own stored bearing) and the regeneration it started. Every rotate response — create, undo
 * and restore — carries `resultingAzimuthDeg`, so one client path draws the outcome of all three
 * without inferring it from tiles of unknown age.
 */
export interface UnitActionCreatedResponse extends UnitOverride {
  resultingAzimuthDeg?: number | null;
  slotRegeneration?: SlotRegeneration;
}

/**
 * `POST …/unit_actions` for a rotate whose bearing lies on the unit's detected axis: the operator
 * turned an inherited rotation back to the unit's natural direction. A rotate action means "deviates
 * from detected", so no row was written — the unit is re-laid at the detected bearing instead, and
 * that regeneration is what comes back.
 */
export interface DetectedAzimuthRestoreResponse {
  restoredDetectedAzimuth: { unitId: number; azimuthDeg: number | null };
  resultingAzimuthDeg?: number | null;
  slotRegeneration?: SlotRegeneration;
}

export type UnitActionCreateResponse =
  UnitActionCreatedResponse | DetectedAzimuthRestoreResponse;

/**
 * `DELETE …/unit_actions/:id` body. An undone ROTATION always answers 200 with the detected bearing
 * the unit is headed back to, plus the regeneration when one started; every other undo is a 204.
 */
export interface UnitActionUndoResponse {
  resultingAzimuthDeg?: number | null;
  slotRegeneration?: SlotRegeneration;
}

// Per spec, each picklist facet wraps its options in a `buckets` array.
// `id` may be string|number|boolean to support boolean facets like `overlapping`.
export interface DevelopmentAreaFacetBucket {
  id: string | number | boolean;
  name: string;
  count: number;
}

export interface DevelopmentAreaPicklistFacet {
  buckets: DevelopmentAreaFacetBucket[];
}

// Range facets (acreage / length / width) skip the `buckets` envelope and
// expose min/max directly. Backend may omit either bound, so both are optional.
export interface DevelopmentAreaRangeFacet {
  min?: number | null;
  max?: number | null;
}

export interface DevelopmentAreaFacets {
  unitType?: DevelopmentAreaPicklistFacet;
  unitDevelopmentType?: DevelopmentAreaPicklistFacet;
  state?: DevelopmentAreaPicklistFacet;
  unitSource?: DevelopmentAreaPicklistFacet;
  estimateSource?: DevelopmentAreaPicklistFacet;
  unitId?: DevelopmentAreaPicklistFacet;
  unitName?: DevelopmentAreaPicklistFacet;
  basinTier?: DevelopmentAreaPicklistFacet;
  overlapping?: DevelopmentAreaPicklistFacet;
  removed?: DevelopmentAreaPicklistFacet;
  filedWellOnly?: DevelopmentAreaPicklistFacet;
  acreage?: DevelopmentAreaRangeFacet;
  length?: DevelopmentAreaRangeFacet;
  width?: DevelopmentAreaRangeFacet;
  verificationStatus?: DevelopmentAreaPicklistFacet;
}

export interface DevelopmentAreasQueryResponse {
  results: DevelopmentArea[];
  totalCount: number;
  page: number;
  pageSize: number;
  facets: DevelopmentAreaFacets;
  // The overlap graph; one entry per unit that has at least one overlap.
  overlappingResults?: DevelopmentAreaOverlappingResult[];
  // Optional today (backend hasn't shipped it yet) — convenience list of every
  // matching unit so consumers don't have to page-walk to enumerate them.
  filteredUnits?: FilteredUnit[];
}

export interface FormationSlot extends Record<string, unknown> {
  developmentAreaName: string;
  formation: string | null;
  slotId: string | null;
  slotNumber?: number | null;
  compositeId?: number | null;
  compositeName?: string | null;
  compositePosition?: number | null;
  compositeEntryEnd?: 'start' | 'end' | null;
  valued: boolean | null;
  notes: string | null;
  uwi: string;
  reserveCategory: string | null;
  wellStatus: string | null;
  /** "LEASE #NUMBER", the well page's name; null on an LTD row or a well with neither. */
  wellName: string | null;
  /**
   * The well was moved in depth and its landing analysis is still running, so
   * `formation` is the zone it left (Story 1). Only ever true under an
   * Inventory Workspace.
   */
  landingPending?: boolean;
  /**
   * The producing length of THIS slot — an LTD's planned stick, or a well-backed
   * slot's measured segment through its own drainage sector. Not the whole well.
   */
  wellProducingLengthFt: number | null;
  /**
   * The share of its well's producing lateral that this slot carries: the length
   * above over the whole well's lateral. 1.0 for a plain LTD, a leg's share of its
   * composite, a sector's share of a crossing well. Null where no lateral could be
   * measured (a vertical well, a permit on a collapsed stick).
   */
  segmentProportionToWellProducingLength?: number | null;
  tvdFt: number | null;
  permitDate: string | null;
  spudDate: string | null;
  firstProductionDate: string | null;
  operator: string | null;
  cumOilBbl: number | null;
  cumGasMcf: number | null;
  cumWaterBbl: number | null;
  // EUR fields come back as decimal strings (e.g. "898876.6593"). Parsed at the
  // cell-renderer level for display.
  oilEurBbl: string | null;
  gasEurMcf: string | null;
  waterEurBbl: string | null;
  xOffsetFt: number | null;
  tvdssFt: number | null;
  resources?: {
    developmentArea?: { href: string; auth: string[] };
    well?: { href: string; auth: string[] };
  };
}

export interface FormationSlotsQueryRequest {
  page?: number;
  page_size?: number;
  type_curve_header_details_id?: number;
  inventory_workspace_id?: number;
  inventory_formation_package_ids?: number[];
  formation_name?: string;
  development_area_id: number;
  cross_section_end_points?: {
    start: { lat: number; lon: number };
    end: { lat: number; lon: number };
  };
  collection_ids?: number[];
  slice_step_ft?: number;
  uwis?: string[];
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  text?: string;
}

export interface FormationSlotsQueryResponse {
  results: FormationSlot[];
  totalCount: number;
}

export interface CreateAccountResponse {
  id: number;
  username: string;
  encrypted_password: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CountResponse {
  count: number;
  count24h: number;
  count7d: number;
  timestamp: string;
}

/**
 * Interface for title assets retrieved from the allocation manager endpoint
 */
export interface TitleAssetResponse {
  id: string;
  entity: string;
  bonus: string;
  delay: string;
  royalty: string;
  executive: string;
  workingInterests: string;
  assetGeojson: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  workspace: string;
  subtractName: string;
  lastCalculated: string;
  royaltyRate: number | null;
  uat: string;
  unitNri: number | null;
  surface: number | null;
  startDepth: number | null;
  endDepth: number | null;
  allocationMethod: string;
  lateralUnitNri: number | null;
  lateralUnitNriFormula: {
    participationTractAc: string;
    totalLlFt: number | null;
    tractNri: number;
    uatFt: number;
    uatGisAc: number;
  } | null;
  acreageUnitNri: number | null;
  acreageUnitNriFormula: {
    participationTractAc: string;
    tractNri: number;
    uatAc: number;
    uatGisAc: number;
    unitTractGrossAcresAc: number;
  };
  [key: string]: unknown;
}

/**
 * Interface for CRM assets retrieved from the allocation manager endpoint
 */
export interface CRMAssetResponse {
  id: string;
  crmAccount: string;
  crmTractId: string;
  crmTractName: string;
  crmSubtractId: string;
  /** Salesforce ID of the subtract. Optional because it may not exist for newly created subtracts. */
  crmSubtractSalesforceId?: string;
  crmSubtractName: string;
  uat: string;
  allocationMethod: string;
  acreageUnitNri: number | null;
  acreageUnitNriFormula: {
    participationTractAc: string;
    tractNri: number;
    uatAc: number;
    uatGisAc: number;
    unitTractGrossAcresAc: number;
  } | null;
  lateralUnitNri: number | null;
  lateralUnitNriFormula: {
    participationTractAc: string;
    totalLlFt: number | null;
    tractNri: number;
    uatFt: number;
    uatGisAc: number;
  } | null;
  assetGeojson: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  legalDescription: string;
  subtractGrossAcres: number | null;
  subtractNetAcres: number | null;
  netAcreageUnits: string | null;
  instrumentType: string | null;
  tractGeojson: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
  tractNriFormula: {
    netRoyaltyAcres: number;
    royaltyRate: number;
    totalTractAcres: number;
  } | null;
  [key: string]: unknown;
}

export type DevelopmentAreasCountResponse = CountResponse;
export type DocumentsCountResponse = CountResponse;

export interface EnhancedFreeCashFlowResponse {
  cashflows: number[];
  years?: string[];
  months?: string[];
  quarters?: string[];
  slotCategories: ('ltd' | 'permit' | 'duc' | 'pdp')[];
}

export interface StructureGridHeadersResponse {
  structureGrids: {
    name: string;
    basins: {
      name: string;
      id: string;
      structureGrids: {
        name: string;
        id: number;
      }[];
    }[];
  };
}

export interface GeologicIntervalTypeCurveHeadersResponse {
  geologicIntervalTypeCurves: {
    id: number;
    basinName: string;
    geologicInterval: string;
    typeCurveFormation: string;
  }[];
}
export interface GeologicInterval {
  id: number;
  basinName: string;
  geologicInterval: string;
  positionalIndex: number;
  age: number;
  createdAt: string;
}

export interface GeologicIntervalResponse {
  geologicIntervals: GeologicInterval[];
  meta: {
    count: number;
  };
}

export interface AreasQueryResponse {
  id: number;
  name: string;
  displayName: string;
  purpose: string;
  category: string;
  grossAcres: number;
  shared: boolean;
  active: boolean;
  createdBy: string;
  dataSource: string;
  createdAt: string;
}

export interface FocusAreasResponse {
  focusAreas: {
    boundaries: {
      name: string;
      areas: {
        name: string;
        id: string;
        /** The area's own colour; null falls back to the category colour. */
        displayColor?: string | null;
      }[];
    }[];
  };
}

export interface StructureGridCollectionCreateResponse {
  id: number;
  basin: string;
  collection_name: string;
  owner: string;
  created_at: string;
  updated_at: string;
}

export interface StructureGridCollectionItem {
  id: number;
  structureGridId: number;
  gridSurface: string;
  gridProcessed: boolean;
  owner: string;
  createdAt: string;
  geologicInterval: string;
  source: string;
  validity: string;
  avgThickness: number | null;
  minDepth: number | null;
  maxDepth: number | null;
}

export interface StructureGridCollectionUpdateResponse {
  id: number;
  href: string;
  auth: string[];
  isDefault: boolean;
  defaultStartTime: string | null;
  defaultEndTime: string | null;
  landingAutoUpdateEnabled?: boolean;
}

export interface StructureGridCollectionResponse {
  id: number;
  href: string;
  auth: string[];
  basin: string;
  collectionName: string;
  owner: string;
  status: string;
  isDefault: boolean;
  defaultStartTime: string | null;
  defaultEndTime: string | null;
  landingAutoUpdateEnabled: boolean;
  createdAt: string;
  sourceCollectionCreatedAt: string;
  validity: string;
  structureGridCollectionItems: StructureGridCollectionItem[];
  events: {
    id: number;
    data: { kind: string; content: string };
    createdBy: string;
    createdAt: string;
  }[];
  hasActiveJob: boolean;
  activeJobStatus?: {
    href: string;
    auth: string[];
  };
  notifications: {
    channel: string;
    events: {
      name: string;
    }[];
  };
  resources: {
    wellLandingAnalysisPreviews?: {
      href: string;
      auth: string[];
    };
    wellLandingAnalysisExport?: {
      href: string;
      auth: string[];
    };
    comments: {
      href: string;
      auth: string[];
    };
    events: {
      href: string;
      auth: string[];
    };
    queries?: {
      wellLandingAnalysisCounts?: { href: string; auth: string[] };
      wellLandingAnalysisRequests?: { href: string; auth: string[] };
      wellLandingAnalyses?: { href: string; auth: string[] };
    };
  };
}

interface WellPoint {
  x: number;
  y: number;
  z: number;
}

export interface Profile2D {
  units: string;
  trajectory: number[][];
  tvdValues: number[];
  // A null y marks a gap between disjoint perforation intervals (see
  // profile2d.rb#build_estimated_perforated_lateral); Highcharts leaves it unconnected.
  perforatedLateral?: (number | null)[][];
  heelVs?: number;
  toeVs?: number;
  lateralEndpoints?: {
    heel: { vs: number; tvdss: number };
    toe: { vs: number; tvdss: number };
  };
}

export interface GunBarrelGeometryItem {
  geometry: {
    type: 'Point' | 'LineString';
    coordinates: number[] | number[][];
  };
  landingPoint: {
    type: 'Point';
    coordinates: number[];
  };
  label: string;
}

export interface GunBarrelView {
  uwi14: string;
  tvdStats?: {
    averageTvd: number;
    minTvd: number;
    maxTvd: number;
  };
  depthBasisGeometry?: GunBarrelGeometryItem;
  perforatedLateralStick?: {
    type: 'LineString';
    coordinates: number[][];
  };
  originalWellboreGeometry?: GunBarrelGeometryItem;
}

interface Well3DViewerWellBore {
  uwi14: string;
  uwi12: string;
  /** Bore CLASSIFICATION (producing/sidetrack/pilot_hole/pre_drill) — carried
   *  even for an estimated bore (falls back to 'producing'). See `estimated`
   *  for the synthesized-vertical provenance signal. */
  type: string;
  spudDate: string | null;
  geometryLabel?: string;
  /** Geometry provenance — `pre_drill_auto` marks an estimated pre-drill bore. */
  geometrySource?: string;
  /** True for an estimated (synthesized vertical) bore — the provenance signal
   *  the FE uses to style/detect it; `type` no longer carries this. */
  estimated?: boolean;
  points: WellPoint[];
  profile2D?: Profile2D;
  gunBarrelView?: GunBarrelView;
  /** Pre-drill bores: the workspace driving this geometry (null on drilled). */
  workspace?: PreDrillWorkspaceRef | null;
}

interface Well3DViewerWell {
  href: string;
  auth: string[];
  id: number;
  firstProductionDate: string | null;
  lastProductionDate: string | null;
  name: string;
  uwi: string;
  leaseName: string;
  wellNumber: string;
  currentOperator: string;
  holeDirection: string | null;
  status: string;
  permitDate: string | null;
  spudDate: string | null;
  srpLandingZone: string | null;
  vendorLandingZone: string | null;
  trueVerticalDepthFeet: number | null;
  completionDate: string | null;
  /** Dropped from the /wells/:id and /queries/wells payloads — the backend column (wells.
   *  srp_landing_zone) this was sourced from is retired; see srpLandingZone above for the
   *  current resolved value. */
  srpLandingZoneAttribute?: string | null;
  resources: {
    queries: {
      wellbores: Resource;
    };
    productionRecord: Resource;
    productionForecast: Resource;
    formation: Resource;
    comments: Resource;
    supportingFiles: Resource;
    events: Resource;
    versions: Resource;
    features: Resource;
    units: Resource;
    bestFitUnit: Resource;
    intersectingUnits: Resource & {
      method: string;
      contentType: string;
      description: string;
    };
    currentVersion: Resource;
  };
  notifications: Notifications;
  source?: string;
  geometrySource?: string;
  color?: string | null;
  wellBores?: Well3DViewerWellBore[];
}

interface BoundingBox {
  maxX: number;
  maxY: number;
  maxZ: number;
  minX: number;
  minY: number;
  minZ: number;
}

interface GridMetadata {
  minElevation: number;
  maxElevation: number;
  minLongitude: number;
  maxLongitude: number;
  minLatitude: number;
  maxLatitude: number;
  validity: string;
}

export interface Grid {
  gridId: number;
  gridSurface: string;
  source: string;
  geologicInterval: string;
  positionalIndex: number;
  intersects: boolean;
  href: string;
  displayColor: string;
  metadata: GridMetadata;
}

export interface StructureGridCollection {
  collectionId: number;
  collectionName: string;
  isDefault: boolean;
  owner: string;
  basin: string;
  validity: string;
  href: string;
  grids: Grid[];
}

export interface Well3DViewerResponse {
  page: number;
  totalCount: number;
  pageSize: number;
  minDepth: number;
  maxDepth: number;
  depthRange: number;
  results: Well3DViewerWell[];
  boundingBox: BoundingBox;
  paddedBoundingBox: BoundingBox;
  paddedBoundingBoxGeometry?: GeoJSON.Polygon;
  wellSearchEnvelopeGeometry?: GeoJSON.Polygon;
  tickRangeDivisions: {
    xTickRange: number;
    yTickRange: number;
    zTickRange: number;
  };
  facets?: {
    holeDirection?: {
      buckets: {
        id: string;
        name: string;
        count: number;
      }[];
    };
    status?: {
      buckets: {
        id: string;
        name: string;
        count: number;
      }[];
    };
  };
}

/**
 * Rows of the 2D formation arrays returned by /queries/structure_grid_profiles.
 * Each entry is one sample point along the subject well's horizontal section.
 */
export interface StructureGridProfileEvalPoint {
  index: number;
  mdFt: number;
  tvdssFt: number;
  azimuthDeg: number;
  /** Vertical Section (ft) from the surface hole — one X per eval point, on the
   *  same 0-origin axis as `wellboreTrajectory.vsValues`. Present for survey
   *  (eval-point) profiles; the formation series is plotted against these so
   *  each formation top lands at its true VS instead of the wellbore's
   *  differently-sampled VS array. Absent for the unit cross-section
   *  (gun-barrel) path, which has no wellbore. */
  vsFt?: number | null;
}

/**
 * 2D TVD arrays returned by /queries/structure_grid_profiles. Outer index is
 * the eval-point row ([evalPoints[i]]); inner index is the slice column
 * ([slicePositions[j]]). Null gaps mean the grid has no data at that sample.
 */
export type FormationTvdGrid = (number | null)[][];

export interface StructureGridProfileFormation {
  gridId: number;
  name: string;
  geologicInterval: string;
  labelColor: string;
  displayColor: string;
  positionalIndex: number;
  topTvdss: FormationTvdGrid;
  /**
   * Null when the builder could not resolve a surface elevation for the well
   * (`StructureGridProfileBuilder#fetch_surface_elevation_from_geometry`
   * returns nil for a blank or Z-less survey geometry). TVDSS is always
   * present; TVD is derived as `surface_ft - tvdss`, so with no elevation there
   * is no TVD to send. Consumers must render TVDSS-only rather than substitute
   * a depth.
   */
  topTvd: FormationTvdGrid | null;
  baseTvdss: FormationTvdGrid;
  /** Null under the same condition as {@link topTvd}. */
  baseTvd: FormationTvdGrid | null;
  defaultThickness: number;
  typeCurveLabel?: string;
}

export interface TypeCurveFormation {
  name: string;
  displayColor: string;
  geologicIntervals: string[];
  topTvdss: FormationTvdGrid;
  /** Null when no surface elevation resolved — see StructureGridProfileFormation.topTvd. */
  topTvd: FormationTvdGrid | null;
  baseTvdss: FormationTvdGrid;
  /** Null under the same condition as {@link topTvd}. */
  baseTvd: FormationTvdGrid | null;
  defaultThickness: number;
}

/** Full VS trajectory (surface → toe) per point. Populated only when the
 *  query is made with `requestType: 'landing_analysis'`; absent for
 *  gun-barrel and other contexts. All three arrays are the same length. */
export interface StructureGridProfileWellboreTrajectory {
  vsValues: number[];
  tvdssValues: number[];
  tvdValues: number[];
}

export interface StructureGridProfileResult {
  uwi: string;
  collectionId: number;
  collectionName: string;
  evalPoints: StructureGridProfileEvalPoint[];
  slicePositions: number[];
  formations: StructureGridProfileFormation[];
  typeCurveFormations: TypeCurveFormation[];
  /** Only present when the request used `requestType: 'landing_analysis'`. */
  wellboreTrajectory?: StructureGridProfileWellboreTrajectory;
  resources: {
    well: Resource;
    collection: Resource;
  };
}

export interface StructureGridProfilesResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: StructureGridProfileResult[];
}

export interface StructureGridResponse {
  id: number;
  gridSurface: string;
  gridProcessed: boolean;
  notes: string | null;
  owner: string;
  source: string;
  sourceProjection: string;
  projection: string;
  sourceCreatedAt: string;
  createdAt: string;
  geologicIntervalId: number | null;
  basinName: string | null;
  geologicInterval: string | null;
  minElevation: string | null;
  maxElevation: string | null;
  minLongitude: string | null;
  maxLongitude: string | null;
  minLatitude: string | null;
  maxLatitude: string | null;
  href: string;
  blobName: string | null;
  hasCollection: boolean;
  validity: 'warning' | 'incomplete' | 'valid' | 'invalid';
  status: string;
  cellWidthFeet: number | null;
  cellHeightFeet: number | null;
  fileSizeMb: number | null;
  resources: {
    comments: {
      href: string;
      auth: string[];
    };
    events: {
      href: string;
      auth: string[];
    };
    notifications: {
      channel: string;
      events: {
        name: string;
      }[];
    };
  };
}

export interface BestFitUnitResponse {
  allocationManagerId: number | null;
  allocationMethod: string | null;
  assignmentType: string;
  developmentArea: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
    uuid: string;
    unitType: string;
    unitName: string | null;
    /** 'd_and_s' | 'joa', or null for a unit GIS has not typed. */
    unitAgreementType: string | null;
    geojson: {
      id: number;
      type: string;
      geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
      properties: {
        SF_UnitID: string;
        SF_ProjectAOI_Id: string | null;
        name: string;
        uuid: string;
        acres: number;
        uwi10: string | null;
        source: string;
        objectId: number;
        unitType: string;
        createdBy: string;
        modifiedBy: string;
        createdDate: number;
        sfUnitName: string;
        modifiedDate: number;
        filedWellOnly: string;
        shapeSTArea: number;
        shapeSTLength: number;
        includeInPricing: string | null;
      };
    };
    geometry: string;
    bespoke: boolean;
    tsvector: string;
    length: number;
    width: number;
    acreage: number;
    filedWellOnly: boolean;
    webMercatorGeometry: string;
    latestScheduledEvaluationId: number;
    sysPeriod: {
      validFrom: string;
      validTo: string | null;
    };
    publisherUpdatedAt: string | null;
    stateAbbreviation: string;
  };
}
// Shared development area type used by both BestFitUnitResponse and WellIntersectionAnalysisResponse
export type DevelopmentAreaDetails = BestFitUnitResponse['developmentArea'];

// Intersecting unit with WIA-specific fields
export interface IntersectingUnit extends DevelopmentAreaDetails {
  assignedToUnit: boolean;
  assignmentType: 'explicit' | 'best_fit' | 'none';
  allocationMethod: string | null;
  allocationManagerId: number | null;
  coveragePercentage: number;
  platBased: boolean;
  /**
   * The recommended pick for a well authored in a Well Workspace, served by
   * the workspace's own unit-candidates endpoints. The ONLY field those add over this shape —
   * the canonical `/queries/well_intersection_analysis` does not compute a recommendation, so
   * it is absent there and the assignment control is the only reader.
   *
   * Distinct from `assignmentType: 'best_fit'`, which says how the well is assigned TODAY.
   * A unit can be the recommendation without holding the assignment, and vice versa.
   */
  bestFit?: boolean;
}

export interface WellIntersectionAnalysisResponse {
  wellUwi: string;
  wellHoleDirection: string | null;
  versionTimestamp: string | null;
  coverageThreshold: number | null;
  intersectingUnitsCount: number;
  assignedUnitCount: number;
  intersectingUnits: IntersectingUnit[];
}

export interface StructureGridListResponse {
  structureGridCollections: StructureGridCollection[];
  grids: Grid[];
}

export interface CRMAccountResponse {
  salesforceId: string;
  salesforceHref: string;
  name: string;
  type: string | null;
  phone: string | null;
  billingAddress: string | null;
  entities: {
    id: number;
    name: string | null;
    entityLinkId: string | null;
  }[];
}

export interface CRMAccountSearchResponse {
  salesforceId: string;
  uuid: string;
  salesforceHref: string;
  name: string;
  type: string | null;
  billingAddress: string | null;
  phone: string | null;
  entities: {
    id: number;
    name: string | null;
  }[];
}

export type TitleQuality = 'none' | 'high' | 'medium' | 'low';
export type TitleRiskFactor = 'none' | 'low' | 'medium' | 'high';

export interface RiskBanner {
  hasRiskedAssets: boolean;
  riskedAssetCount: number;
  titleQualityBreakdown: {
    high: number;
    medium: number;
    low: number;
  };
}

export interface CRMCommonResponse {
  salesforceId: string;
  name: string;
  grossAcres: number | null;
  updatedAt: string;
  quarterCall: string | null;
  netAcres: number | null;
  surveyArea: string | null;
  netAcreageUnits: string | null;
  instrumentType: string | null;
  entities: {
    id: number;
    name: string | null;
  }[];
  startDepth: number | null;
  endDepth: number | null;
  createdAt: string;
  titleQuality?: TitleQuality | null;
  titleRiskFactor?: TitleRiskFactor | null;
}

export interface AccountResponse {
  salesforceId: string;
  salesforceHref: string;
  uuid: string;
  name: string;
}

/**
 * The tract-list indicator for a vesting document: the one recorded deed that created this tract,
 * verified-first then newest. Emitted whole by VestingDocument.indicators_by_tract, which is why
 * these keys are already camelCase on the Ruby side.
 *
 * Tract-grain only — a subtract row never carries one.
 */
export interface VestingDocumentIndicator {
  verified: boolean;
  provenance: 'auto' | 'manual';
  documentHref: string;
}

/** Counts behind the CRM Tract Library summary cards. Flat, so a tile's key can be `keyof` this. */
export interface CrmTractAggregations {
  vestingNone: number;
  vestingNeedsReview: number;
  vestingVerified: number;
  /** Tracts holding at least one linked workbook. */
  externalTitleSupport: number;
}

export interface CRMTractsResponse extends CRMCommonResponse {
  salesforceHref: string;
  subtracts: Omit<CRMSubtractResponse, 'instrumentType'>[];
  account: AccountResponse;
  nri: number | null;
  workingInterest: number | null;
  /** Null for a tract with no vesting document, which is most of them. */
  vestingDocument?: VestingDocumentIndicator | null;
  /** Workbooks linked to this tract. Tract-grain: absent until the tract is reindexed, 0 when
   *  none, absent on a subtract. */
  externalTitleSupportCount?: number | null;
  // Per-tract pricing fields (Eval 3.0 SIM/Broker pair)
  targetNpvExcluded?: number | null;
  targetNpvIncluded?: number | null;
  /** On the CRM tract list: the package whose newest evaluation supplied targetNpvIncluded. */
  targetNpvHref?: string | null;
  maxNpvExcluded?: number | null;
  maxNpvIncluded?: number | null;
}

export interface CRMSubtractResponse extends CRMCommonResponse {}

export interface CRMTractsAndSubtractsResult {
  results: CRMTractsResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  riskBanner?: RiskBanner;
}

export interface CRMAccountResult {
  results: CRMAccountResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
}

export interface CRMAccountSearchResult {
  results: CRMAccountSearchResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface SalesforceAccountResponse {
  salesforceId: string;
  salesforceHref: string;
  name: string;
  type: string;
  billingAddress: {
    city: string | null;
    country: string | null;
    postalCode: string | null;
    state: string | null;
    street: string | null;
  };
}

export interface StructureGridCollectionValidityResponse {
  totalCount: number;
  results: {
    type: string;
    status: string | null;
    isLatest: boolean;
    isValid: boolean;
    level: string;
    failureReason: string | null;
    displayMetadata: {
      label: string;
      status: string | null;
      validationType: string;
      failureMessages: string[];
      failedGridIds: number[];
      helpHint: {
        label: string;
        level: string;
        description: string;
        failureIndication: string;
      };
    };
  }[];
}

export interface PipelineQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: Pipeline[];
}

export interface Pipeline {
  href: string;
  auth: string[];
  uspipId: number;
  pipelineOperator: string | null;
  systemName: string | null;
  subSystemName: string | null;
  commodityDescription: string | null;
  category: string | null;
  type: string | null;
  capacity: string | null;
  status: string | null;
  diameterIn: number | null;
  lengthMiles: number | null;
}

export interface DrillingRigQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: DrillingRig[];
}

export interface DrillingRig {
  href: string;
  auth: string[];
  rigId: number;
  rigName: string;
  uwi: string;
  wellName: string;
  operator: string;
  rigStatus: string;
  rigStatusDetail: string;
  currentDepth: string;
  targetFluid: string;
  targetFormation: string;
  contractor: string;
  spudDate: string;
  resources: {
    well: Resource;
  };
}

export interface DcaScenariosResponse {
  createdAt: string;
  updatedAt: string;
  href: string;
  auth: string[];
  id: number;
  name: string;
  timeFormat: string;
  qiFormat: string;
  diFormat: string;
  durationOption: string;
  durationFormat: string;
  durationPeriod: number;
  isProtected: boolean;
  createdBy: string;
  updatedBy?: string;
  notes: string;
  areaCount: number;
  formationCount: number;
  developmentType: string;
  hasSegmentation: boolean;
  hasOutlierDetection: boolean;
  hasEarlyTime: boolean;
  oilConfigCount: number;
  gasConfigCount: number;
  waterConfigCount: number;
}

export interface DcaScenariosFacetBucket {
  value: string;
  count: number;
}

export interface DcaScenariosQueryFacets {
  createdBy?: { buckets: DcaScenariosFacetBucket[] } | null;
  developmentType?: { buckets: DcaScenariosFacetBucket[] } | null;
}

export interface DcaScenariosQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: DcaScenariosResponse[];
  facets?: DcaScenariosQueryFacets;
}

// ──────────────────────────────────────────────────────────────────────────
// Tiptap / ProseMirror-JSON document. Mirrors the backend write-time whitelist
// in `Tiptap::DocValidator` (app/lib/tiptap/doc_validator.rb): a `doc` root
// whose `content` is a tree of the allowed nodes, with inline `marks`. Keep the
// node/mark unions in sync with that validator.
// ──────────────────────────────────────────────────────────────────────────
export type TiptapNodeType =
  | 'doc'
  | 'paragraph'
  | 'heading'
  | 'bulletList'
  | 'orderedList'
  | 'listItem'
  | 'codeBlock'
  | 'horizontalRule'
  | 'image'
  | 'text'
  | 'math_inline'
  | 'math_display';

export type TiptapMarkType = 'bold' | 'italic' | 'underline' | 'code' | 'link';

export interface TiptapMark {
  type: TiptapMarkType;
  attrs?: Record<string, unknown>;
}

export interface TiptapNode {
  type: TiptapNodeType;
  content?: TiptapNode[];
  /** Present on `text` nodes. */
  text?: string;
  marks?: TiptapMark[];
  /** e.g. `image` → `src`, `math_inline`/`math_display` → `latex`. */
  attrs?: Record<string, unknown>;
}

export interface TiptapDoc extends TiptapNode {
  type: 'doc';
  content?: TiptapNode[];
}

// Insights Lexicon term listing — Queries::LexiconTermsController.
// Request keys are snake_case (wrapped in `query`); response keys are camelCase.
export interface LexiconTermRelatedTerm {
  id: number;
  name: string;
  href: string;
}

export interface LexiconTermResult {
  id: number;
  name: string;
  href: string;
  productDomain: string | null;
  tags: string[];
  relatedTerms: LexiconTermRelatedTerm[];
  createdBy: string | null;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

// Each facet is a flat array of distinct string values.
export interface LexiconTermsQueryFacets {
  names: string[];
  productDomains: string[];
  tags: string[];
  relatedTerms: string[];
  createdBy: string[];
  updatedBy: string[];
}

export interface LexiconTermsQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: LexiconTermResult[];
  facets?: LexiconTermsQueryFacets;
}

// Insights Lexicon term detail — Lexicon::TermsController#show.
// GET /lexicon/terms/:id. Response keys are camelCase; `description` is a raw
// Tiptap doc emitted verbatim (uncamelized node keys survive).
export interface LexiconTermNamedRef {
  id: number;
  name: string;
}

export interface LexiconTermDetailRelatedTerm {
  id: number;
  name: string;
  href: string;
}

export interface LexiconTermDetailAttachment {
  id: number;
  // filename/byteSize/contentType are present only when the blob is attached.
  filename?: string;
  byteSize?: number;
  contentType?: string;
  createdBy: string | null;
  createdAt: string;
  href: string | null;
}

export interface LexiconTermResourceLink {
  href: string;
  auth: string[];
}

export interface LexiconTermDetailResponse {
  id: number;
  name: string;
  description: TiptapDoc;
  notes: string | null;
  productDomain: LexiconTermNamedRef;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  relatedTerms: LexiconTermDetailRelatedTerm[];
  tags: LexiconTermNamedRef[];
  attachments: LexiconTermDetailAttachment[];
  archivedAt: string | null;
  href: string;
  auth: string[];
  resources: {
    comments: LexiconTermResourceLink;
    activityFeed: LexiconTermResourceLink & {
      body: { modelType: string; modelId: number };
    };
    supportingFiles: LexiconTermResourceLink;
    archival: LexiconTermResourceLink;
  };
}

// Insights Lexicon term create — Lexicon::TermsController#create
// (POST /lexicon/terms). Atomic create; responds 201 with the full term
// detail, which is shaped identically to LexiconTermDetailResponse.
// Request keys are camelCase (sent verbatim — no transform in lib/request).
// `tagIds` link existing tags; `newTagNames` are find-or-created
// case-insensitively. `relatedTermIds` is one-directional (the DB writes the
// reverse). `supportingFiles` carry Active Storage direct-upload signed ids.
export interface LexiconTermSupportingFileInput {
  signedId: string;
}

export interface LexiconTermCreateRequest {
  term: {
    name: string;
    description: TiptapDoc;
    notes?: string | null;
    productDomainId: number;
    relatedTermIds?: number[];
    tagIds?: number[];
    newTagNames?: string[];
    supportingFiles?: LexiconTermSupportingFileInput[];
  };
}

// Insights Lexicon term update — Lexicon::TermsController#update
// (PATCH /lexicon/terms/:id). PATCH semantics: an OMITTED key leaves that field
// untouched; a key sent as [] CLEARS it. `supportingFiles` is a MIXED delta —
// an add carries a `signedId` (a new Active Storage direct-upload), a removal
// carries the existing attachment `id` with `_destroy: true`.
export interface LexiconTermSupportingFileRemoval {
  id: number;
  _destroy: true;
}

export type LexiconTermSupportingFileUpdate =
  LexiconTermSupportingFileInput | LexiconTermSupportingFileRemoval;

export interface LexiconTermUpdateRequest {
  term: {
    name?: string;
    description?: TiptapDoc;
    notes?: string | null;
    productDomainId?: number;
    relatedTermIds?: number[];
    tagIds?: number[];
    newTagNames?: string[];
    supportingFiles?: LexiconTermSupportingFileUpdate[];
  };
}

// Insights Lexicon term-form option lookups (all { id, name } shaped).
// GET /lexicon/enums → fixed option sets (Lexicon::EnumsController#index);
// today a single productDomains key, ordered by position then name.
export interface LexiconEnumsResponse {
  productDomains: LexiconTermNamedRef[];
}

// GET /lexicon/tags(?q=) → tag options for the Page Tags multi-select
// (Lexicon::TagsController#index). A blank q returns all tags.
export interface LexiconTagsResponse {
  tags: LexiconTermNamedRef[];
}

// GET /lexicon/terms?q=&excludeId= → slim related-term type-ahead matches
// (Lexicon::TermsController#index; ES substring search). Shares the
// { terms: [{ id, name }] } slim view; a blank q yields [].
export interface LexiconTermLookupResponse {
  terms: LexiconTermNamedRef[];
}

export interface DcaScenarioAreaConfigResult {
  id: number;
  areaGeometryId: number | null;
  areaGeometryName: string | null;
  areaGeometryGeoJson: GeoJSON.Geometry | null;
  formationId: number | null;
  formationName: string | null;
  dcaScenarioId: number;
  developmentType: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DcaScenarioAreaConfigsResponse {
  totalCount: number;
  results: DcaScenarioAreaConfigResult[];
}

export interface TypeCurveScenariosResponse {
  createdAt: string;
  updatedAt: string;
  href: string;
  auth: string[];
  id: number;
  name: string;
  status: string;
  dcaScenario: { id: number; name: string; href: string };
  basinAreaCount: number;
  basins: string[];
  developmentType: string;
  createdBy: string;
  updatedBy?: string;
  resources?: { areaConfigs?: { href: string; auth: string[] } };
}

export interface TypeCurveScenariosFacetBucket {
  value: string;
  count: number;
}

export interface TypeCurveScenariosQueryFacets {
  createdBy?: { buckets: TypeCurveScenariosFacetBucket[] } | null;
  dcaScenarioName?: { buckets: TypeCurveScenariosFacetBucket[] } | null;
  developmentType?: { buckets: TypeCurveScenariosFacetBucket[] } | null;
}

export interface TypeCurveScenariosQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: TypeCurveScenariosResponse[];
  facets?: TypeCurveScenariosQueryFacets;
}

export interface TypeCurveScenarioAreaConfigResult {
  id: number;
  href: string;
  auth: string[];
  basinName: string | null;
  developmentType: string | null;
  subAreaCategory: string | null;
  operatorNames: string[] | null;
  algorithmicFormationPriority: boolean;
  areaGeometry: {
    id: number;
    name: string;
    href: string;
    auth: string[];
    geoJson: GeoJSON.Geometry;
  } | null;
  structureGridCollection: {
    id: number;
    collectionName: string;
    auth: string[];
  } | null;
  subAreas: Record<string, unknown>[];
}

export interface TypeCurveScenarioAreaConfigsResponse {
  totalCount: number;
  results: TypeCurveScenarioAreaConfigResult[];
}

export interface CRMPermissionsResponse {
  hasPermissions: boolean;
  email: string | null;
  salesforceUrl: string | null;
}

export interface EntityLinkResponse {
  id: string;
  entityId: string;
  accountId: string;
  resources: {
    notifications: {
      channel: string;
      events: {
        name: string;
        label: string;
        step_type?: string;
        order?: number;
      }[];
    };
  };
}

export interface DeleteEntityLinkResponse extends EntityLinkResponse {}

export interface StructuredPartyLifecycleResponse {
  importedEntityId: number;
  suggestionId?: number;
  eventId: number;
  // Present on a "Mark as Correct" confirm: the reconcile outcome that resolved
  // the party. Drives the post-confirm "marked as correct via alias" banner.
  outcome?: ReconcileOutcome;
}

export interface StructuredPartyRerunResponse {
  importedEntityId: number;
  workflowProcessId: number | null;
  // Null when a run was already in flight on the anchor and none was started.
  eventId: number | null;
}

// ---------------------------------------------------------------------------
// Reconcile (RECONCILE_TECH_SPEC) — multi-select dedupe + alias of entities
// from the Match Review screen. Backed by Reconcile::Orchestrator /
// PreviewService / Undo via Documents::StructuredParties::ReconciliationsController.
// ---------------------------------------------------------------------------

export type ReconcileOutcome = 'dedup_only' | 'alias_only' | 'mixed';

// Preview-only extension: the selection is already fully aliased (one cluster),
// so reconcile would create zero edges and perform no dedupe — a no-op. The
// orchestrator never emits this on commit; it only classifies a live selection.
export type ReconcilePreviewOutcome = ReconcileOutcome | 'already_reconciled';

// A version token per entity (updated_at fingerprint) captured at preview/load
// time and replayed on submit so the backend can detect mid-edit drift.
export type ReconcileFingerprints = Record<number, string>;

// One same-name group that will be deduplicated (collapsed to one survivor).
export interface ReconcileSameNameGroup {
  name: string;
  entityIds: number[];
}

// An alias assertion created between two surviving entities (hub, spoke).
export type ReconcileAliasPair = [number, number];

// A canonical-typed attribute value dropped during the merge (data-loss banner).
export interface ReconcileLostAttribute {
  entityType: string;
  attribute: string;
  kept: unknown;
  lost: unknown[];
}

// POST .../reconciliation_preview — read-only classification of a selection.
export interface ReconcilePreviewResponse {
  outcome: ReconcilePreviewOutcome;
  sameNameGroups: ReconcileSameNameGroup[];
  aliasMemberIds: number[];
  fingerprints: ReconcileFingerprints;
}

// POST .../reconciliations — the committed reconcile (201).
export interface ReconcileCreateResponse {
  reconciliationId: number;
  outcome: ReconcileOutcome;
  survivingEntityIds: number[];
  retiredEntityIds: number[];
  aliasPairs: ReconcileAliasPair[];
  lostAttributes: ReconcileLostAttribute[];
  // Match still in progress (confidence-stale banner): only Imported survivors.
  confidenceStale: boolean;
}

// POST .../reconciliations/:id/undo
export interface ReconcileUndoResponse {
  reconciliationId: number;
  undoneAt: string;
}

// 409 conflict body when a selected entity changed since its token was captured
// — the refreshed fingerprints let the UI re-evaluate the selection.
export interface ReconcileStaleError {
  error: string;
  currentFingerprints?: ReconcileFingerprints;
}

// ---------------------------------------------------------------------------
// Entity Page Reconciliation — the same Reconcile facility launched from the
// Entity Profile page. Candidate assembly is Elasticsearch-driven (no agent);
// preview/create extend the base reconcile responses.
// ---------------------------------------------------------------------------

// GET /entities/:id/reconcile_signal — the profile-load banner count (the
// scored count, so the banner and the tearsheet rows always agree).
export interface EntityReconcileSignalResponse {
  count: number;
}

// A collapsed alias family presented as one selectable row.
// Selecting the row submits the display member; the alias closure covers the
// rest of the family. The family's live details render via the alias cluster
// fetch, not this payload.
export interface EntityReconcileAliasGroup {
  displayEntityId: number;
  displayName: string;
  memberCount: number;
  // Concrete-type breakdown of the collapsed family (e.g. { IndividualEntity: 2 })
  // — what the alias family contains, for the "Alias Group" column.
  memberTypes: Partial<Record<EntityType, number>>;
  // Flag category if any member of the collapsed group is flagged (else null),
  // so the collapsed row can be styled distinctly (PROD/2907373590, Case 4).
  flagCategory: string | null;
  // That flagged member's reason (shown on hover); null when none.
  flagRationale: string | null;
  // That flagged member's chip severity ('warning' yellow, 'info' blue) or null.
  flagSeverity: FlagSeverity | null;
}

// One factor row of the Match Review confidence rubric, computed
// deterministically for the entity-page context (scored: null = "no data").
export interface EntityReconcileConfidenceFactor {
  factor: string;
  weight: number;
  scored: number | null;
  note?: string | null;
}

// One scored candidate row (standalone entity or a collapsed alias group).
export interface EntityReconcileCandidate {
  entityId: number;
  name: string;
  entityType: EntityType;
  // Tagged "Operator": a code points here or, for a primary, at one of its
  // aliases. A collapsed alias-family row carries its primary's tag.
  operator: boolean;
  confidence: number;
  // Per-factor breakdown (same six factors as Match Review) for the drilldown.
  confidenceFactors: EntityReconcileConfidenceFactor[];
  address: string | null;
  // Selectable primary-address options on this entity (for the survivor's
  // primary-address picker in the preview panel).
  addresses: { id: number; label: string }[];
  // This row's (display) entity's own participation, driving the "Title
  // Workspaces: N | Interpretations: M" links. titleWorkspaces feeds the popup
  // (its length is the displayed count); interpretationCount is a bare count.
  titleWorkspaces: { id: number; name: string }[];
  interpretationCount: number;
  // Which search variation surfaced this candidate (provenance; e.g. the full
  // name, the suffix-stripped name, or "similar to <candidate>").
  searchVariant?: string | null;
  // Consolidation-risk flag category for this entity, or null when unflagged.
  flagCategory: string | null;
  // Free-text reason on the flag (shown on hover); null when none.
  flagRationale: string | null;
  // Chip severity: 'warning' (gated → yellow, in a workspace), 'info' (blue → in
  // an interpretation only), or null (unflagged, or dormant → no chip).
  flagSeverity: FlagSeverity | null;
  // Unresolved address-confidence review flag. Blocks submission
  // (not selection); surfaced on the name in Entity Consolidation + a banner.
  addressFlagged: boolean;
  // 'high'/'medium' criticality of the address-confidence flag, or null.
  addressFlagCriticality: 'high' | 'medium' | null;
  aliasGroup: EntityReconcileAliasGroup | null;
}

// GET /entities/:id/reconcile_candidates — threshold + collapse, display cap 100.
export interface EntityReconcileScoredResponse {
  candidates?: EntityReconcileCandidate[];
  // The viewed entity's own alias-family type breakdown, so its right-panel row
  // shows the same "N Type" Alias Group summary as candidate rows. Null when the
  // viewed entity stands alone (no alias family).
  viewedAliasGroup?: {
    memberCount: number;
    memberTypes: Partial<Record<EntityType, number>>;
  } | null;
  // Recall-bound truncation disclosure (not display-cap): more matched than the
  // retrieval size returned.
  moreFound?: boolean;
  totalBeforeCap?: number;
  // Set (with no candidates) when scoring eliminated everything (Case 4).
  allEliminated?: boolean;
  // The mode that actually produced this set (reflects automatic lexical
  // fallback); diagnostics only — there is no user-facing toggle (R2).
  semantic?: boolean;
  // Whether the semantic capability is enabled in this environment.
  semanticAvailable?: boolean;
}

// Entity-page preview: the base classification plus the two identity/impact counts.
export interface EntityReconcilePreviewResponse extends ReconcilePreviewResponse {
  identityReduction: number;
  affectedWorkspaceCount: number;
  affectedInterpretationCount: number;
}

// The surviving entity to navigate to when the viewed entity was retired (Case 2).
export interface EntityReconcileRetiredInto {
  id: number;
  name: string;
  href: string;
}

// One committed dedupe group: its survivor and the entities retired into it —
// so the summary bullets can attribute each retired entity to ITS OWN survivor.
export interface EntityReconcileDedupeGroup {
  survivor: { id: number; name: string };
  retired: { id: number; name: string }[];
}

// POST /entities/:id/reconciliations — the committed reconcile (201).
export interface EntityReconcileCreateResponse {
  reconciliationId: number;
  outcome: ReconcileOutcome;
  survivingEntityIds: number[];
  retiredEntityIds: number[];
  aliasPairs: ReconcileAliasPair[];
  lostAttributes: ReconcileLostAttribute[];
  viewedEntityRetiredInto: EntityReconcileRetiredInto | null;
  dedupeGroups: EntityReconcileDedupeGroup[];
  // Pre-existing legacy hybrid alias groups included in the reconcile:
  // a type-compatibility violation that predates enforcement, disclosed as a
  // non-blocking notice rather than blocked.
  legacyHybridWarnings: { id: number; name: string }[];
}

export interface StructuredPartyRerunDocumentResponse {
  workflowProcessIds: number[];
  eventIds: number[];
}

export interface StructuredPartyAddResponse {
  entityId: number;
  citationId: number;
  eventId: number;
}

export interface StructuredAddressActionResponse {
  addressId: number;
  eventId?: number | null;
  suggestionId?: number | null;
  clonedAddressIds?: number[];
  partyId?: number;
  fromPartyId?: number;
  toPartyIds?: number[];
}

export interface CreateCrmAccountResponse {
  id: string;
  uuid: string;
  name: string;
  type: string;
  source: string;
  phone: string;
  billingStreet: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  createdAt: string;
  salesforceId: string;
  resources: {
    notifications: {
      channel: string;
      events: {
        name: string;
        label: string;
        step_type?: string;
        order?: number;
      }[];
    };
  };
  queries: {
    processStatus: {
      href: string;
      auth: string[];
    };
  };
}

export interface CountyCoverageResponse {
  results: CountyRow[];
  facets: CountyCoverageFacets;
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface CountyCoverageSummaryResponse {
  totalDocuments: number;
  last24Hours: number;
  last7Days: number;
  last30Days: number;
  last6Months: number;
  topStates: StateStats[];
  topCounties: CountyStats[];
}

export interface StateStats {
  id: number;
  name: string;
  abbreviation: string;
  documentCount: number;
}

export interface CountyStats {
  id: number;
  countyName: string;
  stateId: number;
  stateName: string;
  stateAbbreviation: string;
  documentCount: number;
}

export interface CountyRow {
  id: number;
  countyName: string;
  countyShortName: string;
  stateId: number;
  stateName: string;
  stateAbbreviation: string;
  documentCount: number;
  earliestDocument: DocumentReferenceMin;
  mostRecentDocument: DocumentReferenceMin;
  coverageStartDate: string;
  coverageEndDate: string;
  documentsLast24Hours: number;
  documentsLast7Days: number;
  documentsLast30Days: number;
  documentsLast6Months: number;
  percentageOfTotal: number;
}

export interface DocumentReferenceMin {
  volume: string;
  page: string;
  href: string;
  title?: string;
}

export interface CountyCoverageFacets {
  states: StateFacet[];
  counties: CountyFacet[];
}

export interface StateFacet {
  id: number;
  name: string;
  abbreviation: string;
  documentCount: number;
}

export interface CountyFacet {
  id: number;
  countyName: string;
  countyShortName: string;
  stateId: number;
  stateName: string;
  stateAbbreviation: string;
  documentCount: number;
}

/** Single option within an AFE Catalog facet group. */
export interface AfeCatalogFacetItem {
  id: string;
  value: string;
}

/** Facet groups returned by the AFE Catalog query for populating filter dropdowns. */
export interface AfeCatalogFacets {
  wellTypes: AfeCatalogFacetItem[];
  afeTypes: AfeCatalogFacetItem[];
  basins: AfeCatalogFacetItem[];
  states: AfeCatalogFacetItem[];
  counties: AfeCatalogFacetItem[];
  operators: AfeCatalogFacetItem[];
  reportedFormations: AfeCatalogFacetItem[];
}

export interface TypeCurveVerificationBlock {
  verificationStatus?: string | null;
  lastVerifiedBy?: string | null;
  lastVerifiedDate?: string | null;
}

/** Single option within a CRM Tract facet group. */
export interface CrmTractFacetItem {
  id: string;
  value: string;
}

/** Facet groups returned by the CRM Tracts query for populating filter dropdowns. */
export interface CrmTractFacets {
  instrumentTypes: CrmTractFacetItem[];
  entities: CrmTractFacetItem[];
  accounts: CrmTractFacetItem[];
}

export interface SlotNRIResponse {
  slots: SlotNRI[];
  riskBanner?: RiskBanner;
}

export interface SlotNRIPaginatedResponse {
  slots: SlotNRI[];
  meta: {
    hasMore: boolean;
    totalCount: number;
    limit: number;
    offset: number;
  };
  riskBanner?: RiskBanner;
}

// Filter and sort types for server-side pagination
export type SlotAllocationFilters = Record<
  string,
  string | { min?: number | string; max?: number | string }
>;

export interface SlotAllocationSort {
  column: string;
  direction: 'asc' | 'desc';
}

export interface SlotNRI extends UnvaluedReasonContext {
  id: number;
  slotNumber: number;
  uwi: string | null;
  wellStatus: string;
  typeCurveStatus?: string | null;
  wellName: string;
  formationName: string;
  href: string | null;
  allocationMethod: string;
  allocationManagerId: number | null;
  leaseName: string | null;
  currentOperator: string | null;
  holeDirection: string | null;
  permitDate: string | null;
  spudDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  rigReleaseDate: string | null;
  trueVerticalDepthFeet: number | null;
  nri: number;
  workingInterest: number;
  wiSource?: string | null;
  nriCertifiedSource?: string | null;
  nriCertifiedWorkspaceId?: number | null;
  interestEntityId?: number | null;
  interestType: string | null;
  stateAbbreviation?: string | null;
  assetType?: 'wellbore' | 'ltd_slot' | null;
  unitTract: SlotNRIUnitTract | null;
  // Omitted by the flat /entities/:id/slots endpoint (simplified, no tract hierarchy).
  tracts?: SlotNRITract[];
  // Volume source fields (optional - present in enhanced_slot_allocations response)
  volumeSourceId?: string | null;
  volumeSourceType?: string | null;
  volumeSourceHref?: string | null;
  forecastStatus?: string | null;
  forecastDate?: string | null;
  dcaScenario?: string | null;
  slotLength?: number | null;
  typeCurveLateralLength?: number | null;
  basin?: string | null;
  tier?: string | null;
  typeCurveAreaName?: string | null;
  typeCurveVerification?: TypeCurveVerificationBlock | null;
  // AFE cost fields (present in enhanced_slot_allocations response)
  afeSourceId?: string | null;
  afeSourceHref?: string | null;
  afeWellName?: string | null;
  slotDrillingCosts?: number | null;
  drillingCostsDate?: string | null;
  slotCompletionCosts?: number | null;
  completionCostsDate?: string | null;
  slotFacilitiesCosts?: number | null;
  facilitiesCostsDate?: string | null;
  slotContingenciesCosts?: number | null;
  contingenciesCostsDate?: string | null;
  slotOtherCosts?: number | null;
  otherCostsDate?: string | null;
  // Valuation fields (present in enhanced_slot_allocations response)
  valued?: boolean | null;
  unvaluedReason?: string | null;
  // Per-slot pricing fields (Eval 3.0 SIM/Broker pair)
  targetNpvExcluded?: number | null;
  targetNpvIncluded?: number | null;
  maxNpvExcluded?: number | null;
  maxNpvIncluded?: number | null;
  // Scenario-area selections per assumption type (baked into the
  // slot_allocations snapshot). Present in enhanced_slot_allocations response.
  dpsArea?: string | null;
  taxArea?: string | null;
  differentialArea?: string | null;
  discountRateArea?: string | null;
  expenseArea?: string | null;
  // Paired hrefs to the scenario whose area it is; null on an older capture.
  dpsAreaHref?: string | null;
  taxAreaHref?: string | null;
  differentialAreaHref?: string | null;
  discountRateAreaHref?: string | null;
  expenseAreaHref?: string | null;
}

export interface SlotNRIUnitTract {
  id: string;
  href: string;
  name: string;
  unitSource: string;
  unitDevelopmentType: string;
}

export interface SlotNRICrmAccount {
  salesforceId: string;
  salesforceHref: string | null;
  uuid: string | null;
  name: string;
}

export interface SlotNRIEntity {
  id: number;
  name: string;
}

export interface SlotNRIDepthRange {
  startDepth?: number;
  endDepth?: number | null;
}

export interface SlotNRITractNriFormula {
  grossAcres?: number;
  netAcreageUnits?: string;
  nma?: number;
  nra?: number;
  leaseRoyaltyRate?: number;
  instrumentType?: string;
  orriBurdens?: number;
  // Legacy fields (may still be present in some responses)
  royaltyRate?: string;
  netMineralAcres?: string;
  totalTractAcres?: string;
}

export interface SlotNRIBundleOfSticks {
  executive: number;
  bonus: number;
  delay: number;
  royalty: number;
  royaltyBurdens: number;
  wi: number;
  orri: number;
  orriBurdens: number;
  leaseRoyaltyRate: number;
  leaseRoyaltyType: string;
}

// Response from GET /crm/tracts/:tract_id/bundle_of_sticks_ownership
// or GET /crm/subtracts/:subtract_id/bundle_of_sticks_ownership
export interface BundleOfSticksOwnershipResponse {
  id: number;
  tract?: { href: string };
  subtract?: { href: string };
  executive: number | null;
  bonus: number | null;
  delay: number | null;
  royalty: number | null;
  wi: number | null;
  orri: number | null;
  nri: number | null;
  nma: number | null;
  postBurdenNma: number | null;
  nra: number | null;
  netAcreageUnits: string | null;
  grossAcres: number | null;
  leaseRoyaltyRate: number | null;
  leaseRoyaltyType: string | null;
  royaltyBurdens: number | null;
  orriBurdens: number | null;
  estate: string | null;
  instrumentType: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SlotNRIParameters {
  participationTractFactor: number;
  unitCoverageThreshold: number;
  uatCoverageThreshold: number;
  minimumDevelopmentAreaCoverage: number;
}

export interface SlotNRIVerificationItem {
  commentsCount: number;
  status: boolean | null;
  id: number;
  href: {
    events: string;
    comments: string;
  };
}

export interface SlotNRIVerifications {
  nri: SlotNRIVerificationItem;
  workingInterest: SlotNRIVerificationItem;
}

export interface SlotNRISubtract {
  geojson: GeoJSON.Feature<GeoJSON.Polygon> | null;
  intersectionGeojson: GeoJSON.Feature<GeoJSON.Polygon> | null;
  nriCalculationId: number;
  salesforceId: string;
  href: string;
  name: string;
  netAcres: number;
  grossAcres: number;
  quarterCall: string;
  allocationMethod: string;
  nriFormula: {
    allocationManagerPresent: boolean;
    tractNri: number;
    tractWi: number;
    participationTractGisAc: number;
    uatGisAc: number;
    uatMeasureValue: number;
    unitMeasureValue: number;
    measureUnit: string;
    unitGisAc?: number; // Only present when allocationManagerPresent is false
  } | null;
  tractNri: string;
  tractNriFormula: SlotNRITractNriFormula;
  bundleOfSticks: SlotNRIBundleOfSticks;
  nri: number;
  workingInterest: number;
  interestType: string | null;
  parameters: SlotNRIParameters;
  unitAllocationTract: {
    id: number;
    name: string;
    tractName: string;
    gisAcres: number;
    allocationValue: string;
    allocationFactor: number;
  } | null;
  verifications: SlotNRIVerifications;
  titleQuality?: TitleQuality | null;
  titleRiskFactor?: TitleRiskFactor | null;
}

export interface SlotNRITract {
  salesforceId: string;
  href: string;
  name: string;
  crmAccount: SlotNRICrmAccount | null;
  entities: SlotNRIEntity[];
  netAcres: number;
  grossAcres: number;
  /** CRM raw JSON key (jbuilder endpoints remap to netAcres). */
  tractNetAcres?: number;
  /** CRM raw JSON key (jbuilder endpoints remap to grossAcres). */
  tractGrossAcres?: number;
  netAcreageUnits: string;
  surveyArea: string;
  quarterCall: string;
  instrumentType: string;
  interestType: string | null;
  depthRange?: SlotNRIDepthRange;
  nri: number;
  workingInterest: number;
  /**
   * This tract's share of a certified slot's interest, distributed across the
   * slot's tracts by the certified-interest overlay. Present only on
   * a certified slot; `0` is a real value (a held or uncertified tract), which is
   * why reads must test for presence rather than falsiness. Absent on a
   * non-certified slot — and on a cached blob built before the overlay shipped.
   */
  certifiedNri?: number;
  certifiedWi?: number;
  subtracts: SlotNRISubtract[];
  geojson: GeoJSON.Feature<GeoJSON.Polygon> | null;
  // Per-tract pricing fields (Eval 3.0 SIM/Broker pair)
  targetNpvExcluded?: number | null;
  targetNpvIncluded?: number | null;
  maxNpvExcluded?: number | null;
  maxNpvIncluded?: number | null;
}

export interface SubtractIntersectionAnalysisParameterResponse {
  id: number;
  subtractId: string;
  unitId: number | null;
  participationTractFactor: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AllocationMethodUpdateResponse {
  id: number;
  allocationMethod: 'acreage' | 'lateral_length';
  oldAllocationMethod: string;
  newAllocationMethod: string;
  status: string;
  resources: {
    notifications: {
      channel: string;
      shouldSubscribe: boolean;
      events: {
        name: string;
        label: string;
        stepType?: string;
        order?: number;
      }[];
    };
  };
  queries: {
    processStatus: {
      href: string;
    };
  };
}

// Response for unchanged allocation method (no workflow created)
export interface AllocationMethodUnchangedResponse {
  message: string;
  unchanged: true;
}

import type {
  SlotVerificationCounts,
  NriVerificationCounts,
} from 'components/slot-summary-table/types';
import type {
  DcaScenarioResponse,
  GeneralConfig,
  OutlierConfig,
  SegmentationConfig,
  ValidateAreaConfigResponse,
} from './dca-scenarios-api/responses';

export type SlotSummaryRow = Omit<SlotSummary, 'wellType' | 'producing'> & {
  unitType: string;
  pdp: number;
  /** Backend-provided slot verification counts (camelCase from API). Excludes expired permit by default. */
  slotVerification?: SlotVerificationCounts;
  /** Backend-provided NRI verification counts (camelCase from API). Excludes expired permit by default. */
  nriVerification?: NriVerificationCounts;
  /** LTD-excluded slot verification counts (for "Exclude LTDs" frontend toggle) */
  excludedSlotVerification?: SlotVerificationCounts;
  /** LTD-excluded NRI verification counts (for "Exclude LTDs" frontend toggle) */
  excludedNriVerification?: NriVerificationCounts;
};

export interface SlotsByFormationRow extends Record<string, unknown> {
  formation: string;
  pdp: number;
  duc: number;
  permit: number;
  activeEvalLTD: number;
  valuedSlotTotal: number;
  activeUpsideLTD: number;
  otherLTD?: number;
  shutIn: number;
  inactive: number;
  expiredPermit: number;
  /** Each Create New Well status its own column. */
  proposal?: number;
  planned?: number;
  prePermit?: number;
  apd?: number;
  total: number;
}

export interface SlotsByFormationSummaryTotals {
  pdp: number;
  duc: number;
  permit: number;
  activeEvalLTD: number;
  valuedSlotTotal: number;
  activeUpsideLTD: number;
  otherLTD?: number;
  shutIn: number;
  inactive: number;
  expiredPermit: number;
  /** Each Create New Well status its own column. */
  proposal?: number;
  planned?: number;
  prePermit?: number;
  apd?: number;
  total: number;
}

export interface SlotsByFormationBasin {
  basin: string;
  rows: SlotsByFormationRow[];
}

export interface SlotsByFormationResponse {
  formationDataAvailable: boolean;
  basins: SlotsByFormationBasin[];
  totalRow: SlotsByFormationRow;
  summaryTotals: SlotsByFormationSummaryTotals;
}

export interface SlotSummaryResponse {
  slotsSummary: {
    rows: SlotSummaryRow[];
  };
  slotsByFormation: SlotsByFormationResponse;
}

// Enhanced Slot Allocations types (Eval 3.0)

/**
 * Response type for POST /packages/:package_id/enhanced_slot_allocations
 */
export interface EnhancedSlotAllocationsResponse {
  slots: EnhancedSlotAllocation[];
}

/**
 * A slot with volume source fields from the enhanced_slot_allocations endpoint
 */
export interface EnhancedSlotAllocation {
  id: number;
  uwi: string;
  wellStatus: string;
  wellName: string;
  formationName: string;
  slotNumber: number;
  href: string;
  typeCurveStatus: string | null;
  leaseName: string | null;
  currentOperator: string | null;
  trueVerticalDepthFeet: number | null;
  holeDirection: string | null;
  rigReleaseDate: string | null;
  permitDate: string | null;
  spudDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
  allocationMethod: string;
  allocationManagerId: number | null;
  unitTract: {
    id: number;
    name: string;
    href: string;
  } | null;
  nri: number;
  workingInterest: number;
  interestType: string | null;
  // Volume source fields
  volumeSourceId: string | null;
  volumeSourceType: string | null;
  forecastStatus: string | null;
  forecastDate: string | null;
  dcaScenario: string | null;
  slotLength: number | null;
  typeCurveLateralLength: number | null;
  basin: string | null;
  tier: string | null;
  typeCurveAreaName: string | null;
  typeCurveVerification?: TypeCurveVerificationBlock | null;
  tracts: EnhancedSlotTract[];
}

/**
 * Tract data within an enhanced slot allocation
 */
export interface EnhancedSlotTract {
  salesforceId: string;
  href: string;
  name: string;
  crmAccount: SlotNRICrmAccount | null;
  netAcres: number;
  grossAcres: number;
  netAcreageUnits: string;
  surveyArea: string;
  quarterCall: string;
  instrumentType: string;
  interestType: string | null;
  nri: number;
  workingInterest: number;
  geojson: GeoJSON.Feature<GeoJSON.Polygon> | null;
  subtracts: SlotNRISubtract[];
}

/**
 * Response type for the available-forecasts lookups: the per-slot
 * `GET /slots/:slot_id/available_forecasts`, and `POST /queries/available_forecasts`,
 * which answers for EITHER a slot or a Type Curve Area in this same shape.
 *
 * On the area path `slotId`/`uwi`/`smartDefault` are null and `pdpDeclines` is empty —
 * declines are per-well, and "current" is a property of a slot's latest snapshot, so
 * neither exists for an area. The keys are always present so callers need no branching.
 */
export interface AvailableForecastsResponse {
  slotId: string | null;
  uwi: string | null;
  smartDefault: {
    volumeSourceId: string;
    volumeSourceType: 'pdp_decline' | 'type_curve';
    forecastSource?: string;
  } | null;
  typeCurves: TypeCurveForecast[];
  pdpDeclines: PdpDeclineForecast[];
}

/**
 * Type curve forecast option for the edit volume source modal
 */
export interface TypeCurveForecast {
  id: string;
  name: string;
  href?: string;
  createdAt?: string;
  version?: number;
  isCurrent?: boolean;
  forecastStatus?: string;
  source?: string;
  spacing?: number;
  status?: string;
  /** A retired curve: the row's own current one, or any when archived curves were asked for. */
  archived?: boolean;
  /** The promoted workspace's verdict, and who promoted it and when. */
  typeCurveVerification?: TypeCurveVerificationBlock | null;
}

/**
 * PDP decline forecast option for the edit volume source modal
 */
export interface PdpDeclineForecast {
  /** A DCA decline's id is its database id, an integer; a Legacy decline's is a string. */
  id: string | number;
  name: string;
  createdAt?: string;
  version?: number;
  isCurrent?: boolean;
  /** DCA only: createdAt is the promotion time when true, the creation time when false. */
  promoted?: boolean;
  source?: string;
  forecastStatus?: string;
  dcaScenario?: string;
  forecastSource?: string;
}

/**
 * A single AFE option for the edit AFE source modal
 */
export interface AvailableAfe {
  id: string;
  afeType: string;
  createdAt?: string;
  href?: string;
  well: {
    id: string;
    name: string;
    href?: string;
  };
  drillingCost?: number;
  completionCost?: number;
  facilitiesCost?: number;
  contingenciesCost?: number;
  otherCost?: number;
  totalCost?: number;
}

/**
 * Response type for GET /slots/:slot_id/available_afes
 */
export interface AvailableAfesResponse {
  slotId: string;
  wellName: string | null;
  smartDefault: {
    id: string;
    matchType: string;
  } | null;
  availableAfes: AvailableAfe[];
}

export interface AfeSummary {
  id: number;
  wellName: string | null;
  drillingCost: number;
  completionCost: number;
  facilitiesCost: number;
  contingenciesCost: number;
  otherCost: number;
  totalCost: number;
}

export interface AfeSummariesResponse {
  afeSummaries: AfeSummary[];
}

// Wellbore types
export type WellboreGeometryStatus =
  'Valid' | 'Invalid' | 'Error' | 'Processing' | 'Not Started' | 'Warning';

export type WellboreValidationStatus =
  'processed' | 'processing' | 'pending' | 'failed';

export type WellboreValidationResult =
  'valid' | 'invalid' | 'warning' | 'error' | 'unavailable';

export interface WellboreValidation {
  status: WellboreValidationStatus;
  result: WellboreValidationResult;
  failureReason: string | null;
  id: number;
  href: string;
  auth: string[];
}

export interface WellboreSurveyPoint {
  wellPoint: number;
  x: number;
  y: number;
  md: number;
  tvdss: number;
}

export interface WellboreSurveyGeometryPoints {
  surveyGeometry: WellboreSurveyPoint[];
  perforatedLateral: WellboreSurveyPoint[];
}

// Geometry export types (with pointIndex)
export interface WellboreGeometryExportPoint {
  pointIndex: number;
  wellPoint: number;
  x: number;
  y: number;
  md: number;
  tvdss: number;
}

export interface WellboreGeometryExportSurveyPoints {
  surveyGeometry: WellboreGeometryExportPoint[];
  perforatedLateral: WellboreGeometryExportPoint[];
}

export interface WellboreGeometryExportResult {
  id: number;
  uwi: string;
  uwi14: string;
  surveyGeometryPoints: WellboreGeometryExportSurveyPoints;
}

export interface WellboreGeometryExportResponse {
  totalCount: number;
  results: WellboreGeometryExportResult[];
}

// Workspace summary embedded on a predrill wellbore row. Drilled rows carry
// `workspace: null`. Present on the response payload but not yet surfaced
// in the wellbores table UI.
export interface WellboreWorkspaceSummary {
  id: number;
  /** Workspace display name; null for a system-generated workspace. */
  name: string | null;
  isDefault: boolean;
  stale: boolean;
  promoted: boolean;
  /** Promote-time verdict surfaced in the Verification column. */
  workspaceVerdict: 'correct' | 'incorrect' | 'unverified' | null;
  href: string;
  auth: string[];
}

// Nested workspace object on pre-drill result rows (matches
// PreDrillWorkspace#api_payload); null on drilled.
export interface PreDrillWorkspaceRef extends WellboreWorkspaceSummary {
  /** Geometry provenance, e.g. 'operator'. */
  source: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
}

/** The Well Workspace a pre-drill row was authored under, as the wellbores query names it. */
export interface WellboreWellWorkspaceSummary {
  id: number;
  name: string;
  href: string;
  auth: string[];
}

export interface Wellbore {
  href: string;
  auth: string[];
  id: number;
  uwi: string;
  uwi12: string;
  uwi14: string;
  source: string;
  geometrySource: string;
  lastUpdatedAt?: string;
  geometryStatus: WellboreGeometryStatus;
  geometryComment: string | null;
  validation: WellboreValidation;
  completionType: string;
  // Provenance kind (Survey/Pre-Drill/Estimated) — the Geometry Source column,
  // distinct from `geometrySource` above (raw vendor-pipeline value)
  // and from `completionType` (bore classification).
  geometrySourceKind: string;
  holeDirection: string | null;
  perforatedLateralLengthFt: number | null;
  perforationSource: string | null;
  // The Insights perforated lateral cut when the survey has one, else the perforated lateral.
  completionLengthFt: number | null;
  completionSource: string | null;
  measuredDepthFt: number | null;
  trueVerticalDepthFt: number | null;
  kbElevationFt: number | null;
  surveyGeometryPoints?: WellboreSurveyGeometryPoints;
  // Pre-drill scalar fields (BE integration). Null on drilled rows.
  predrillType: string | null;
  targetZoneName: string | null;
  suggestionStatus: string | null;
  confidenceScore: number | null;
  // Workspace driving this pre-drill row's geometry (null on drilled rows);
  // feeds the "Wellbore Geometry Source" + "Verification" columns.
  workspace?: WellboreWorkspaceSummary | null;
  // The Well Workspace the row was authored under. A Well Workspace
  // mints the geometry workspace above without a name, so this is the name the
  // Source column shows. Null on a drilled row and outside a Well Workspace.
  wellWorkspace?: WellboreWellWorkspaceSummary | null;
}

export interface WellboresResponse {
  totalCount: number;
  results: Wellbore[];
}

export interface WellboreMapData {
  uwi14: string;
  uwi12: string;
  /** Bore CLASSIFICATION. An estimated (synthesized vertical) bore folds into
   *  its well's candidate group and reads its real class the same way a
   *  drilled uwi14 would — null only when no classification could
   *  be resolved at all. See `estimated` for the synthesized-vertical
   *  provenance signal (its survey is a near-zero-length line, so the inset
   *  renders it as a point). */
  type: 'producing' | 'sidetrack' | 'pilot_hole' | null;
  /** True for an estimated (synthesized vertical) bore. */
  estimated?: boolean;
  survey?: GeoJSON.Feature | null;
  surfaceHole?: GeoJSON.Point | null;
  bottomHole?: GeoJSON.Point | null;
  stick?: GeoJSON.Feature | null;
  /** Present when the wells query is made with `includeProfile2d` for the
   *  source well — the surface→toe trajectory (VS vs TVDSS) for the 2D Profile. */
  profile2D?: Profile2D | null;
}

export interface WellWithWellboresResult {
  href: string;
  auth: string[];
  id: number;
  firstProductionDate: string | null;
  lastProductionDate: string | null;
  name: string;
  uwi: string;
  leaseName: string;
  wellNumber: string;
  currentOperator: string;
  holeDirection: string | null;
  status: string;
  permitDate: string | null;
  spudDate: string | null;
  srpLandingZone: string | null;
  vendorLandingZone: string | null;
  trueVerticalDepthFeet: number | null;
  /** Measured depth (ft). For a vertical well MD ~= TVD, so it stands in as a
   *  TD proxy when trueVerticalDepthFeet is null (common for verticals). */
  measuredDepthFeet: number | null;
  completionDate: string | null;
  /** Dropped from the /wells/:id and /queries/wells payloads — the backend column (wells.
   *  srp_landing_zone) this was sourced from is retired; see srpLandingZone above for the
   *  current resolved value. */
  srpLandingZoneAttribute?: string | null;
  wellBores: WellboreMapData[];
  /** Well-level surface location; present for permit / pre-drill wells that
   *  have no drilled wellbore trajectory. */
  surfaceHole?: GeoJSON.Point | null;
  resources: {
    queries: {
      wellbores: Resource;
    };
    productionRecord: Resource;
    productionForecast: Resource;
    formation: Resource;
    comments: Resource;
    supportingFiles: Resource;
    events: Resource;
    versions: Resource;
    features: Resource;
    units: Resource;
    bestFitUnit: Resource;
    intersectingUnits: Resource & {
      method: string;
      contentType: string;
      description: string;
    };
    currentVersion: Resource;
  };
  notifications: Notifications;
}

export interface WellMapResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: WellWithWellboresResult[];
  // Facet groups (status, currentOperator, …) from a `facets:[…]` wells query;
  // the Pre-Drill color-by modal reads its attribute value lists from here.
  facets?: Record<string, WellsFacetGroup | null>;
}

export interface DocumentLocationIntersection {
  documentLocationName: string;
  documentLocationCoveragePercent: number;
}

export interface Establishment {
  id: number;
  establishmentDate: string;
  referenceLocationId: number;
  referenceLocationName: string;
  entityId: number;
  entityName: string;
  surface: number | null;
  mineral: number | null;
  documentLocationIntersections?: DocumentLocationIntersection[];
}

export interface EstablishmentEntity {
  id: number;
  name: string;
}

export interface DocumentLocation {
  id: number;
  name: string;
  geometryJson: GeoJSON.Geometry | null;
}

export interface ReferenceLocationGeometry {
  id: number;
  name: string;
  geometryJson: GeoJSON.Geometry | null;
}

export type EstablishmentsResponse = {
  establishments: Establishment[];
  referenceLocations?: ReferenceLocationGeometry[];
  documentLocations?: DocumentLocation[];
};
export interface WellLandingAnalysisResponse {
  id: number;
  href: string;
  auth: string[];
}

export interface WellLandingAnalysisInstance {
  id: number;
  href: string;
  auth: string[];
  /** 'system_landing_analysis', or a manual kind the FE labels "Manual". */
  analysisType?: WellLandingAnalysisType;
  /** The result behind the run; a manual landing interpretation names it as its source. */
  wellLandingAnalysisResultId?: number | null;
  /** When the run behind this landing ran (UAT item 8): this run for a system row, the run a
   *  changed zone was marked from, null for an interval-only pick. */
  sourceRunAt?: string | null;
  /** The system result a user_derived row was marked from (review): what Mark as Correct sends
   *  as the source when a Manual run is selected. Null for interval-only; a system row's own. */
  sourceResultId?: number | null;
  /** When a manual pick was marked correct: an interval-only pick's date (UAT item 8). */
  markedAt?: string | null;
  geometryVersion: string;
  uwi14: string;
  createdAt: string;
  /** Geometry-source kind ('source_well_survey' | 'pre_drill'). Lets the FE
   *  categorize drilled vs pre-drill instances without a join. */
  geometrySourceType?: string;
  /** Subject workspace for pre-drill instances; null on drilled. One instance
   *  per promoted workspace shares the uwi14 and is distinguished by this. */
  workspace?: PreDrillWorkspaceRef | null;
  structureGridCollection: {
    id: number;
    collectionName: string;
    auth: string[];
    href: string;
    isDefault: boolean;
    defaultStartTime: string | null;
    defaultEndTime: string | null;
  } | null;
  landingParameters: {
    usePerforations: boolean;
    stepLengthFt: number;
    horizontalInclinationCutoffDeg: number;
    thicknessCutoffFt: number;
  };
}

export interface WellLandingAnalysisInstancesResponse {
  totalCount: number;
  results: WellLandingAnalysisInstance[];
}

export interface WellLandingAnalysisRequestNotificationEvent {
  name: string;
  label: string;
}

export interface WellLandingAnalysisExportResponse {
  exportId: string;
  status: 'processing' | 'complete' | 'failed';
  message: string;
}

export interface WellLandingAnalysisExportPusherPayload {
  export_id: string;
  status: 'complete' | 'failed';
  download_url?: string;
  expires_at?: string;
  row_count?: number;
  file_count?: number;
  file_size_bytes?: number;
  collection_id: number;
  error?: string;
}

export interface WellLandingAnalysisRequestResponse {
  id: number;
  href: string;
  auth: string[];
  status: 'pending' | 'running' | 'completed' | 'failed';
  totalWellbores: number;
  createdAt: string;
  resources: {
    activeJobStatus?: {
      href: string;
      auth: string[];
    };
    notifications: {
      channel: string;
      events: WellLandingAnalysisRequestNotificationEvent[];
    };
  };
}

export interface BatchReadyPusherEvent {
  id: number;
  sidekiqBatchId: string;
  pollingHref: string;
  timestamp: string;
}

export interface SidekiqBatchProgressResponse {
  bid: string;
  total: number;
  pending: number;
  done: number;
  failures: number;
  complete: boolean;
  percentComplete: number;
}

export type WellLandingAnalysisRequestStatus =
  'pending' | 'processing' | 'processed' | 'failed' | 'cancelled';

export interface WellLandingAnalysisRequestStatusResponse {
  id: number;
  href: string;
  status: WellLandingAnalysisRequestStatus;
  totalWells: number;
  updatedAt: string;
  progress: {
    total: number;
    pending: number;
    processing: number;
    processed: number;
    failed: number;
    cancelled: number;
  };
  counts: {
    flagged: number;
    errors: number;
  };
}

// ─── Pusher event payloads ───────────────────────────────────────────────────

export interface WellLandingAnalysisRequestStatusChangedEvent {
  id: number;
  status: 'processing' | 'processed' | 'failed';
  timestamp: string;
}

export interface WellLandingAnalysisStatusChangedEvent {
  id: number;
  uwi14: string;
  href?: string;
  auth?: string[];
  status: 'processing' | 'processed' | 'failed';
  timestamp: string;
  failureReason?: string;
}

// ─── Preview / full result response ─────────────────────────────────────────

export interface WellLandingAnalysisPreviewWellPoint {
  index: number;
  type: string;
  landingZoneGridId: number;
  landingZoneGridName: string;
  landingZoneInterval: string;
  zoneBelowGridId: number;
  zoneBelowGridName: string;
  zoneBelowInterval: string;
  depthBelowLandingZoneTopFt: number;
  depthToNextZoneFt: number;
  measuredDepthFt: number;
  tvdssFt: number;
  azimuthDegree: number;
  inclinationDegree: number;
  includedInSummary: boolean;
  reasonForExclusion: string | null;
  geometry: {
    type: string;
    coordinates: [number, number];
  };
}

export type WellLandingAnalysisType =
  'system_landing_analysis' | 'user_derived' | 'user_interval_only';

export interface WellLandingAnalysisPreview {
  id: number;
  href: string;
  analysisType?: WellLandingAnalysisType;
  wellLandingAnalysisResultId?: number | null;
  /** When the run behind this landing ran (UAT item 8): this run for a system row, the run a
   *  changed zone was marked from, null for an interval-only pick. */
  sourceRunAt?: string | null;
  /** When a manual pick was marked correct: an interval-only pick's date (UAT item 8). */
  markedAt?: string | null;
  isPreview: boolean;
  auth: string[];
  uwi14: string;
  createdAt: string;
  /** Geometry-source identity so a fetched result is self-describing and can be
   *  keyed to (uwi14, workspace) without cross-referencing the list. */
  geometrySourceType?: string;
  geometrySourceId?: number | null;
  /** Subject workspace driving this result; null on drilled. */
  workspace?: PreDrillWorkspaceRef | null;
  structureGridCollection: {
    id: number;
    collectionName: string;
    auth: string[];
    href: string;
    isDefault: boolean;
    defaultStartTime: string | null;
    defaultEndTime: string | null;
  } | null;
  landingParameters: {
    usePerforations: boolean;
    stepLengthFt: number;
    horizontalInclinationCutoffDeg: number;
    thicknessCutoffFt: number;
  };
  wellLevel: {
    landingZoneGridId: number;
    landingZoneGridName: string;
    landingZoneInterval: string;
    typeCurveFormation: string;
    vendorLandingZone: string;
    zoneBelowGridId: number;
    zoneBelowGridName: string;
    zoneBelowInterval: string;
    depthBelowLandingZoneTopFt: number;
    depthToNextZoneFt: number;
    wellboreType: string;
    /** FE-facing Type label ('Pre-Drill' for pre-drill subjects, else the
     *  mapped bore-type label). Display this, not the raw `wellboreType`. */
    completionType?: string;
    wellboreTrajectory: string;
  };
  longestContinuousSegment: {
    landingZoneGridId: number;
    segmentLengthFt: number;
    depthBelowLandingZoneTopFt: number;
    depthToNextZoneFt: number;
  };
  allInZones: {
    gridId: number;
    gridName: string;
    feetInZone: number;
    percentInZone: number;
    geologicInterval: string;
    geologicIntervalId: number;
  }[];
  geometryContext: {
    /**
     * Null for a VERTICAL subject: a vertical has no lateral, so there is no
     * meaningful wellbore azimuth to average. Rendering it as a number would
     * assert a direction the well does not have.
     */
    wellAzimuthDegree: number | null;
    /** Null for a vertical subject — see {@link wellAzimuthDegree}. */
    wellInclinationDegree: number | null;
    tortuosity: number | null;
    lateralLengthFt: number | null;
    /** Null for a vertical subject: no lateral, so no toe to point anywhere. */
    toeDirection: string | null;
  };
  /**
   * How the landing was determined. `'perforation'` for a vertical, whose
   * producing interval comes from IHS perforation intervals rather than from a
   * lateral trajectory. Absent on drilled/pre-drill results, which are always
   * trajectory-derived.
   */
  landingMethod?: string | null;
  outputSupportData: {
    totalHorizontalPoints: number;
    includedPointsCount: number;
    noAttributionReason: string | null;
    excludedBreakdown: {
      outOfRange: number;
      aboveAllGrids: number;
      belowAllGrids: number;
      belowThicknessCutoff: number;
    };
  } | null;
  wellPoints: WellLandingAnalysisPreviewWellPoint[];
}

export interface WellLandingAnalysisRequestCancelResponse {
  id: number;
  href: string;
  status: 'cancelled';
  cancelledAt: string;
}

export interface LandingAnalysisCountsResponse {
  totalJobsRun: number;
  totalWellsWithResults: number;
  wellsWithNoResults: number;
  lastRunAt: string | null;
}

export interface WellLandingAnalysisJobResult {
  id: number;
  href: string;
  status: 'pending' | 'processing' | 'processed' | 'failed' | 'cancelled';
  runType: 'collection' | 'auto_update';
  totalWells: number;
  flaggedWellboresCount: number;
  runErrorsCount: number;
  inclinationCutoffDeg: number;
  thicknessCutoffFt: number;
  stepLengthFt: number;
  usePerforationGeometry: boolean;
  runDate: string;
  structureGridCollection: {
    id: number;
    collectionName: string;
    href: string;
    auth: string[];
  };
  resources: {
    queries: {
      wellLandingAnalyses: { href: string; auth: string[] };
    };
  };
}

export interface WellLandingAnalysisJobsQueryResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  results: WellLandingAnalysisJobResult[];
}

export interface WellLandingAnalysisWellResult {
  id: number;
  href: string;
  auth: string[];
  wellLandingAnalysisRequestId: number;
  isPreview: boolean;
  status: 'pending' | 'processing' | 'processed' | 'failed' | 'cancelled';
  failureReason: string | null;
  analysisResult: string | null;
  geometryVersion: string | null;
  uwi14: string;
  createdAt: string;
  landingZone: string | null;
  landingZoneId: number | null;
  landingInterval: string | null;
  landingSurface: string | null;
  wellboreType: string | null;
  wellboreTrajectory: string | null;
  lateralLengthFt: number | null;
  executionStatus: 'processed' | 'failed' | null;
  jobResult: string | null;
  validWellBoreGeometry: boolean;
  wellName: string;
  currentOperator: string;
  wellStatus: string;
  holeDirection: string;
  trueVerticalDepthFt: number | null;
  permitDate: string | null;
  spudDate: string | null;
  landingAnalysisFlags: string | null;
  structureGridCollection: {
    id: number;
    collectionName: string;
    href: string;
    auth: string[];
    isDefault: boolean;
    defaultStartTime: string | null;
    defaultEndTime: string | null;
  };
  well: {
    href: string;
    auth: string[];
  };
  landingParameters: {
    stepLengthFt: number;
    usePerforations: boolean;
    thicknessCutoffFt: number;
    interpolationMethod: string;
    toeUpThresholdDeg: number;
    uTurnThresholdDeg: number;
    outOfRangeThreshold: number;
    toeDownThresholdDeg: number;
    initialAzimuthPercent: number;
    horizontalInclinationCutoffDeg: number;
  };
}

export interface WellLandingAnalysisWellsQueryResponse {
  totalCount: number;
  results: WellLandingAnalysisWellResult[];
}

// ─── Well Spacing ─────────────────────────────────────────────────────────────

export interface WellSpacingRequestNotificationEvent {
  name: string;
  label: string;
}

export interface WellSpacingRequestResponse {
  id: number;
  href: string;
  auth: string[];
  status: 'pending' | 'running' | 'completed' | 'failed';
  createdAt: string;
  resources: {
    notifications: {
      channel: string;
      events: WellSpacingRequestNotificationEvent[];
    };
  };
}

export interface WellSpacingRequestShowResponse {
  id: number;
  href: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  triggerType: string;
  triggerUwi14: string | null;
  statusAggregates: {
    statusCounts: {
      pending: number;
      processing: number;
      processed: number;
      failed: number;
    };
    failedCount: number;
  };
  updatedAt: string;
  resources: {
    notifications: {
      channel: string;
      events: { name: string }[];
    };
    queries: {
      wellSpacingResults: {
        href: string;
        auth: string[];
      };
    };
  };
}

export interface WellSpacingStatusChangedEvent {
  id: number;
  subjectUwi14: string;
  href: string;
  auth: string[];
  status: 'pending' | 'running' | 'processed' | 'failed';
  timestamp: string;
  resources: {
    spacingResults: {
      href: string;
      auth: string[];
      uwi14: string;
      spacingParameters: {
        searchRadiusFt: number;
        stepLengthFt: number;
        horizontalInclinationCutoffDeg: number;
        usePerforations: boolean;
        /**
         * The Gun Barrel Azimuth the rows were computed at. Null for a horizontal
         * subject. Part of the results cache identity for a vertical, so a results
         * query must send back the value that was computed, not the live slider.
         */
        gunBarrelAzimuthDeg: number | null;
      };
    };
    evalPointResults: {
      href: string;
      auth: string[];
      uwi14: string;
      spacingParameters: {
        stepLengthFt: number;
        horizontalInclinationCutoffDeg: number;
        /**
         * Also part of the eval-point dataset's identity for a vertical subject, so a
         * follow-up eval-point query must carry it too. Null for a horizontal.
         */
        gunBarrelAzimuthDeg: number | null;
      };
    };
  };
}

export interface WellSpacingResult {
  id: number;
  href: string;
  auth: string[];
  subjectUwi14: string;
  offsetUwi14: string;
  offsetUwi10: string;
  offsetWellName: string;
  offsetWellType: string;
  /** The subject measured against its own other segments, a self pair. */
  isSelfPair?: boolean;
  // Pre-drill fields: `workspace`/`offsetWorkspace` are the promoting workspace
  // for the SUBJECT/OFFSET geometry (null on drilled); offset kind via offsetWellType.
  // 'estimated' is a vertical subject's own geometry. Such a row carries no
  // `workspace` (the BE emits that only for 'pre_drill') but DOES carry an
  // `offsetWorkspace` when the OFFSET is estimated, since that one is resolved by
  // geometry id without filtering on kind.
  geometrySourceType?: 'source_well_survey' | 'pre_drill' | 'estimated';
  /** Subject workspace (pre-drill subject only; null for drilled). */
  workspace?: PreDrillWorkspaceRef | null;
  /** Offset workspace (pre-drill offset only; null for drilled). */
  offsetWorkspace?: PreDrillWorkspaceRef | null;
  subjectGeometryUpdatedAt: string;
  offsetGeometryUpdatedAt: string;
  minDistanceFt: number;
  overlapPercent: number;
  createdAt: string;
  horizontalDistanceFt: number;
  verticalDistanceFt: number;
  trueDistanceFt: number;
  rollUpMethod: string;
  spacingParameters: {
    searchRadiusFt: number;
    stepLengthFt: number;
    horizontalInclinationCutoffDeg: number;
    /** Azimuth these numbers were computed at; null for a horizontal subject. */
    gunBarrelAzimuthDeg: number | null;
  };
  isValid: boolean;
}

export interface WellSpacingResultsResponse {
  totalCount: number;
  results: WellSpacingResult[];
}

export interface WellSpacingEvalPointResult {
  href: string;
  auth: string[];
  subjectUwi14: string;
  offsetWellName: string;
  subjectGeometryUpdatedAt: string;
  geometrySource: string;
  // SUBJECT geometry-source kind (NOT the offset's; permit offsets are detected
  // from offsetWellType). `workspace`/`offsetWorkspace` pin markers by workspace.
  // 'estimated' is a vertical subject's own geometry. Such a row carries no
  // `workspace` (the BE emits that only for 'pre_drill') but DOES carry an
  // `offsetWorkspace` when the OFFSET is estimated, since that one is resolved by
  // geometry id without filtering on kind.
  geometrySourceType?: 'source_well_survey' | 'pre_drill' | 'estimated';
  /** Subject workspace (pre-drill subject only; null for drilled). */
  workspace?: PreDrillWorkspaceRef | null;
  /** Offset workspace (pre-drill offset only; null for drilled). */
  offsetWorkspace?: PreDrillWorkspaceRef | null;
  /**
   * What keyed the eval-point dataset. The two shapes are disjoint and both keys
   * are always on the wire (irrelevant half null): a horizontal keys on trajectory
   * params, a vertical on `{ strategy: 'vertical_td', gunBarrelAzimuthDeg }`.
   */
  evalPointParams: {
    stepLengthFt: number | null;
    horizontalInclinationCutoffDeg: number | null;
    gunBarrelAzimuthDeg: number | null;
    strategy: 'vertical_td' | null;
  };
  offsetUwi14: string;
  offsetUwi10?: string;
  offsetWellType?: string;
  /** The offset is the subject itself, on one of its other laterals. */
  isSelfPair?: boolean;
  /**
   * True when this offset was spaced off an ESTIMATED (survey-less vertical)
   * geometry — the per-offset twin of the subject's `estimated` flag; drives the
   * gun barrel's lighter treatment. A drilled/permit offset is false. Read this,
   * NOT `offsetWellType` (which carries the classified bore type).
   */
  offsetEstimated?: boolean;
  offsetGeometryUpdatedAt: string;
  evalPointIndex: number;
  evalPointMdFt: number;
  evalPointVsFt: number;
  evalPointTvdssFt: number;
  evalPointTvdFt: number;
  evalPointLat: number;
  evalPointLon: number;
  horizontalDistanceFt: number;
  verticalDistanceFt: number;
  trueDistanceFt: number;
  /** Offset well TVD at the slice through this eval point (BE-computed). */
  offsetTvdFt: number;
  /**
   * Offset well's own surface-hole (SHL) and total-depth (TD) TVDSS, for drawing
   * its gun-barrel stick from SHL→TD instead of the subject-surface approximation.
   * Null when the offset is missing KB/TVD in the source data — the FE then falls
   * back to the subject-surface datum.
   */
  offsetShlTvdssFt?: number | null;
  offsetTdTvdssFt?: number | null;
  /** Legs are numbered from 1 along the bore by direction. Null when the bore has none stored. */
  evalPointLegIndex?: number | null;
  /** MD and leg on the offset where it crossed this eval point. */
  offsetMdFt?: number | null;
  offsetLegIndex?: number | null;
  isValid: boolean;
}

export interface WellSpacingEvalPointResultsResponse {
  totalCount: number;
  results: WellSpacingEvalPointResult[];
}

export interface OwnershipGraphEncumbrance {
  type: string;
  fraction?: string | number;
  fractionF?: number;
  encumbranceOwnerNodeId?: number;
  constrainedRights?: string[] | null;
  class?: string;
  duration?: string | null;
  terminationCondition?: string | null;
  sourceConveyanceId?: number | null;
  royaltyRate?: string | null;
  amount?: string;
}

// A lease a net is charged from, once however many of its lessors' rows name the piece.
export interface OwnershipGraphRoyaltyRate {
  conveyanceId: number;
  royaltyRate: string;
}

// A lease a royalty net is charged from: its rate on its share of the piece.
export interface OwnershipGraphLeaseShare extends OwnershipGraphRoyaltyRate {
  share: string;
}

export interface OwnershipGraphNodeInterest {
  interestType: string;
  rights: string[];
  fraction: number | string;
  fractionF: number;
  net: number | string;
  netF: number;
  encumbrances: OwnershipGraphEncumbrance[];
  leaseShares?: OwnershipGraphLeaseShare[];
  royaltyRates?: OwnershipGraphRoyaltyRate[];
}

export interface OwnershipGraphNode {
  id: string;
  entity: {
    entityId: number;
    entityName: string;
    canonicalEntityId: number;
    [key: string]: unknown;
  };
  property: {
    propertyId: number | null;
    propertyName: string;
    propertyType: string | null;
    volume: {
      geometryName: string | null;
      polygon: unknown;
      depthRange: [number, number | null];
    };
  };
  asset: {
    assetId?: number;
    propertyId?: number;
    estateType?: string;
  } | null;
  interests: OwnershipGraphNodeInterest[];
  severedFromNodeId: string | null;
  isEstablishment?: boolean;
  effectiveDate?: string | null;
}

export interface OwnershipGraphEdgeParams {
  retained?: boolean;
  severanceType?: string;
  severanceSubtype?: string;
  retentionSubtype?: string;
  conveyedInterestTypes?: string[];
  consolidation?: boolean;
  encumbranceUpdate?: boolean;
  terminationReason?: string;
  originalLeaseConveyanceId?: number;
  quantumFunction?: string;
  mathTrace?: string;
  royaltyRate?: string;
  grantorCanonicalId?: number;
}

export interface OwnershipGraphEdge {
  id: string;
  fromNodeId: string | null;
  toNodeId: string;
  eventType: string;
  transactionId: number | null;
  conveyanceId: number | null;
  documentId: number | null;
  effectiveDate: string | null;
  recordingDate: string | null;
  transactionDate: string | null;
  isValid: boolean;
  validationReason: string | null;
  params: OwnershipGraphEdgeParams;
}

export interface OwnershipGraphDocumentRecord {
  documentId: number;
  documentTitle: string | null;
  instrumentDate: string | null;
  fileDate: string | null;
  documentType: string | null;
  county: string | null;
  state: string | null;
  effectiveDate: string | null;
  recordingDate: string | null;
}

export interface OwnershipGraphPathToReview {
  label: string;
  reason: 'entity_chain' | 'encumbrance_chain';
  subjectNodeId: string;
  nodeIds: string[];
  edgeIds: string[];
  encumbranceType?: string | null;
  encumbranceOwnerNodeId?: string | null;
}

// A row of a leaf that named a consumed owner, rewritten in place to name its successors, or dropped (no successors).
export interface OwnershipGraphPointerUpdate {
  nodeIds: string[];
  fromOwnerNodeId: string;
  toOwnerNodeIds: string[];
  conveyanceId: number | null;
  documentId: number | null;
  transactionId: number | null;
  transactionDate: string | null;
}

export interface OwnershipGraphResponse {
  entityId: number;
  cutoffDate: string;
  ownershipSummary: Record<
    string,
    { fraction: number | string; fractionF: number }
  >;
  documents: Record<string, OwnershipGraphDocumentRecord>;
  geographies: unknown[];
  graph: {
    nodes: Record<string, OwnershipGraphNode>;
    edges: Record<string, OwnershipGraphEdge>;
    adjacency: Record<string, string[]>;
    reverseAdjacency: Record<string, string[]>;
    pointerUpdates?: OwnershipGraphPointerUpdate[];
  };
  subjectAssetNodeIds: (string | number)[];
  pathsToReview?: OwnershipGraphPathToReview[];
}

// ─── Inventory Workspace ─────────────────────────────────────────────────────
// Contract per the Inventory Management Automation spec. The backend is being
// built in parallel; these mirror the payloads documented there.

/**
 * An inventory package offered on a candidate type curve.
 *
 * Every field optional: the endpoint returns `inventoryPackages: []` on every
 * type curve in every response seen so far, so the shape is inferred from the
 * columns that consume it rather than observed.
 */
export interface InventoryCandidatePackage {
  name: string;
  /** Package well spacing — part of the package's identity. */
  wellSpacingFt: number | null;
  /** `"{name} -- {wellSpacingFt}"`, e.g. `"Base -- 1320"`. */
  displayName: string;
  /** The family's default promoted package. */
  isDefault: boolean;
  /** LTD places this package carries; package-anchor responses leave out places the package excluded. */
  ltdProperties: number;
  /**
   * How many of `ltdProperties` are valued — PACKAGE-ANCHOR (eval) responses only. Spelled as the
   * wire sends it: the LTD inflection acronym (config/initializers/inflections.rb) capitalizes a
   * mid-key `ltd`.
   */
  valuedLTDProperties?: number;
  /**
   * With a run selected (the request sent `calculationId`): how many of these places that run
   * valued, per curve it priced them on. Absent when no run is selected or the run priced none.
   */
  calculationValued?: { uniqueTcId: string; valuedLTDProperties: number }[];
  /**
   * The slot rows behind `ltdProperties` — PACKAGE-ANCHOR (eval) responses only, absent for a
   * launch. They are `inventory_unit_slots` ids, which is the `insights_unit_formation_slot_id`
   * the LTD tile emits, so the map filters the scenario superset to exactly the rows this
   * package resolved. Same aggregate as the count, so the two cannot disagree.
   */
  ltdSlotIds?: number[];
  /** Extent of those slots, `[minLng, minLat, maxLng, maxLat]` — what the map frames. */
  bbox?: [number, number, number, number];
}

/** One type curve that can serve an (area, formation) pair. */
export interface InventoryFormationCandidate {
  uniqueTcId: string;
  typeCurveHeaderDetailsId: number;
  /** Display label. Equal to `uniqueTcId` in every observed response. */
  label: string;
  /** The pair's default — what the server attaches when none is chosen. */
  isDefault: boolean;
  /** True for the type curve the workspace was launched from. */
  isOrigin: boolean;
  /** Well-spacing prefill off the header. */
  wellSpacingFt: number | null;
  /** The curve's detail page, minted by the BE; null when it has no public id. */
  href?: string | null;
  /**
   * @deprecated Packages are a property of the `(area, formation)` zone, not of
   * a type curve — read `InventoryFormationCandidateRow.inventoryPackages`.
   * Still typed because this branch's backend nests them here; drop once it
   * emits them at row level.
   */
  inventoryPackages?: InventoryCandidatePackage[];
}

/** One (area, formation) pair the workspace can cover. */
export interface InventoryFormationCandidateRow {
  /** `"<typeCurveArea>:<formation>"`. The pair's identity and the row key —
   *  formation alone is not unique, the same formation recurs across areas. */
  id: string;
  typeCurveArea: string;
  formation: string;
  /** True for the pair the workspace was launched from: always included, and
   *  its row cannot be deselected. */
  isOrigin: boolean;
  typeCurveAreaId?: number | null;
  /** Already a percent, to two decimals — do not re-round. */
  overlapPercent?: number | null;
  /** Units whose centroid sits in this area. NOT summable down the column. */
  unitCount?: number | null;
  /** Geologic interval; null where a basin has no live mapping. */
  interval?: string | null;
  /** Formation swatch, as configured hex. Null when no source carries one. */
  color?: string | null;
  /**
   * What the DEFAULT package would inherit — the number the column shows
   * before anyone opens the Inventory dropdown. Selecting another package
   * re-reads that package's own `ltdProperties`; there is no second request.
   */
  ltdProperties?: number | null;
  /** How many of `ltdProperties` are valued — PACKAGE-ANCHOR (eval) responses only. */
  valuedLTDProperties?: number;
  typeCurves: InventoryFormationCandidate[];
  /**
   * Packages that exist for this zone, parallel to `typeCurves` — a package
   * belongs to the `(area, formation)` zone, not to a type curve. Its identity
   * is `(name, wellSpacingFt)`, so "Base" at 1320 and at 880 are two packages.
   * Empty means nothing promoted here yet, and the FE offers a new package.
   */
  inventoryPackages: InventoryCandidatePackage[];
}

/**
 * One Type Curve Area on the launch-modal map: the target plus each intersecting
 * neighbour, ordered target first then descending overlap. `geometry` is inline
 * GeoJSON, ready for a Mapbox source — dropped only when the request sends
 * `includeGeometry: false`.
 */
export interface InventoryCandidateArea {
  id: number;
  name: string;
  isOrigin: boolean;
  /** Retired areas stay in the set (can still hold promoted type curves). */
  isActive: boolean;
  overlapPercent: number;
  unitCount: number;
  bbox: [number, number, number, number];
  geometry?: GeoJSON.Geometry | null;
}

/** POST /queries/inventory_formation_candidates */
export interface InventoryFormationCandidatesResponse {
  results: InventoryFormationCandidateRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** `[minLng, minLat, maxLng, maxLat]` for the whole candidate set — frame the map with it. */
  bbox?: [number, number, number, number];
  /** The map layer: target area first, then neighbours by descending overlap. */
  areas?: InventoryCandidateArea[];
  /** The footer rollup over the selected (default: all) rows. */
  summary?: {
    areaCount: number;
    formationCount: number;
    typeCurveCount: number;
    unitCount: number;
    ltdProperties: number;
    /**
     * Package anchor only — units holding at least one LTD slot (the eval scenario ledger's
     * "LTD Capable Units"). Absent on the origin anchors.
     */
    ltdUnitCount?: number;
  };
}

/** Leaf of the workspace's left-nav Type Curves tree (one per type-curve family). */
export interface InventoryTypeCurveTreeLeaf {
  /**
   * @deprecated The zone id no longer lives on the type curve. A package is a
   * property of the `(area, formation)` zone, so the id moved to the parallel
   * `inventoryPackages` array as `inventoryFormationPackageId`. Older backends
   * still emit it here, hence optional rather than removed — read it through
   * `resolveTreeLeaves`, never directly.
   */
  inventoryTypeCurveId?: number;
  uniqueTcId: string;
  /** Current-promoted header detail id; null when the family has none promoted. */
  typeCurveHeaderDetailsId: number | null;
  /**
   * The id `/type_curve_headers/:public_id` resolves on. Null when the family
   * has no promoted header. Distinct from `label`, which is a display name.
   */
  tcPublicId: string | null;
  /** Display name for the tree row: the header's workspace name, else its id. */
  label: string;
  /** The formation's default type curve (type_curve_header_details.is_default). */
  isDefault: boolean;
  /** The launched (origin) type curve for the zone. Optional until the backend
   *  emits it; used as the display fallback when no leaf is the default. */
  isOrigin?: boolean;
  /** The curve's well spacing; the formation opens on the package at this
   *  spacing. Null or absent when unknown (an older backend, a picker pick). */
  wellSpacingFt?: number | null;
}

/** One inventory package of an `(area, formation)` zone. */
export interface InventoryTreePackage {
  /** Zone id — the node-details endpoint resolves on this. */
  inventoryFormationPackageId: number;
  /** Source package name; null is the "new package" seed. */
  name: string | null;
  wellSpacingFt: number | null;
  /** `"{name} -- {wellSpacingFt}"`; null when `name` is null. */
  displayName: string | null;
  /** The scenario's default package for the zone. */
  isDefault: boolean;
  /** Client-only, never sent by the server: the package the operator picked
   *  for the zone, set by `applyPackagePicks`. Outranks every default. */
  isPicked?: boolean;
}

export interface InventoryTypeCurveTreeFormation {
  /**
   * The formation. `formation` is what the jbuilder emits since the grain
   * re-key; `formationName` was the pre-rename key, still accepted so an older
   * backend keeps working.
   */
  formation?: string;
  formationName?: string;
  /** Geologic interval label for the formation (e.g. "UPPER SPRABERRY"). */
  interval?: string;
  /**
   * Configured formation swatch colour (hex), from the scenario area-config /
   * geologic-interval `display_color` — the same source the sidebar tree and the
   * gun barrel bands paint from. Null when no source carries one.
   */
  color?: string | null;
  typeCurves: InventoryTypeCurveTreeLeaf[];
  /**
   * Packages for this zone, parallel to `typeCurves` — a package is a property
   * of the `(area, formation)` zone, not of a type curve. Optional until the
   * backend on this branch emits it.
   */
  inventoryPackages?: InventoryTreePackage[];
}

/**
 * A formation's name, whichever key the response used.
 *
 * @param {InventoryTypeCurveTreeFormation} formation - The tree formation node.
 * @return {string} The formation name.
 */
export const treeFormationName = (
  formation: InventoryTypeCurveTreeFormation
): string => formation.formation ?? formation.formationName ?? '';

export interface InventoryTypeCurveTreeArea {
  typeCurveArea: string;
  /** The launched area — the area serving the workspace's launched family. */
  isOrigin: boolean;
  typeCurveCount: number;
  formations: InventoryTypeCurveTreeFormation[];
}

export interface InventoryTypeCurveTree {
  typeCurveCount: number;
  areas: InventoryTypeCurveTreeArea[];
}

/**
 * One area on the workspace detail map: the launched Type Curve Area plus its
 * co-developed neighbours, as inline GeoJSON. `isCovered` separates the two —
 * true where this workspace holds zones, false for a neighbour drawn as context.
 * `geometry` is absent when the request sends `?include_geometry=false`.
 */
export interface InventoryWorkspaceMapArea {
  id: number;
  name: string;
  isOrigin: boolean;
  isActive: boolean;
  isCovered: boolean;
  overlapPercent: number;
  bbox: [number, number, number, number];
  geometry?: GeoJSON.Geometry | null;
}

/** The workspace detail-page map block, from `GET /inventory_workspaces/:id`. */
export interface InventoryWorkspaceMap {
  /** `[minLng, minLat, maxLng, maxLat]` for the whole area set. */
  bbox: [number, number, number, number];
  areas: InventoryWorkspaceMapArea[];
}

/** Verdict on a verification task. `null` means unanswered. */
export type InventoryTaskStatus = 'correct' | 'incorrect' | null;

/**
 * One task in the Inventory Workspace checklist, from
 * `GET /inventory_workspaces/:id/tasks`.
 *
 * Key-for-key identical to the Type Curve workspace task node, so one component
 * renders both. `value` and `linkedFilterIds` are inert here: inventory tasks are
 * operator confirmations, so nothing is field-backed and nothing cites filters.
 */
export interface InventoryWorkspaceTask {
  id: number;
  /** Catalog path. This, not `id`, is what the save URL takes. */
  path: string;
  title: string;
  /** Section key, e.g. `ltd_inventory`. */
  group: string;
  groupTitle: string;
  required: boolean;
  description: string;
  status: InventoryTaskStatus;
  note: string | null;
  /** Always null for inventory. Present for shape parity with the Type Curve node. */
  value: null;
  /** Always empty for inventory. Present for shape parity. */
  linkedFilterIds: number[];
  commentsCount: number;
}

/** `GET /inventory_workspaces/:id/tasks` */
export interface InventoryWorkspaceTasksResponse {
  /** The full catalog in fixed order, verdicted or not. */
  tasks: InventoryWorkspaceTask[];
  /** Tasks carrying a verdict, correct or incorrect. Counts the gate task too. */
  completed: number;
  total: number;
}

/** GET/POST /inventory_workspaces — show and create share one jbuilder shape. */
export interface InventoryWorkspaceResponse {
  id: number;
  href: string;
  auth: string[];
  name: string;
  /** The launched type curve family. */
  uniqueTcId: string;
  /**
   * Workspace-level default flag (also the listing facet). False on create.
   *
   * NB there is deliberately no workspace-level package field: a package is a
   * per-`(area, formation)` property, carried on the tree's `inventoryPackages`.
   */
  isDefaultInventory: boolean;
  promoted: boolean;
  typeCurveTree: InventoryTypeCurveTree;
  /**
   * The detail-page map: launched area + co-developed neighbours, inline GeoJSON.
   * Optional so MSW fixtures and `?include_geometry=false` callers still type.
   */
  map?: InventoryWorkspaceMap;

  // Legacy fields, absent from the real BE response but still seeded by MSW so
  // the tasks drawer and Promote gating keep working until their rework.
  tasks?: { completed: number; total: number; unitsFrozen: boolean };
  source?: string;
  meta?: { unitCount: number; page: number; pageSize: number };
  resources?: Record<string, { href: string; auth: string[] }>;
}

/**
 * POST /inventory_workspaces/:id/slot_inventory_requests — one Compute
 * Inventory (slot generation) run. 202 when created/regenerating, 200 when an
 * existing run is returned; `notifications` present only while active.
 */
export interface InventorySlotRequestResponse {
  id: number;
  href: string;
  auth: string[];
  status: string;
  sourceInventoryPackageName: string | null;
  createdAt: string | null;
  notifications?: { channel: string; events: { name: string }[] };
  resources?: Record<string, string>;
  // Present on the synchronous single-unit run (POST …/units/:unitId/…), which
  // returns 200 with the finished run instead of a Pusher channel.
  failureReason?: string | null;
  updatedAt?: string | null;
  progress?: {
    unitsRequested: number;
    unitsSucceeded: number;
    unitsFailed: number;
    unitsSkipped: number;
  };
  summary?: {
    slotsCreated: number;
    wellSlotsCreated: number;
    ltdSlotsCreated: number;
  };
  units?: { unitId: number; status: string; error: string | null }[];
  // Lightweight `{ status: 'empty', message }` body when nothing was eligible.
  message?: string;
}

/**
 * POST /inventory_workspaces/:id/geometry_refreshes — 201 carrying only the
 * channel and the one event to watch, not a workspace payload.
 */
export interface InventoryGeometryRefreshResponse {
  notifications: {
    channel: string;
    events: { name: string; label: string }[];
  };
}

/**
 * One slot as the Slot Editing PATCH returns it — the core slot shape (no
 * well-enrichment columns). `geometryGeojson` is the authoritative persisted
 * lateral. See the FE contract for the full field reference.
 */
export interface InventoryFormationSlotEdit {
  id: number;
  unitId: number;
  unitName?: string;
  formationName?: string;
  inventoryPackageName?: string | null;
  wellSpacingFt?: number | null;
  slotNumber?: number;
  hasWell?: boolean;
  valued: boolean;
  notes: string | null;
  uwi?: string | null;
  wellProportionInUnit?: number | null;
  wellStatus?: string | null;
  forecastStatus?: string | null;
  reserveCategory?: string | null;
  geometrySourceType?: string | null;
  geometryGeojson: GeoJSON.LineString | null;
}

/**
 * A landing edit (Story 1): a permit / pre-drill slot moved in depth. The bore
 * was re-landed on the request; the landing analysis and any formation change
 * resolve afterwards, reported on the workspace's Pusher channel.
 */
export interface InventoryLandingEditResponse {
  id: number;
  uwi14: string;
  uwi: string;
  unitId: number;
  inventoryUnitSlotId: number | null;
  targetTvdssFt: number;
  fromFormation: string | null;
  toFormation: string | null;
  landingStatus: 'pending' | 'landed' | 'unlanded' | 'failed';
  landedAt: string | null;
  /**
   * The draft's full landing computation (landing grid, zone below, depths, segments) plus the
   * collection and geometry it ran against. Null until the landing resolves; kept here because a
   * draft writes no landing analysis result row.
   */
  landingResult: Record<string, unknown> | null;
}

/** Bulk Slot Editing PATCH response: the updated slots, in requested order. */
export interface InventoryFormationSlotsBulkResponse {
  results: InventoryFormationSlotEdit[];
  /** The landing edits the batch wrote; empty for a drainage-only batch. */
  landingEdits?: InventoryLandingEditResponse[];
}

/** A lateral's own vertex order: first (`start`) or last (`end`) coordinate. */
export type SlotCompositeEnd = 'start' | 'end';

/** The keys a slot row carries for a composite leg; all null on a plain LTD. */
export interface SlotCompositeLegFields {
  compositeId?: number | null;
  compositeName?: string | null;
  compositePosition?: number | null;
  compositeEntryEnd?: SlotCompositeEnd | null;
}

export interface SlotCompositePoint {
  slotId: number;
  end: SlotCompositeEnd;
  /** Compass label of that tip, e.g. "N end". Display only. */
  label: string;
  coordinates?: number[];
  propertyId?: string;
  formationName?: string;
}

export interface SlotCompositeLeg {
  slotId: number;
  position: number;
  entryEnd: SlotCompositeEnd;
  exitEnd: SlotCompositeEnd;
  formationName: string;
  slotNumber: number;
  propertyId: string;
  inventoryFormationPackageId?: number;
  perforatedLateralLengthFt?: number;
  proportion?: number;
  /** The leg's verdict; the same on every leg after a cascade. */
  valued?: boolean;
  notes?: string | null;
  ends: { end: SlotCompositeEnd; label: string; coordinates: number[] }[];
}

/** A composite wellbore (merged LTDs), as every slot_composites endpoint returns it. */
export interface SlotComposite {
  /** Null on a preview. */
  id: number | null;
  name: string;
  inventoryWorkspaceId: number;
  unitId: number;
  unitName: string;
  legCount: number;
  landingPoint: SlotCompositePoint | null;
  bottomHole: SlotCompositePoint | null;
  bottomHoleOptions: (SlotCompositePoint & { tangentLengthFt?: number })[];
  legs: SlotCompositeLeg[];
  tangents: { fromSlotId: number; toSlotId: number; lengthFt: number }[];
  producingLengthFt: number;
  tangentLengthFt: number;
  tangentsProducing?: boolean;
  notes: string | null;
  geometryGeojson?: GeoJSON.LineString | null;
  tangentsGeojson?: GeoJSON.MultiLineString | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** `POST …/slot_composites/preview`. */
export interface SlotCompositeResponse {
  composite: SlotComposite;
}

/** `POST …/slot_composites` (201): the composite plus its legs in drilling order. */
export interface SlotCompositeMergeResponse {
  composite: SlotComposite;
  slots: (InventoryFormationSlotEdit & SlotCompositeLegFields)[];
}

/** `DELETE …/slot_composites/:id`: the legs, now plain LTDs. */
export interface SlotCompositeUnmergeResponse {
  slots: (InventoryFormationSlotEdit & SlotCompositeLegFields)[];
}

/** `GET …/slot_composites?unit_id=`. */
export interface SlotCompositesIndexResponse {
  composites: SlotComposite[];
}

/** The 422 envelope's `details` for a composite write. */
export interface SlotCompositeErrorDetails {
  ineligible?: { slotId?: number; reason?: string }[];
  expectedBottomHole?: {
    slotId: number;
    end: SlotCompositeEnd;
    label?: string;
  };
}

/**
 * The subset of `POST /area_geometries` (create) the Unit Builder consumes: the
 * created area geometry's identity. After a shared+active publish the full unit
 * can be read back from `development_areas` by `area_geometry_id` if needed.
 */
export interface CreatedAreaGeometry {
  id: number;
  businessId: string;
  name: string;
}

/** One before/after property-count row of a promotion plan target. */
export interface InventoryPromotionPlanStatusRow {
  status: string;
  before: { valued: number; total: number };
  after: { valued: number; total: number };
}

/**
 * One promotion target — an `(area, formation)` zone.
 *
 * Keyed by the zone rather than by a type curve: the grain re-key made a
 * package a property of the zone, so `typeCurveHeaderDetailsId` is gone.
 * Default movement is per-zone too, because taking the default for one
 * formation says nothing about the others.
 */
export interface InventoryPromotionPlanTarget {
  inventoryFormationPackageId: number | null;
  formation: string | null;
  inventoryPackageName: string | null;
  wellSpacingFt: number | null;
  /** False when this target's counts are unchanged by the promote. */
  changed: boolean;
  /** The zone's incumbent default workspace, if this promote displaces one. */
  currentDefaultWorkspaceId: number | null;
  /**
   * development_area ids of units the outgoing default published that this
   * workspace does not re-slot. An id list, not a count — empty unless this
   * promote actually takes this zone's default from another workspace.
   */
  unitsDroppedFromDefault: number[];
  rows: InventoryPromotionPlanStatusRow[];
}

/**
 * POST /inventory_workspaces/:id/promotion_plan — read-only confirm-dialog
 * preview. `inventories[]` may be omitted from the request, in which case the
 * server derives the targets from the workspace's own input rows.
 */
export interface InventoryWorkspacePromotionPlanResponse {
  /** False renders "No changes" in the modal. */
  hasChanges: boolean;
  perInventory: InventoryPromotionPlanTarget[];
}

/** One published target of a completed promote. */
export interface PromotedInventoryResult {
  inventoryFormationPackageId: number | null;
  formation: string | null;
  inventoryPackageName: string | null;
  wellSpacingFt: number | null;
  assignmentsWritten: number;
  assignmentsClosed: number;
  recordsPublished: number;
  recordsSkippedUnchanged: number;
  unitsReslotted: number;
  unitsSkippedExcluded: number;
}

/**
 * PATCH /inventory_workspaces/:id with `{ workspace: { promote: true } }`.
 * 422 NOT_PROMOTABLE carries the outstanding units in `details.incomplete`.
 */
export interface PromoteInventoryWorkspaceResponse {
  id: number;
  href: string;
  auth: string[];
  promoted: boolean;
  isDefaultInventory: boolean;
  promotedInventories: PromotedInventoryResult[];
}

/**
 * GET /inventory_workspaces/:inventoryWorkspaceId/inventory_formation_packages/:id
 * — one zone's inventory details, optionally scoped to a package via
 * `?inventoryPackageName=`.
 */
export interface InventoryTypeCurveDetailsResponse {
  id: number;
  inventoryPackageName: string | null;
  isDefaultPackage: boolean;
  typeCurveArea: string;
  isOrigin: boolean;
  /**
   * The formation. `formation` is the current key; `formationName` was the
   * pre-rename one. Read both through `detailsFormationName`.
   */
  formation?: string;
  formationName?: string;
  uniqueTcId: string;
  typeCurveHeaderDetailsId: number | null;
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
  /** True once inventory has been computed for this zone; gates the Compute
   *  Inventory button (disabled while true, until a spacing value is edited).
   *  Currently hardcoded false on the backend. */
  hasComputedInventory?: boolean;
  /**
   * False when a Compute Inventory run on this zone could not produce anything
   * whatever the request said — no promoted type curve, no matching area config,
   * or a formation whose slot depth no structure grid and no area well can
   * resolve. Known at load, so the button is disabled instead of sending a
   * request the backend refuses. `computeBlockedReason` carries the operator-
   * facing why. Absent on older payloads, which read as computable.
   */
  canComputeInventory?: boolean;
  computeBlockedReason?: string | null;
  /**
   * Dissolved footprint of this zone's units. The Wells section bounds its query
   * by this instead of the Type Curve Area polygon, so the wells it counts and
   * the slots in `propertyCountsByStatus` come from the same region. Null when
   * the zone resolves no units. GeoJSON keys are passed through verbatim.
   */
  unitScopeGeojson?: GeoJSON.MultiPolygon | GeoJSON.Polygon | null;
  propertyCountsByStatus: {
    producing: number;
    duc: number;
    permit: number;
    ltd: number;
    shutIn: number;
  };
}

/**
 * The node-details formation, whichever key the response used.
 *
 * @param {InventoryTypeCurveDetailsResponse} details - The node details.
 * @return {string} The formation name.
 */
export const detailsFormationName = (
  details: InventoryTypeCurveDetailsResponse
): string => details.formation ?? details.formationName ?? '';

/**
 * One zone's resolved spacing config for a unit — the row shape shared by the
 * per-zone and all-zones `spacing_configs` reads. Values resolve in three tiers
 * (`unit_package` → `inherited` → `formation_package_default`); `source` says
 * which. A `formation` is not unique (a workspace can hold two zones on the same
 * formation), so key on `inventoryFormationPackageId`.
 */
export interface UnitSpacingConfig {
  inventoryFormationPackageId: number;
  formation: string;
  typeCurveArea: string;
  inventoryPackageName: string | null;
  source: 'unit_package' | 'inherited' | 'formation_package_default';
  inheritedFromWorkspaceId: number | null;
  wellSpacingFt: number | null;
  crossLineSetbackFt: number | null;
  inLineSetbackFt: number | null;
  spacingToleranceFt: number | null;
  ignoreExistingDevelopment: boolean;
  drainageSectorCount: number | null;
}

/** `GET /inventory_workspaces/:id/units/:unitId/spacing_configs` — one row per
 *  zone in the workspace, ordered by `inventoryFormationPackageId`. */
export interface UnitSpacingConfigsResponse {
  unitId: number;
  inventoryWorkspaceId: number;
  spacingConfigs: UnitSpacingConfig[];
}

/**
 * One inventory for a type curve — in this domain an inventory is a promoted
 * Inventory Workspace. Returned by `POST /queries/type_curve_headers` with
 * `view: 'inventories'`.
 */
export interface TypeCurveInventoryRow {
  id: number;
  inventoryWorkspaceId: number;
  name: string;
  uniqueTcId: string;
  formation: string;
  typeCurveScenarioName: string | null;
  isDefault: boolean;
  source: string;
  unitCount: number | null;
  /** The count the launch picker's "LTD Properties" column shows. */
  slotCount: number | null;
  promoted: boolean;
  promotedAt: string | null;
  createdBy: string | null;
  tasks: { completed: number; total: number };
}

/** `POST /queries/type_curve_headers` with `view: 'inventories'`. */
export interface TypeCurveInventoriesResponse {
  results: TypeCurveInventoryRow[];
  meta: { totalCount: number; page: number; pageSize: number };
}

/**
 * One (area, formation) pair of a workspace's inventory context.
 *
 * Mirrors the listing mock's `ResolvedContextRow`: the area the pair draws
 * from with its measured overlap, the Type Curve it is inventoried against and
 * the Inventory it opens on.
 */
export interface InventoryContextRow {
  rowId: string;
  areaId: string;
  areaName: string;
  /** True when the pair draws from the workspace's own target area. */
  isTarget: boolean;
  /** Share of the area co-developed with the target, 0–1. 1 for the target. */
  overlapFraction: number;
  /** Slot in the shared area colour palette; 0 is the target. */
  colorIndex: number;
  formationCode: string;
  /** Geologic interval, e.g. "WOLFCAMP A". */
  interval: string | null;
  /** Structure-grid display colour, null where the grid has none. */
  stratColor: string | null;
  typeCurveName: string;
  /** Curves available for the pair — alternates beyond the resolved one. */
  curveCount: number;
  inventoryName: string | null;
  spacingFt: number | null;
  /** Modelled LTD locations, null when the area contributes no units. */
  locationCount: number | null;
}

/** Per-unit verification verdict rolled up to the workspace. */
export type InventoryWorkspaceVerification =
  'Not started' | 'Correct' | 'Incorrect';

/**
 * One LINE of the Inventories listing — a `(workspace, area, formation, type
 * curve)` zone, not a workspace.
 *
 * A workspace covering three zones is three rows, with its workspace-level
 * fields repeating down them. The server flattens this itself, so the table
 * renders `results` directly.
 */
export interface InventoryListingRow {
  /** Unique per line, and the row key — `id` repeats down a workspace's lines. */
  rowId: string;
  inventoryFormationPackageId: number | null;

  // ── The zone: varies line to line ──
  typeCurveArea: string | null;
  typeCurveAreaId: number | null;
  /**
   * Already a percent, already rounded to two decimals — render as given.
   * Re-rounding collapses genuine sub-1% overlaps to "0%", which reads as no
   * overlap when there is some.
   */
  overlapPercent: number | null;
  formation: string | null;
  /** Geologic interval; null where a basin has no live mapping. */
  formationInterval: string | null;
  typeCurve: string | null;
  /** Null means no promoted inventory for the zone yet — the "new package" state. */
  inventoryName: string | null;
  spacingFt: number | null;
  ltdProperties: number | null;

  // ── The workspace: repeats down its lines ──
  id: number;
  inventoryWorkspaceId: number;
  name: string | null;
  uniqueTcId: string;
  /** Promoted package names, one per zone, de-duped by the server. */
  inventoryPackageName: string[];
  isDefault: boolean;
  promoted: boolean | null;
  promotedAt: string | null;
  promotedBy: string | null;
  status: string | null;
  /** Null is "Not started", not missing data. */
  verification: 'correct' | 'incorrect' | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  typeCurveScenario: string | null;
  typeCurveScenarioId: number | null;
  basin: string | null;
  state: string | null;
  unitCount: number | null;
  source: string | null;
  unitsVerified: { verified: number; total: number } | null;
  /**
   * Columns the mock lists but the listing contract has no field for. Optional
   * so the row type stays an honest description of the response — they are
   * hidden by default and render an em dash until the backend supplies them.
   */
  propertyCount?: number | null;
  oilEurMbbl?: number | null;

  // ── Map and navigation ──
  /** Areas this workspace covers, for highlighting the tile layer. */
  typeCurveAreaIds: number[];
  /** `[minLng, minLat, maxLng, maxLat]` union of the workspace's areas. */
  bbox: [number, number, number, number] | null;
  href: string;
  auth: string[];
  resources?: {
    typeCurve?: { href: string | null; auth: string[] };
    typeCurveScenario?: { href: string | null; auth: string[] };
    export?: { href: string; auth: string[] };
  };
}

/** One bucket of a listing facet. `label` is present on typeCurveScenario only. */
export interface InventoryFacetBucket {
  value: string | boolean;
  label?: string;
  count: number;
}

/** POST /queries/type_curve_headers with `view: 'inventories'`. */
export interface InventoryWorkspacesListResponse {
  results: InventoryListingRow[];
  /**
   * WORKSPACES, not lines — this is the paginator's count. `results.length` is
   * the line count and will exceed it whenever a workspace spans zones.
   */
  totalCount: number;
  page: number;
  /** WORKSPACES per page. */
  pageSize: number;
  /** Lines on this page; equals `results.length`. */
  rowCount: number;
  /** Twelve facets, each counted per workspace and excluding its own selection. */
  facets?: Record<string, { buckets: InventoryFacetBucket[] }>;
}

/** One row of `GET /inventory_workspaces/:id/units` (paged membership). */
export interface InventoryWorkspaceUnitRow {
  /** InventoryUnit id — the grain per-unit verification hangs off. */
  id: number;
  /** development_areas.id (no FK on the BE, may point at a pruned area). */
  unitId: number;
  name: string | null;
  acres: number | null;
  azimuthDeg: number | null;
  excluded: boolean;
  frozen: boolean;
  notes: string | null;
  verification: { id: number | null; status: boolean | null };
  resources: Record<string, { href: string; auth?: string[] }>;
}

/** `GET /inventory_workspaces/:id/units`. */
export interface InventoryWorkspaceUnitsResponse {
  units: InventoryWorkspaceUnitRow[];
  meta: { unitCount: number; page: number; pageSize: number };
}

/**
 * One polymorphic verification item, as rendered by
 * `verification_items/_verification_item.json.jbuilder` (the generic
 * `GET /:resource_type/:resource_id/verification_items` surface).
 */
export interface VerificationItemRow {
  id: number;
  verifiableType: string;
  verifiableId: number;
  path: string;
  /** true = correct, false = incorrect, null = unverified. */
  correctnessState: boolean | null;
  note: string | null;
  /** Catalog label; null when the verifiable has no catalog entry. */
  name: string | null;
  description: string | null;
  editable: boolean;
  lockReason: string | null;
}

/** GET /feedback/oncall — see BugsFeatures::OncallsController. */
export type OncallResponse =
  // location is the place the line shows ("India", "United States"); geo is the code it was
  // published as (IN, US).
  | { state: 'ok'; name: string; geo: string; location: string }
  | { state: 'unavailable' };

/** The on-call line at the top of the report form, including while it is being read. */
export type CurrentOncall = OncallResponse | { state: 'loading' };
